/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
	| T
	| {
			[P in keyof T]?: P extends " $fragmentName" | "__typename" ? T[P] : never;
	  };

import type { DocumentTypeDecoration } from "@graphql-typed-document-node/core";
/** Input for adding a user to a workspace. */
export type AddWorkspaceMemberInput = {
	/** The role to assign; defaults to Participant when null. */
	role?: WorkspaceRole | null | undefined;
	/** The Artemis user ID to add. */
	userId: string;
	/** The workspace to add the user to. */
	workspaceId: string;
};

/**
 * System-observed reachability of an agent's deployed runtime, refreshed by health pings. Distinct
 * from Status, which is the user's enable/disable intent.
 */
export type AgentHealth =
	/** Terminal runtime failure. Reserved — not auto-written yet. */
	| "FAILED"
	/**
	 * Just created / deploying; not yet confirmed reachable. The initial state, and where
	 *             an agent stays after the (expected-to-fail) cold ping right after create.
	 */
	| "PROVISIONING"
	/** Last health ping succeeded. */
	| "READY"
	/** Deployed but the last health ping failed. */
	| "UNREACHABLE";

/**
 * How the platform relates to the agent's runtime (ADR-050). Immutable after creation.
 * Only deployment, liveness, task delivery, and credential rotation are kind-aware; identity,
 * authorization, and the credential broker are kind-blind (ARCH-030).
 */
export type AgentKind =
	/**
	 * The agent runs outside the platform (a local machine, a remote A2A host, anywhere
	 *             else); the platform never reaches the runtime and holds an identity only. It polls for
	 *             dispatched tasks, self-reports, and rotates its credential explicitly. Liveness is
	 *             heartbeat-derived from token issuance (ARCH-030).
	 */
	| "EXTERNAL"
	/**
	 * The platform provisions and owns the runtime, knows its endpoint, and pushes work
	 *             to it over A2A. Credential rotation happens on redeploy.
	 */
	| "MANAGED";

export type AgentKindOperationFilterInput = {
	eq?: AgentKind | null | undefined;
	in?: Array<AgentKind> | null | undefined;
	neq?: AgentKind | null | undefined;
	nin?: Array<AgentKind> | null | undefined;
};

/** Identifies the role of an agent in the platform. Immutable after provisioning (ADR-016). */
export type AgentRole =
	/** Workspace-level orchestrator. Not assignable to tasks. */
	| "LEAD_AGENT"
	/** Stateless execution unit. The only valid task assignee (ADR-016). */
	| "WORKER";

export type AgentRoleOperationFilterInput = {
	eq?: AgentRole | null | undefined;
	in?: Array<AgentRole> | null | undefined;
	neq?: AgentRole | null | undefined;
	nin?: Array<AgentRole> | null | undefined;
};

/**
 * A single skill advertised by an agent in its A2A agent card
 * (/.well-known/agent-card.json).
 */
export type AgentSkillFilterInput = {
	and?: Array<AgentSkillFilterInput> | null | undefined;
	/** Gets or sets the skill description. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the skill's unique identifier within the agent's card. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable skill name. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<AgentSkillFilterInput> | null | undefined;
	/** Gets or sets free-text tags categorizing the skill. */
	tags?: ListStringOperationFilterInput | null | undefined;
};

/**
 * User-controlled lifecycle state of an agent (the customer's enable/disable intent). Set via
 * updateAgent. Distinct from AgentHealth, which is system-observed reachability.
 * Mirrors the ProjectStatus convention where status is the user-controlled lifecycle.
 */
export type AgentStatus =
	/** Agent is disabled by the user; it cannot be assigned new tasks. */
	| "DISABLED"
	/** Agent is enabled and may be assigned tasks. */
	| "ENABLED";

export type AgentStatusOperationFilterInput = {
	eq?: AgentStatus | null | undefined;
	in?: Array<AgentStatus> | null | undefined;
	neq?: AgentStatus | null | undefined;
	nin?: Array<AgentStatus> | null | undefined;
};

/** The uploaded files to build a deployment's graph from. */
export type AnalyzeTerraformInput = {
	/**
	 * Staging keys from requestTerraformUpload that have already been uploaded to. At least
	 * one is required. Upload the .tfstate and the .tf sources together for the most
	 * accurate graph.
	 */
	uploadKeys?: Array<string> | null | undefined;
};

/**
 * The catalog of apps an organization admin can make available to workspaces (ADR-061). Adding an
 * app means a new member here plus whatever the app needs to act; nothing about existing apps changes.
 */
export type AppKind =
	/** The StratoNext GitHub App: workspaces connect an installation to link repositories (REQ-090). */
	| "GIT_HUB"
	/**
	 * Port: an org-level machine identity that sees the workspaces where it is enabled and
	 *             creates, dispatches and cancels tasks there.
	 */
	| "PORT";

/** User-controlled lifecycle of an app at the organization or workspace level. */
export type AppStatus =
	/**
	 * The app is switched off. At the org level this is a hard stop for every workspace;
	 *             workspace rows and connections are kept so re-enabling restores them.
	 */
	| "DISABLED"
	/** The app may be used (org level: workspaces may enable it; workspace level: it is active there). */
	| "ENABLED";

/** Whether a human approver approved or rejected the request. */
export type ApprovalDecision =
	/** The request is approved. */
	| "APPROVED"
	/** The request is rejected. */
	| "REJECTED";

/** The type of request being routed through the approval workflow. */
export type ApprovalRequestType =
	/** A proposed infrastructure change submitted by ChangeManagementService. */
	| "CHANGE_REQUEST"
	/**
	 * An approval-gated cloud credential escalation submitted by CredentialBrokerService. Unlike a
	 * change, it is reusable within a bounded window rather than consumed at first use (ADR-045).
	 */
	| "CREDENTIAL_REQUEST";

export type ApprovalRequestTypeOperationFilterInput = {
	eq?: ApprovalRequestType | null | undefined;
	in?: Array<ApprovalRequestType> | null | undefined;
	neq?: ApprovalRequestType | null | undefined;
	nin?: Array<ApprovalRequestType> | null | undefined;
};

/** The risk classification of an approval request. Set by the submitting service; never modified by ApprovalService. */
export type ApprovalRiskLevel =
	/**
	 * Catastrophic risk: irreversible destruction of data or stateful infrastructure, or the
	 * removal of a security control. Always requires a human — it ranks above every
	 * AutoApprovalThreshold, so no environment setting can auto-approve it (ADR-049).
	 */
	| "CRITICAL"
	/** High risk. */
	| "HIGH"
	/** Low risk. */
	| "LOW"
	/** Medium risk. */
	| "MEDIUM";

export type ApprovalRiskLevelOperationFilterInput = {
	eq?: ApprovalRiskLevel | null | undefined;
	in?: Array<ApprovalRiskLevel> | null | undefined;
	neq?: ApprovalRiskLevel | null | undefined;
	nin?: Array<ApprovalRiskLevel> | null | undefined;
};

/** Lifecycle status of an ArtemisApproval record. */
export type ApprovalStatus =
	/** Approved — either automatically or by a human approver. */
	| "APPROVED"
	/** The approval was consumed (post-approval action completed). Terminal. */
	| "CONSUMED"
	/** The approval window elapsed before a decision was made. */
	| "EXPIRED"
	/** Invalidated by a newer submission for the same task. */
	| "INVALIDATED"
	/** Awaiting human review. */
	| "PENDING"
	/** Rejected by a human approver. */
	| "REJECTED";

export type ApprovalStatusOperationFilterInput = {
	eq?: ApprovalStatus | null | undefined;
	in?: Array<ApprovalStatus> | null | undefined;
	neq?: ApprovalStatus | null | undefined;
	nin?: Array<ApprovalStatus> | null | undefined;
};

/**
 * Represents a registered agent on the platform.
 * Environment sub-workers are not agents and have no record here (ADR-016).
 */
export type ArtemisAgentFilterInput = {
	/**
	 * Gets or sets the raw agent card JSON, fetched from the agent's A2A endpoint.
	 * Null until the first capability discovery run.
	 */
	agentCard?: StringOperationFilterInput | null | undefined;
	and?: Array<ArtemisAgentFilterInput> | null | undefined;
	/**
	 * Gets or sets the OAuth2 client ID used to authenticate this agent. Platform-owned
	 *             and stable across redeploys (ADR-038); presented by the agent to the platform agent-token
	 *             endpoint.
	 */
	clientId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the optional expiry of the client secret (ADR-050). Null means the secret
	 * never expires. Enforced at token issuance: an expired credential is refused with the same
	 * uniform invalid-client error as a wrong secret. Set at creation or rotation of an
	 * External agent's credential.
	 */
	clientSecretExpiresAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the provider-specific settings chosen at deploy time (e.g. AWS Region).
	 * Generic key-value bag so new per-provider deploy options can be added without a schema change.
	 * Persisted so post-deploy operations (ping, delete, invoke) target the same region the agent
	 * was deployed into rather than the environment's default.
	 */
	deploymentSettings?:
		| ListFilterInputTypeOfKeyValuePairOfStringAndStringFilterInput
		| null
		| undefined;
	/** Gets or sets the A2A endpoint URL for this agent. */
	endpointUrl?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the environment this agent runs in. The association is optional: null means
	 * the agent is not running on any tracked environment — it may be on a local machine, a
	 * remote A2A host, or anywhere else outside platform-managed infrastructure (ARCH-030).
	 * Managed deployment requires an environment because provisioning needs one; identity does not.
	 */
	environmentId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the system-observed reachability of the agent's runtime, refreshed by health
	 * pings. New managed agents start Provisioning. Always null for
	 * External agents: the platform has no channel to reach them (no A2A),
	 * so they have no reachability — LastSeenAt is their only liveness signal.
	 * Orthogonal to Status.
	 */
	health?: NullableOfAgentHealthOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets how the platform relates to this agent's runtime (ADR-050). Immutable after
	 * creation. Managed agents are deployed and invoked by the platform;
	 * External agents run on untracked infrastructure and pull their work.
	 */
	kind?: AgentKindOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the last time this agent successfully authenticated at the agent-token
	 * endpoint (ARCH-030). Null until the first token issuance. For External
	 * agents this heartbeat is the liveness signal that derives Health, since the
	 * platform can never ping them.
	 */
	lastSeenAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable name of the agent. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisAgentFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the user ID of the person who created this agent. */
	ownerId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/**
	 * When the background purge reclaimed this row's external cloud resources (runtime/IAM/SSM).
	 * Null until then; set so the sweep never re-runs teardown for the same soft-deleted row.
	 */
	physicalCleanupAt?: DateTimeOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the agent role. Immutable after provisioning.
	 * Workers are task assignees; Lead-Agents are workspace-level orchestrators.
	 */
	role?: AgentRoleOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the provider-specific runtime ID for this agent's deployed instance
	 * (e.g., a Bedrock AgentCore runtime ARN). Null for externally-hosted agents not
	 * managed by the Artemis platform.
	 */
	runtimeId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the skills advertised in the agent's A2A card. Empty until the first
	 * successful capability discovery.
	 */
	skills?: ListFilterInputTypeOfAgentSkillFilterInput | null | undefined;
	/**
	 * Gets or sets the user-controlled lifecycle state (enable/disable intent). New agents start
	 * Enabled. Orthogonal to Health.
	 */
	status?: AgentStatusOperationFilterInput | null | undefined;
	/**
	 * Gets or sets whether the agent's A2A card advertises push notification support. Refreshed
	 * on every successful capability discovery.
	 */
	supportsPushNotifications?: BooleanOperationFilterInput | null | undefined;
	/**
	 * Gets or sets whether the agent's A2A card advertises streaming support. Refreshed on every
	 * successful capability discovery (agent creation/deployment, and every ping).
	 */
	supportsStreaming?: BooleanOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * Represents a registered agent on the platform.
 * Environment sub-workers are not agents and have no record here (ADR-016).
 */
export type ArtemisAgentSortInput = {
	/**
	 * Gets or sets the raw agent card JSON, fetched from the agent's A2A endpoint.
	 * Null until the first capability discovery run.
	 */
	agentCard?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the OAuth2 client ID used to authenticate this agent. Platform-owned
	 *             and stable across redeploys (ADR-038); presented by the agent to the platform agent-token
	 *             endpoint.
	 */
	clientId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the optional expiry of the client secret (ADR-050). Null means the secret
	 * never expires. Enforced at token issuance: an expired credential is refused with the same
	 * uniform invalid-client error as a wrong secret. Set at creation or rotation of an
	 * External agent's credential.
	 */
	clientSecretExpiresAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the A2A endpoint URL for this agent. */
	endpointUrl?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the environment this agent runs in. The association is optional: null means
	 * the agent is not running on any tracked environment — it may be on a local machine, a
	 * remote A2A host, or anywhere else outside platform-managed infrastructure (ARCH-030).
	 * Managed deployment requires an environment because provisioning needs one; identity does not.
	 */
	environmentId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the system-observed reachability of the agent's runtime, refreshed by health
	 * pings. New managed agents start Provisioning. Always null for
	 * External agents: the platform has no channel to reach them (no A2A),
	 * so they have no reachability — LastSeenAt is their only liveness signal.
	 * Orthogonal to Status.
	 */
	health?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/**
	 * Gets or sets how the platform relates to this agent's runtime (ADR-050). Immutable after
	 * creation. Managed agents are deployed and invoked by the platform;
	 * External agents run on untracked infrastructure and pull their work.
	 */
	kind?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the last time this agent successfully authenticated at the agent-token
	 * endpoint (ARCH-030). Null until the first token issuance. For External
	 * agents this heartbeat is the liveness signal that derives Health, since the
	 * platform can never ping them.
	 */
	lastSeenAt?: SortEnumType | null | undefined;
	/** Gets or sets the human-readable name of the agent. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets or sets the user ID of the person who created this agent. */
	ownerId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/**
	 * When the background purge reclaimed this row's external cloud resources (runtime/IAM/SSM).
	 * Null until then; set so the sweep never re-runs teardown for the same soft-deleted row.
	 */
	physicalCleanupAt?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the agent role. Immutable after provisioning.
	 * Workers are task assignees; Lead-Agents are workspace-level orchestrators.
	 */
	role?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the provider-specific runtime ID for this agent's deployed instance
	 * (e.g., a Bedrock AgentCore runtime ARN). Null for externally-hosted agents not
	 * managed by the Artemis platform.
	 */
	runtimeId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the user-controlled lifecycle state (enable/disable intent). New agents start
	 * Enabled. Orthogonal to Health.
	 */
	status?: SortEnumType | null | undefined;
	/**
	 * Gets or sets whether the agent's A2A card advertises push notification support. Refreshed
	 * on every successful capability discovery.
	 */
	supportsPushNotifications?: SortEnumType | null | undefined;
	/**
	 * Gets or sets whether the agent's A2A card advertises streaming support. Refreshed on every
	 * successful capability discovery (agent creation/deployment, and every ping).
	 */
	supportsStreaming?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/** Represents a single approval record managed by ApprovalService. */
export type ArtemisApprovalFilterInput = {
	/** Gets or sets a detailed description of the action the agent wants to perform, as submitted by the agent. Visible to human approvers. */
	actionDescription?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the ID of the agent that submitted the request. */
	agentId?: StringOperationFilterInput | null | undefined;
	and?: Array<ArtemisApprovalFilterInput> | null | undefined;
	/** Gets or sets the deadline by which a human must act if auto-approval does not apply. */
	approvalWindowExpiresAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the target environment ID, used to retrieve the automation policy. */
	environmentId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the time this approval was first redeemed for credentials. Null until the first
	 * redemption. For a reusable credential_request the reuse window is measured from this
	 * timestamp (not from approval), so an approval that sits unredeemed for a long time — a human can
	 * take days to approve — still grants a full reuse window once its task actually starts using it
	 * (ADR-045).
	 */
	firstRedeemedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets a short human-readable name for this approval request, provided by the submitting agent. */
	name?: StringOperationFilterInput | null | undefined;
	/** Gets or sets an optional human-readable note attached at resolution time. */
	notes?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisApprovalFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets an opaque reference to the type-specific record owned by the submitting service. */
	payloadRef?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the type of this approval request. */
	requestType?: ApprovalRequestTypeOperationFilterInput | null | undefined;
	/** Gets or sets the timestamp of resolution. */
	resolvedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the internal user ID or agent ID that resolved this approval, if resolved by a human. */
	resolvedBy?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the risk level, classified by the submitting service. */
	riskLevel?: ApprovalRiskLevelOperationFilterInput | null | undefined;
	/** Gets or sets the current lifecycle status. */
	status?: ApprovalStatusOperationFilterInput | null | undefined;
	/** Gets or sets the ID of the task paused pending this approval. */
	taskId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the timestamp of the last status update. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/** Represents a single approval record managed by ApprovalService. */
export type ArtemisApprovalSortInput = {
	/** Gets or sets a detailed description of the action the agent wants to perform, as submitted by the agent. Visible to human approvers. */
	actionDescription?: SortEnumType | null | undefined;
	/** Gets or sets the ID of the agent that submitted the request. */
	agentId?: SortEnumType | null | undefined;
	/** Gets or sets the deadline by which a human must act if auto-approval does not apply. */
	approvalWindowExpiresAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the target environment ID, used to retrieve the automation policy. */
	environmentId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the time this approval was first redeemed for credentials. Null until the first
	 * redemption. For a reusable credential_request the reuse window is measured from this
	 * timestamp (not from approval), so an approval that sits unredeemed for a long time — a human can
	 * take days to approve — still grants a full reuse window once its task actually starts using it
	 * (ADR-045).
	 */
	firstRedeemedAt?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets a short human-readable name for this approval request, provided by the submitting agent. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets an optional human-readable note attached at resolution time. */
	notes?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets an opaque reference to the type-specific record owned by the submitting service. */
	payloadRef?: SortEnumType | null | undefined;
	/** Gets or sets the type of this approval request. */
	requestType?: SortEnumType | null | undefined;
	/** Gets or sets the timestamp of resolution. */
	resolvedAt?: SortEnumType | null | undefined;
	/** Gets or sets the internal user ID or agent ID that resolved this approval, if resolved by a human. */
	resolvedBy?: SortEnumType | null | undefined;
	/** Gets or sets the risk level, classified by the submitting service. */
	riskLevel?: SortEnumType | null | undefined;
	/** Gets or sets the current lifecycle status. */
	status?: SortEnumType | null | undefined;
	/** Gets or sets the ID of the task paused pending this approval. */
	taskId?: SortEnumType | null | undefined;
	/** Gets or sets the timestamp of the last status update. */
	updatedAt?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/** Represents a binary or document asset owned by a workspace entity. */
export type ArtemisAssetFilterInput = {
	and?: Array<ArtemisAssetFilterInput> | null | undefined;
	/** Gets or sets the category, supplied by the caller at registration time. */
	category?: AssetCategoryOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the free-text description. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the caller-supplied MIME type. */
	mediaType?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable name. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisAssetFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the parent ID. */
	parentId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the parent type. */
	parentType?: ParentTypeOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/**
	 * When the background purge reclaimed this row's S3 object. Null until then; set so the sweep
	 * never re-runs the S3 delete for the same soft-deleted row.
	 */
	physicalCleanupAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the size in bytes of the stored object. */
	size?: LongOperationFilterInput | null | undefined;
	/** Gets or sets the temporary staging path: staging/{workspaceId}/{assetId}/{filename}. */
	stagingKey?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the lifecycle state of this asset. */
	state?: AssetStateOperationFilterInput | null | undefined;
	/** Gets or sets the confirmed live path: assets/{workspaceId}/{assetId}/{filename}. */
	storageKey?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the summary. Populated for Document assets; auto-generated by the platform if not supplied by the caller. */
	summary?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the asset record was last updated. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * An append-only audit record of a single mutation, satisfying REQ-016 (Universal Audit Trail).
 *
 * Deliberately NOT an ArtemisEntity: it carries no soft-delete column, is never
 * itself audited, and holds no foreign key to the subject entity. It stores a self-contained
 * snapshot so the record survives the subject's eventual purge. See
 * ADR-036-data-lifecycle-soft-delete-audit-retention.
 */
export type ArtemisAuditLogFilterInput = {
	/** Gets or sets the kind of action. */
	action?: AuditActionOperationFilterInput | null | undefined;
	/** Gets or sets the OAuth2 client_id of the actor, when an M2M client (agent) performed the action. */
	actorClientId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the verified provider identity of the actor as {issuer}|{subject}.
	 * This is the "verified identity" REQ-016 requires; it does not change even if the internal user is remapped.
	 */
	actorSubject?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the internal user ID of the actor, when a human performed the action. */
	actorUserId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the JSON snapshot of the entity after the change (null on delete/purge). Stored as jsonb. */
	after?: StringOperationFilterInput | null | undefined;
	and?: Array<ArtemisAuditLogFilterInput> | null | undefined;
	/** Gets or sets the JSON snapshot of the entity before the change (null on create). Stored as jsonb. */
	before?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the subject entity ID. A plain string, intentionally not a foreign key. */
	entityId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the subject entity type (the entity's Particle, e.g. "Task", "Project"). */
	entityType?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the audit record ID (prefixed aud_). */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the UTC time the action occurred. Also the partition key for future range partitioning. */
	occurredAt?: DateTimeOperationFilterInput | null | undefined;
	or?: Array<ArtemisAuditLogFilterInput> | null | undefined;
	/** Gets or sets the organization the action belongs to. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the workspace the subject entity belongs to, when applicable. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * An append-only audit record of a single mutation, satisfying REQ-016 (Universal Audit Trail).
 *
 * Deliberately NOT an ArtemisEntity: it carries no soft-delete column, is never
 * itself audited, and holds no foreign key to the subject entity. It stores a self-contained
 * snapshot so the record survives the subject's eventual purge. See
 * ADR-036-data-lifecycle-soft-delete-audit-retention.
 */
export type ArtemisAuditLogSortInput = {
	/** Gets or sets the kind of action. */
	action?: SortEnumType | null | undefined;
	/** Gets or sets the OAuth2 client_id of the actor, when an M2M client (agent) performed the action. */
	actorClientId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the verified provider identity of the actor as {issuer}|{subject}.
	 * This is the "verified identity" REQ-016 requires; it does not change even if the internal user is remapped.
	 */
	actorSubject?: SortEnumType | null | undefined;
	/** Gets or sets the internal user ID of the actor, when a human performed the action. */
	actorUserId?: SortEnumType | null | undefined;
	/** Gets or sets the JSON snapshot of the entity after the change (null on delete/purge). Stored as jsonb. */
	after?: SortEnumType | null | undefined;
	/** Gets or sets the JSON snapshot of the entity before the change (null on create). Stored as jsonb. */
	before?: SortEnumType | null | undefined;
	/** Gets or sets the subject entity ID. A plain string, intentionally not a foreign key. */
	entityId?: SortEnumType | null | undefined;
	/** Gets or sets the subject entity type (the entity's Particle, e.g. "Task", "Project"). */
	entityType?: SortEnumType | null | undefined;
	/** Gets or sets the audit record ID (prefixed aud_). */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the UTC time the action occurred. Also the partition key for future range partitioning. */
	occurredAt?: SortEnumType | null | undefined;
	/** Gets or sets the organization the action belongs to. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets or sets the workspace the subject entity belongs to, when applicable. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * Represents a deployment of a project to an environment, including the provider-specific
 * configuration required to discover and refresh its infrastructure graph.
 */
export type ArtemisDeploymentFilterInput = {
	and?: Array<ArtemisDeploymentFilterInput> | null | undefined;
	/**
	 * Gets or sets the provider-specific configuration for this deployment.
	 * For AWS environments this is an AwsDeploymentConfig serialised as JSONB.
	 * Null for providers that do not yet require structured configuration.
	 */
	config?: AwsDeploymentConfigFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets an optional free-text description of the deployment. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the ID of the environment this deployment targets. */
	environmentId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the semantic graph of this deployment's infrastructure.
	 * Populated (or refreshed) by RefreshDeployment. Stored as JSONB inline on the row.
	 */
	graph?: SemanticGraphFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable name of the deployment. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisDeploymentFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the ID of the project this deployment belongs to. */
	projectId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the Terraform provenance for this deployment, set when its graph was built from
	 * supplied Terraform files. Null for deployments discovered from a live provider.
	 */
	terraformConfig?: TerraformDeploymentConfigFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * Represents a registered cloud target (e.g. AWS account, Azure subscription) within a workspace.
 * Agents operate against environments; no credentials are stored here.
 */
export type ArtemisEnvironmentFilterInput = {
	/**
	 * Gets or sets whether agents may access (operate on) this environment via its operations role.
	 * User-controlled and independent of the operations-role metadata. Hard-enforced: the credential
	 * broker refuses to mint base or escalated credentials for the environment while this is false,
	 * and the agent has no way to assume the role itself (ADR-041).
	 */
	allowAgentsToAccess?: BooleanOperationFilterInput | null | undefined;
	/**
	 * Gets or sets whether agents may be deployed into this environment. User-controlled and
	 * independent of the deployment-role metadata: disabling it keeps the stored ARNs but blocks
	 * new deployments. Enforced at agent creation (see AgentService.CreateAgentAsync).
	 */
	allowAgentsToBeDeployed?: BooleanOperationFilterInput | null | undefined;
	and?: Array<ArtemisEnvironmentFilterInput> | null | undefined;
	/**
	 * Gets or sets the highest risk level that may be auto-approved in this environment.
	 * Defaults to Medium. Production targets need no stricter
	 * default: the risk evaluator escalates one level for them, so the same action lands a tier
	 * higher and is gated by this very threshold.
	 */
	autoApproveUpTo?:
		| AutoApprovalThresholdOperationFilterInput
		| null
		| undefined;
	/**
	 * Gets or sets the classification of this environment (production or non-production).
	 * Defaults to NonProduction on registration.
	 */
	classification?:
		| EnvironmentClassificationOperationFilterInput
		| null
		| undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the list of deployments associated with this environment. */
	deployments?:
		| ListFilterInputTypeOfArtemisDeploymentFilterInput
		| null
		| undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the metadata for this environment. */
	metadata?:
		| ListFilterInputTypeOfKeyValuePairOfStringAndStringFilterInput
		| null
		| undefined;
	/** Gets or sets the human-readable name. Unique within a workspace. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisEnvironmentFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the user ID of the person accountable for this environment. Set to the creator and
	 * transferable by the owner or a workspace administrator; it grants no permissions.
	 */
	ownerId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/**
	 * When the background purge reclaimed this row's external cloud resources (Secrets Manager
	 * container). Null until then; set so the sweep never re-runs teardown for the same row.
	 */
	physicalCleanupAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the cloud provider for this environment. */
	provider?: EnvironmentProviderOperationFilterInput | null | undefined;
	/** Gets or sets the lifecycle status of this environment. */
	status?: EnvironmentStatusOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * Represents a registered cloud target (e.g. AWS account, Azure subscription) within a workspace.
 * Agents operate against environments; no credentials are stored here.
 */
export type ArtemisEnvironmentSortInput = {
	/**
	 * Gets or sets whether agents may access (operate on) this environment via its operations role.
	 * User-controlled and independent of the operations-role metadata. Hard-enforced: the credential
	 * broker refuses to mint base or escalated credentials for the environment while this is false,
	 * and the agent has no way to assume the role itself (ADR-041).
	 */
	allowAgentsToAccess?: SortEnumType | null | undefined;
	/**
	 * Gets or sets whether agents may be deployed into this environment. User-controlled and
	 * independent of the deployment-role metadata: disabling it keeps the stored ARNs but blocks
	 * new deployments. Enforced at agent creation (see AgentService.CreateAgentAsync).
	 */
	allowAgentsToBeDeployed?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the highest risk level that may be auto-approved in this environment.
	 * Defaults to Medium. Production targets need no stricter
	 * default: the risk evaluator escalates one level for them, so the same action lands a tier
	 * higher and is gated by this very threshold.
	 */
	autoApproveUpTo?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the classification of this environment (production or non-production).
	 * Defaults to NonProduction on registration.
	 */
	classification?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the human-readable name. Unique within a workspace. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the user ID of the person accountable for this environment. Set to the creator and
	 * transferable by the owner or a workspace administrator; it grants no permissions.
	 */
	ownerId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/**
	 * When the background purge reclaimed this row's external cloud resources (Secrets Manager
	 * container). Null until then; set so the sweep never re-runs teardown for the same row.
	 */
	physicalCleanupAt?: SortEnumType | null | undefined;
	/** Gets or sets the cloud provider for this environment. */
	provider?: SortEnumType | null | undefined;
	/** Gets or sets the lifecycle status of this environment. */
	status?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * Defines a recurring scheduled task template within a project.
 * Each firing creates one ArtemisJobRun and one ArtemisTask.
 */
export type ArtemisJobFilterInput = {
	/** Gets or sets the agent assigned to execute each job run. */
	agentId?: StringOperationFilterInput | null | undefined;
	and?: Array<ArtemisJobFilterInput> | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the deployment IDs scoped to each run. Empty list means all deployments. */
	deploymentIds?: ListStringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the description. Required — it becomes each run's task description,
	 *             which is what the assigned agent receives as its message.
	 */
	description?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the IDs of the environments each run targets directly.
	 * Copied onto every spawned task, so a job can operate on an environment
	 * even when it has no project (or the project has no deployments).
	 */
	environmentIds?: ListStringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the instructions passed to the agent on each run. */
	instructions?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the UTC timestamp of the last successful firing. Advisory only. */
	lastFiredAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable job name. */
	name?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the advisory next fire time. The external scheduler is authoritative. */
	nextFireAt?: DateTimeOperationFilterInput | null | undefined;
	or?: Array<ArtemisJobFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the project this job belongs to.
	 * Null when the job targets environments directly (see EnvironmentIds).
	 */
	projectId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the cron schedule expression (EventBridge format). */
	schedule?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the external scheduler resource ID (e.g. EventBridge schedule ARN).
	 * Used for update/pause/resume/delete operations.
	 */
	schedulerResourceId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the job lifecycle status. */
	status?: JobStatusOperationFilterInput | null | undefined;
	/** Gets or sets the IANA timezone for the schedule. Defaults to UTC. */
	timezone?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the last-updated timestamp. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/** Records one firing of a job. Each run has a backing ArtemisTask. */
export type ArtemisJobRunFilterInput = {
	and?: Array<ArtemisJobRunFilterInput> | null | undefined;
	/** Gets or sets the UTC timestamp when the run completed. Null while running. */
	completedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the UTC timestamp when the run was created/fired. */
	firedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the parent job ID. */
	jobId?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisJobRunFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the project ID (denormalized from the job for query convenience). Null for environment-targeted jobs. */
	projectId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the created task ID. Null until the task is created. */
	taskId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets how the run was triggered. */
	triggerType?: JobRunTriggerTypeOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/** Records one firing of a job. Each run has a backing ArtemisTask. */
export type ArtemisJobRunSortInput = {
	/** Gets or sets the UTC timestamp when the run completed. Null while running. */
	completedAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the UTC timestamp when the run was created/fired. */
	firedAt?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the parent job ID. */
	jobId?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets the project ID (denormalized from the job for query convenience). Null for environment-targeted jobs. */
	projectId?: SortEnumType | null | undefined;
	/** Gets or sets the created task ID. Null until the task is created. */
	taskId?: SortEnumType | null | undefined;
	/** Gets or sets how the run was triggered. */
	triggerType?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * Defines a recurring scheduled task template within a project.
 * Each firing creates one ArtemisJobRun and one ArtemisTask.
 */
export type ArtemisJobSortInput = {
	/** Gets or sets the agent assigned to execute each job run. */
	agentId?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the description. Required — it becomes each run's task description,
	 *             which is what the assigned agent receives as its message.
	 */
	description?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the instructions passed to the agent on each run. */
	instructions?: SortEnumType | null | undefined;
	/** Gets or sets the UTC timestamp of the last successful firing. Advisory only. */
	lastFiredAt?: SortEnumType | null | undefined;
	/** Gets or sets the human-readable job name. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets the advisory next fire time. The external scheduler is authoritative. */
	nextFireAt?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the project this job belongs to.
	 * Null when the job targets environments directly (see EnvironmentIds).
	 */
	projectId?: SortEnumType | null | undefined;
	/** Gets or sets the cron schedule expression (EventBridge format). */
	schedule?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the external scheduler resource ID (e.g. EventBridge schedule ARN).
	 * Used for update/pause/resume/delete operations.
	 */
	schedulerResourceId?: SortEnumType | null | undefined;
	/** Gets or sets the job lifecycle status. */
	status?: SortEnumType | null | undefined;
	/** Gets or sets the IANA timezone for the schedule. Defaults to UTC. */
	timezone?: SortEnumType | null | undefined;
	/** Gets or sets the last-updated timestamp. */
	updatedAt?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * Represents a project in the Artemis platform.
 * A project groups tasks, workflows, and jobs within a workspace.
 */
export type ArtemisProjectFilterInput = {
	and?: Array<ArtemisProjectFilterInput> | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the deployments related to the project. */
	deployments?:
		| ListFilterInputTypeOfArtemisDeploymentFilterInput
		| null
		| undefined;
	/** Gets or sets the free-text description of the project's purpose. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the agent instructions related to the project. */
	instructions?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the links related to the project. */
	links?: ListFilterInputTypeOfProjectLinkFilterInput | null | undefined;
	/** Gets or sets the human-readable name. Unique within a workspace. */
	name?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the notes related to the project. */
	notes?: ListStringOperationFilterInput | null | undefined;
	or?: Array<ArtemisProjectFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the user ID of the person accountable for this project. Set to the creator and
	 * transferable by the owner or a workspace administrator; it grants no permissions.
	 */
	ownerId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the repositories linked to the project. */
	projectRepositories?:
		| ListFilterInputTypeOfArtemisProjectRepositoryFilterInput
		| null
		| undefined;
	/** Gets or sets the lifecycle status. Archived projects are read-only. */
	status?: ProjectStatusOperationFilterInput | null | undefined;
	/** Gets or sets the AI-generated summary of the task. */
	summary?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the project type */
	type?: ProjectTypeOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * Links a project to a repository in an external git provider (REQ-092), via a workspace's
 * existing ArtemisGitProviderConnection installation. Holds only identifiers the
 * provider considers stable — cloning, pushing, or minting agent credentials against the
 * repository is a separate future feature (REQ-091), not this entity's concern.
 */
export type ArtemisProjectRepositoryFilterInput = {
	and?: Array<ArtemisProjectRepositoryFilterInput> | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the repository's default branch, or null when not yet known. */
	defaultBranch?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the ID of the git provider connection whose installation grants access to this repository. */
	gitProviderConnectionId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisProjectRepositoryFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the ID of the project this repository is linked to. */
	projectId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the external git provider (e.g. GitHub), copied from the connection at link time. */
	provider?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the provider's stable numeric repository ID (e.g. GitHub's repo id), used to detect renames. */
	repositoryExternalId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the repository's fully-qualified name (e.g. owner/repo). */
	repositoryFullName?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * Represents a project in the Artemis platform.
 * A project groups tasks, workflows, and jobs within a workspace.
 */
export type ArtemisProjectSortInput = {
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the free-text description of the project's purpose. */
	description?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the agent instructions related to the project. */
	instructions?: SortEnumType | null | undefined;
	/** Gets or sets the human-readable name. Unique within a workspace. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the user ID of the person accountable for this project. Set to the creator and
	 * transferable by the owner or a workspace administrator; it grants no permissions.
	 */
	ownerId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets the lifecycle status. Archived projects are read-only. */
	status?: SortEnumType | null | undefined;
	/** Gets or sets the AI-generated summary of the task. */
	summary?: SortEnumType | null | undefined;
	/** Gets or sets the project type */
	type?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * A workspace-level knowledge container that bundles a freeform document with asset references
 * and optional project links. Accessible to both human users and agents.
 */
export type ArtemisReportFilterInput = {
	and?: Array<ArtemisReportFilterInput> | null | undefined;
	/** Gets or sets the primary freeform document body. No schema imposed. */
	body?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the internal user ID of the user who created this report. */
	createdBy?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisReportFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the internal user ID of the assigned owner. */
	ownerId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the list of project IDs to which this report is linked. */
	projectIds?: ListStringOperationFilterInput | null | undefined;
	/** Gets or sets the report lifecycle status. */
	status?: ReportStatusOperationFilterInput | null | undefined;
	/** Gets or sets the AI-generated summary of the report. */
	summary?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable title of this report. */
	title?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the report type. */
	type?: ReportTypeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was last updated. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * A workspace-level knowledge container that bundles a freeform document with asset references
 * and optional project links. Accessible to both human users and agents.
 */
export type ArtemisReportSortInput = {
	/** Gets or sets the primary freeform document body. No schema imposed. */
	body?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the internal user ID of the user who created this report. */
	createdBy?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets or sets the internal user ID of the assigned owner. */
	ownerId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets the report lifecycle status. */
	status?: SortEnumType | null | undefined;
	/** Gets or sets the AI-generated summary of the report. */
	summary?: SortEnumType | null | undefined;
	/** Gets or sets the human-readable title of this report. */
	title?: SortEnumType | null | undefined;
	/** Gets or sets the report type. */
	type?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was last updated. */
	updatedAt?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * Represents a discrete unit of work on the Artemis platform.
 * Tasks are the authoritative record of work; A2A tasks are execution vehicles.
 */
export type ArtemisTaskFilterInput = {
	/**
	 * Gets or sets the A2A task ID returned by the agent when this task was dispatched
	 * (message/send's result ID). Required to send a tasks/cancel signal for
	 * this task later; null until the task has actually been dispatched to an agent.
	 */
	a2ATaskId?: StringOperationFilterInput | null | undefined;
	and?: Array<ArtemisTaskFilterInput> | null | undefined;
	/** Gets or sets the assets related to the task. Not mapped — resolved by GraphQL. */
	assets?: ListFilterInputTypeOfArtemisAssetFilterInput | null | undefined;
	/**
	 * Gets or sets the ID of the assignee for this task (agent ID or user ID depending on AssigneeType).
	 * Null when the task has no assignee.
	 */
	assigneeId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the type of the assignee. Null when the task has no assignee. */
	assigneeType?: NullableOfAssigneeTypeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/**
	 * Gets or sets who created the task: the authenticated user ID, or the agent ID for an
	 * agent-created task. Set by the server from the caller, never from input. Null for tasks the
	 * platform creates itself (job runs) and for rows that predate the column. Used to stop a person
	 * approving escalations on work they set up themselves (SEC-003 M6).
	 */
	createdBy?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the list of task IDs this task depends on.
	 * The task will not be dispatched until all referenced tasks have reached a terminal status.
	 */
	dependsOn?: ListStringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the IDs of the project deployments this task is meant to work on.
	 * Optional but recommended — the assigned agent resolves each deployment's environment
	 * via its own pre-start hook chain rather than inferring it from free-text instructions.
	 */
	deploymentIds?: ListStringOperationFilterInput | null | undefined;
	/** Gets or sets the free-text description of the work to be done. */
	description?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the IDs of the environments this task targets directly.
	 * Optional — lets the assigned agent operate on an environment even when the
	 * task's project has no deployments tying it to that environment.
	 */
	environmentIds?: ListStringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the agent instructions related to the project. */
	instructions?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the job run ID that created this task. Null for tasks not created by a job.
	 * When non-null, TaskService notifies JobService when this task reaches a terminal state.
	 */
	jobRunId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the links related to the task. */
	links?: ListFilterInputTypeOfTaskLinkFilterInput | null | undefined;
	/** Gets or sets the cumulative execution metrics across all runs. */
	metrics?: TaskMetricsFilterInput | null | undefined;
	/** Gets or sets the human-readable title of the task. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisTaskFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the ID of the project this task belongs to.
	 * Null when the task targets environments directly (see EnvironmentIds).
	 */
	projectId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the agent's latest output: the final artifact upon completion, or — while
	 * awaiting input — the question it's paused on, until a later push overwrites it.
	 */
	result?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the current lifecycle status of the task. */
	status?: TaskStatusOperationFilterInput | null | undefined;
	/**
	 * Context for the current Status: when Status is
	 * InputRequired and a user has responded, this holds the ID of the
	 * WorklogEntry carrying that response, so dispatch can replay it. Starts as the
	 * literal string "task created". Exposed on the GraphQL API as a diagnostic pointer — it is an
	 * entry ID or a sentinel string, not human-readable prose; the worklog remains the public
	 * activity record.
	 */
	statusReason?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the AI-generated summary of the task. */
	summary?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the agent's self-verification criteria: a description of what the agent
	 * will check to confirm the task succeeded. May be set by the user at creation time or
	 * by the agent when it accepts the task. Null when no verification is defined.
	 */
	verificationCriteria?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the agent's notes from the verification run: what passed, what failed,
	 * and any relevant observations. Null when no verification has been run.
	 */
	verificationNotes?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the outcome of the agent's verification step.
	 * Null until the agent explicitly reports a verification result.
	 * Orthogonal to Status — a task can be Completed with VerificationFailed.
	 */
	verificationStatus?:
		| NullableOfVerificationStatusOperationFilterInput
		| null
		| undefined;
	/**
	 * Gets or sets the ID of the parent workflow.
	 * Null for standalone tasks. Set by WorkflowService and never changed.
	 */
	workflowId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the append-only worklog recording status transitions,
	 * agent messages, restart events, dependency resolution events, and input responses.
	 */
	worklog?: ListFilterInputTypeOfWorklogEntryFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * Represents a discrete unit of work on the Artemis platform.
 * Tasks are the authoritative record of work; A2A tasks are execution vehicles.
 */
export type ArtemisTaskSortInput = {
	/**
	 * Gets or sets the A2A task ID returned by the agent when this task was dispatched
	 * (message/send's result ID). Required to send a tasks/cancel signal for
	 * this task later; null until the task has actually been dispatched to an agent.
	 */
	a2ATaskId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the ID of the assignee for this task (agent ID or user ID depending on AssigneeType).
	 * Null when the task has no assignee.
	 */
	assigneeId?: SortEnumType | null | undefined;
	/** Gets or sets the type of the assignee. Null when the task has no assignee. */
	assigneeType?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/**
	 * Gets or sets who created the task: the authenticated user ID, or the agent ID for an
	 * agent-created task. Set by the server from the caller, never from input. Null for tasks the
	 * platform creates itself (job runs) and for rows that predate the column. Used to stop a person
	 * approving escalations on work they set up themselves (SEC-003 M6).
	 */
	createdBy?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the free-text description of the work to be done. */
	description?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the agent instructions related to the project. */
	instructions?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the job run ID that created this task. Null for tasks not created by a job.
	 * When non-null, TaskService notifies JobService when this task reaches a terminal state.
	 */
	jobRunId?: SortEnumType | null | undefined;
	/** Gets or sets the cumulative execution metrics across all runs. */
	metrics?: TaskMetricsSortInput | null | undefined;
	/** Gets or sets the human-readable title of the task. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the ID of the project this task belongs to.
	 * Null when the task targets environments directly (see EnvironmentIds).
	 */
	projectId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the agent's latest output: the final artifact upon completion, or — while
	 * awaiting input — the question it's paused on, until a later push overwrites it.
	 */
	result?: SortEnumType | null | undefined;
	/** Gets or sets the current lifecycle status of the task. */
	status?: SortEnumType | null | undefined;
	/**
	 * Context for the current Status: when Status is
	 * InputRequired and a user has responded, this holds the ID of the
	 * WorklogEntry carrying that response, so dispatch can replay it. Starts as the
	 * literal string "task created". Exposed on the GraphQL API as a diagnostic pointer — it is an
	 * entry ID or a sentinel string, not human-readable prose; the worklog remains the public
	 * activity record.
	 */
	statusReason?: SortEnumType | null | undefined;
	/** Gets or sets the AI-generated summary of the task. */
	summary?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the agent's self-verification criteria: a description of what the agent
	 * will check to confirm the task succeeded. May be set by the user at creation time or
	 * by the agent when it accepts the task. Null when no verification is defined.
	 */
	verificationCriteria?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the agent's notes from the verification run: what passed, what failed,
	 * and any relevant observations. Null when no verification has been run.
	 */
	verificationNotes?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the outcome of the agent's verification step.
	 * Null until the agent explicitly reports a verification result.
	 * Orthogonal to Status — a task can be Completed with VerificationFailed.
	 */
	verificationStatus?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the ID of the parent workflow.
	 * Null for standalone tasks. Set by WorkflowService and never changed.
	 */
	workflowId?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * A reusable task definition. Holds exactly the fields needed to pre-fill a new task and nothing
 * about execution: a template has no status, no worklog and no metrics, and is never dispatched.
 * Tasks created from a template carry no reference back to it — the relationship is not persisted.
 */
export type ArtemisTaskTemplateFilterInput = {
	and?: Array<ArtemisTaskTemplateFilterInput> | null | undefined;
	/** Gets or sets the default assignee for tasks created from this template. */
	assigneeId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the kind of the default assignee. */
	assigneeType?: NullableOfAssigneeTypeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the targeted deployment IDs. Empty list means all deployments. */
	deploymentIds?: ListStringOperationFilterInput | null | undefined;
	/** Gets or sets the description of the work, which becomes the created task's description. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the directly targeted environment IDs. */
	environmentIds?: ListStringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the instructions passed to the agent. */
	instructions?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the template name. Also used as the default name of tasks created from it. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisTaskTemplateFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the values a user fills in when turning this template into a task. Each one is
	 * referenced as a {{key}} token in the template's text fields (name, description,
	 * instructions, verification criteria) and substituted client-side before the task is created.
	 */
	placeholders?:
		| ListFilterInputTypeOfTaskTemplatePlaceholderFilterInput
		| null
		| undefined;
	/**
	 * Gets or sets the project tasks created from this template target.
	 * Null when the template targets environments directly (see EnvironmentIds).
	 */
	projectId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the template was last modified. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the criteria describing when the work is considered done. */
	verificationCriteria?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * A reusable task definition. Holds exactly the fields needed to pre-fill a new task and nothing
 * about execution: a template has no status, no worklog and no metrics, and is never dispatched.
 * Tasks created from a template carry no reference back to it — the relationship is not persisted.
 */
export type ArtemisTaskTemplateSortInput = {
	/** Gets or sets the default assignee for tasks created from this template. */
	assigneeId?: SortEnumType | null | undefined;
	/** Gets or sets the kind of the default assignee. */
	assigneeType?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the description of the work, which becomes the created task's description. */
	description?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the instructions passed to the agent. */
	instructions?: SortEnumType | null | undefined;
	/** Gets or sets the template name. Also used as the default name of tasks created from it. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the project tasks created from this template target.
	 * Null when the template targets environments directly (see EnvironmentIds).
	 */
	projectId?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the template was last modified. */
	updatedAt?: SortEnumType | null | undefined;
	/** Gets or sets the criteria describing when the work is considered done. */
	verificationCriteria?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * Represents a platform user with a stable internal identity.
 * Created or updated by UserService on first resolution of a provider token.
 */
export type ArtemisUserFilterInput = {
	and?: Array<ArtemisUserFilterInput> | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the UTC timestamp at which the user was disabled (e.g. left the company), or null
	 * when active. Disabled users don't count toward the organization's active-user quota.
	 */
	disabledAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable display name from the identity provider token. */
	displayName?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the primary email address from the identity provider token. */
	email?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the UTC timestamp of the user's last authenticated request. Used for active-user counting. Not exposed via GraphQL. */
	lastActivityAt?: DateTimeOperationFilterInput | null | undefined;
	or?: Array<ArtemisUserFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the identifier of the external identity provider (e.g. "cognito"). */
	provider?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the provider-issued subject identifier (sub claim). Unique within a provider. */
	subject?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the UTC timestamp of the last successful identity resolution. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
};

/**
 * Represents a platform user with a stable internal identity.
 * Created or updated by UserService on first resolution of a provider token.
 */
export type ArtemisUserSortInput = {
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the UTC timestamp at which the user was disabled (e.g. left the company), or null
	 * when active. Disabled users don't count toward the organization's active-user quota.
	 */
	disabledAt?: SortEnumType | null | undefined;
	/** Gets or sets the human-readable display name from the identity provider token. */
	displayName?: SortEnumType | null | undefined;
	/** Gets or sets the primary email address from the identity provider token. */
	email?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the UTC timestamp of the user's last authenticated request. Used for active-user counting. Not exposed via GraphQL. */
	lastActivityAt?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets the identifier of the external identity provider (e.g. "cognito"). */
	provider?: SortEnumType | null | undefined;
	/** Gets or sets the provider-issued subject identifier (sub claim). Unique within a provider. */
	subject?: SortEnumType | null | undefined;
	/** Gets or sets the UTC timestamp of the last successful identity resolution. */
	updatedAt?: SortEnumType | null | undefined;
};

/**
 * One invocation of a webhook that got past authentication. Doubles as the idempotency record: the
 * pair (WebhookId, IdempotencyKey) is unique, so a repeated delivery is
 * discarded on every API replica. A failed delivery keeps no key, so the caller may retry it.
 */
export type ArtemisWebhookDeliveryFilterInput = {
	and?: Array<ArtemisWebhookDeliveryFilterInput> | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the resolved deployment, if any. */
	deploymentId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets why the delivery failed, or why a created task could not be dispatched. */
	error?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the idempotency key: the caller's Idempotency-Key header, the SNS message
	 * id, or sha256: plus the body hash. Null on a failed delivery.
	 */
	idempotencyKey?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisWebhookDeliveryFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the caller's payload as text (the SNS Message for SNS notifications). */
	payload?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the resolved project, if any. */
	projectId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets when the invocation was received. */
	receivedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the SHA-256 (hex) of the raw request body. */
	requestHash?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the caller's IP address. */
	sourceIp?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the outcome. */
	status?: WebhookDeliveryStatusOperationFilterInput | null | undefined;
	/** Gets or sets the task the delivery created (CreateTask only). */
	taskId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the caller's User-Agent header. */
	userAgent?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the webhook that was invoked. */
	webhookId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * One invocation of a webhook that got past authentication. Doubles as the idempotency record: the
 * pair (WebhookId, IdempotencyKey) is unique, so a repeated delivery is
 * discarded on every API replica. A failed delivery keeps no key, so the caller may retry it.
 */
export type ArtemisWebhookDeliverySortInput = {
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the resolved deployment, if any. */
	deploymentId?: SortEnumType | null | undefined;
	/** Gets or sets why the delivery failed, or why a created task could not be dispatched. */
	error?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the idempotency key: the caller's Idempotency-Key header, the SNS message
	 * id, or sha256: plus the body hash. Null on a failed delivery.
	 */
	idempotencyKey?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets the caller's payload as text (the SNS Message for SNS notifications). */
	payload?: SortEnumType | null | undefined;
	/** Gets or sets the resolved project, if any. */
	projectId?: SortEnumType | null | undefined;
	/** Gets or sets when the invocation was received. */
	receivedAt?: SortEnumType | null | undefined;
	/** Gets or sets the SHA-256 (hex) of the raw request body. */
	requestHash?: SortEnumType | null | undefined;
	/** Gets or sets the caller's IP address. */
	sourceIp?: SortEnumType | null | undefined;
	/** Gets or sets the outcome. */
	status?: SortEnumType | null | undefined;
	/** Gets or sets the task the delivery created (CreateTask only). */
	taskId?: SortEnumType | null | undefined;
	/** Gets or sets the caller's User-Agent header. */
	userAgent?: SortEnumType | null | undefined;
	/** Gets or sets the webhook that was invoked. */
	webhookId?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/**
 * A workspace-scoped inbound endpoint an external system (an alarm, a pipeline) calls with a secret
 * to create and dispatch a task, or to run deployment discovery (ARCH-035, ADR-064). The organization
 * and workspace of every invocation come from this record, never from the request.
 */
export type ArtemisWebhookFilterInput = {
	/** Gets or sets what an invocation does. Immutable after creation. */
	action?: WebhookActionOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the default agent for CreateTask; null otherwise. A
	 * caller may name another agent of the same workspace per call (agentId); a call that
	 * names none and has no default is refused. Holding the secret therefore means being able to
	 * run any agent of the workspace.
	 */
	agentId?: StringOperationFilterInput | null | undefined;
	and?: Array<ArtemisWebhookFilterInput> | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the user who created the webhook. */
	createdBy?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the deployment used when the caller names neither a project nor a deployment. */
	deploymentId?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the description. For CreateTask it is required and
	 * becomes each task's description, which is what the assigned agent receives as its message.
	 */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the default instructions for created tasks, used when the caller sends no
	 * task.instructions. Whichever applies is placed ahead of the fenced payload.
	 */
	instructions?: StringOperationFilterInput | null | undefined;
	/** Gets or sets when the webhook last accepted an invocation. Advisory only. */
	lastInvokedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the human-readable name. Also the name of every task the webhook creates. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisWebhookFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the project used when the caller names neither a project nor a deployment. */
	projectId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets when the current secret was issued. */
	secretRotatedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the lifecycle status. */
	status?: WebhookStatusOperationFilterInput | null | undefined;
	/** Gets or sets the last-updated timestamp. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/**
 * A workspace-scoped inbound endpoint an external system (an alarm, a pipeline) calls with a secret
 * to create and dispatch a task, or to run deployment discovery (ARCH-035, ADR-064). The organization
 * and workspace of every invocation come from this record, never from the request.
 */
export type ArtemisWebhookSortInput = {
	/** Gets or sets what an invocation does. Immutable after creation. */
	action?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the default agent for CreateTask; null otherwise. A
	 * caller may name another agent of the same workspace per call (agentId); a call that
	 * names none and has no default is refused. Holding the secret therefore means being able to
	 * run any agent of the workspace.
	 */
	agentId?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the user who created the webhook. */
	createdBy?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the deployment used when the caller names neither a project nor a deployment. */
	deploymentId?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the description. For CreateTask it is required and
	 * becomes each task's description, which is what the assigned agent receives as its message.
	 */
	description?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/**
	 * Gets or sets the default instructions for created tasks, used when the caller sends no
	 * task.instructions. Whichever applies is placed ahead of the fenced payload.
	 */
	instructions?: SortEnumType | null | undefined;
	/** Gets or sets when the webhook last accepted an invocation. Advisory only. */
	lastInvokedAt?: SortEnumType | null | undefined;
	/** Gets or sets the human-readable name. Also the name of every task the webhook creates. */
	name?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets the project used when the caller names neither a project nor a deployment. */
	projectId?: SortEnumType | null | undefined;
	/** Gets or sets when the current secret was issued. */
	secretRotatedAt?: SortEnumType | null | undefined;
	/** Gets or sets the lifecycle status. */
	status?: SortEnumType | null | undefined;
	/** Gets or sets the last-updated timestamp. */
	updatedAt?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/** Represents a user's membership in a workspace, including their assigned role. */
export type ArtemisWorkspaceMemberFilterInput = {
	and?: Array<ArtemisWorkspaceMemberFilterInput> | null | undefined;
	/** Gets or sets the date and time the entity was created. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the entity ID. */
	id?: StringOperationFilterInput | null | undefined;
	or?: Array<ArtemisWorkspaceMemberFilterInput> | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: StringOperationFilterInput | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the role the user holds within the workspace. */
	role?: WorkspaceRoleOperationFilterInput | null | undefined;
	/** Gets or sets the ID of the user who is a member of the workspace. */
	userId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: StringOperationFilterInput | null | undefined;
};

/** Represents a user's membership in a workspace, including their assigned role. */
export type ArtemisWorkspaceMemberSortInput = {
	/** Gets or sets the date and time the entity was created. */
	createdAt?: SortEnumType | null | undefined;
	/** Gets or sets the date and time the entity was deleted. */
	deletedAt?: SortEnumType | null | undefined;
	/** Gets or sets the entity ID. */
	id?: SortEnumType | null | undefined;
	/** Gets or sets the organization ID of the entity. */
	organizationId?: SortEnumType | null | undefined;
	/** Gets the particle type of the entity. */
	particle?: SortEnumType | null | undefined;
	/** Gets or sets the role the user holds within the workspace. */
	role?: SortEnumType | null | undefined;
	/** Gets or sets the ID of the user who is a member of the workspace. */
	userId?: SortEnumType | null | undefined;
	/** Gets or sets the workspace ID of the entity. */
	workspaceId?: SortEnumType | null | undefined;
};

/** Classifies the type of content stored in an ArtemisAsset. */
export type AssetCategory =
	/** A compiled or opaque binary file. */
	| "BINARY"
	/** A text or markdown document. Carries a summary field. */
	| "DOCUMENT"
	/** Any other file type not covered by Document or Binary. */
	| "GENERIC"
	/** A platform-generated per-run task transcript (raw intermediate agent output). Appended last so existing int values stay stable. */
	| "TRANSCRIPT";

export type AssetCategoryOperationFilterInput = {
	eq?: AssetCategory | null | undefined;
	in?: Array<AssetCategory> | null | undefined;
	neq?: AssetCategory | null | undefined;
	nin?: Array<AssetCategory> | null | undefined;
};

/** Represents the lifecycle state of an ArtemisAsset (ARCH-003). */
export type AssetState =
	/** Upload confirmed; object exists in the live storage path. */
	| "ACTIVE"
	/** Confirmation attempted but the staging object was not found. */
	| "FAILED"
	/** Registered; upload URL issued; upload has not been confirmed. */
	| "PENDING";

export type AssetStateOperationFilterInput = {
	eq?: AssetState | null | undefined;
	in?: Array<AssetState> | null | undefined;
	neq?: AssetState | null | undefined;
	nin?: Array<AssetState> | null | undefined;
};

/**
 * Identifies whether a task's assignee is an agent (dispatched via A2A) or a human user
 * (completed manually via UI or CLI in user mode).
 */
export type AssigneeType =
	/** Task is assigned to an agent worker and will be dispatched via A2A. */
	| "AGENT"
	/** Task is assigned to a human user for manual action; dispatch is not allowed. */
	| "USER";

/** The kind of action recorded in the audit trail. */
export type AuditAction =
	/** An entity was created. */
	| "CREATED"
	/** An entity was soft-deleted (hidden, retained for the grace window). */
	| "DELETED"
	/** A soft-deleted entity (or a batch) was physically purged after its retention window. */
	| "PURGED"
	/** A soft-deleted entity was restored. */
	| "RESTORED"
	/** An entity's fields were modified. */
	| "UPDATED";

export type AuditActionOperationFilterInput = {
	eq?: AuditAction | null | undefined;
	in?: Array<AuditAction> | null | undefined;
	neq?: AuditAction | null | undefined;
	nin?: Array<AuditAction> | null | undefined;
};

/**
 * The highest ApprovalRiskLevel the platform may approve without a human, per
 * environment (REQ-017, REQ-018). A single ordered threshold: an action auto-approves when its
 * assessed risk is at or below this level, and waits for a human otherwise.
 */
export type AutoApprovalThreshold =
	/** Autonomous. Every action runs unattended, including destructive ones. */
	| "HIGH"
	/** Supervised. Low- and medium-risk actions run unattended; anything higher waits. */
	| "MEDIUM"
	/** Human-gated. Every agent action waits for a human. */
	| "NONE";

export type AutoApprovalThresholdOperationFilterInput = {
	eq?: AutoApprovalThreshold | null | undefined;
	in?: Array<AutoApprovalThreshold> | null | undefined;
	neq?: AutoApprovalThreshold | null | undefined;
	nin?: Array<AutoApprovalThreshold> | null | undefined;
};

/**
 * Provider-specific deployment configuration for AWS environments.
 * Stored as a JSONB column on ArtemisDeployment.
 */
export type AwsDeploymentConfigFilterInput = {
	and?: Array<AwsDeploymentConfigFilterInput> | null | undefined;
	or?: Array<AwsDeploymentConfigFilterInput> | null | undefined;
	/**
	 * Gets or sets the AWS region for this deployment.
	 * When null, the environment's DefaultRegion metadata value is used.
	 */
	region?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the CloudFormation stack names that make up this deployment. */
	stackNames?: ListStringOperationFilterInput | null | undefined;
};

/**
 * Provider-specific deployment configuration for AWS environments.
 * Stored as a JSONB column on ArtemisDeployment.
 */
export type AwsDeploymentConfigInput = {
	/**
	 * Gets or sets the AWS region for this deployment.
	 * When null, the environment's DefaultRegion metadata value is used.
	 */
	region?: string | null | undefined;
	/** Gets or sets the CloudFormation stack names that make up this deployment. */
	stackNames: Array<string>;
};

/**
 * How much a task would touch, as one word a person can read at a glance while typing it.
 * This is a different axis from . A level says how worried to
 * be; a band says what gets touched. A production read is Low risk and ReadOnly blast radius; an
 * email to every customer is trivial to perform and Destructive, because it cannot be unsent.
 */
export type BlastRadius =
	/** Removes something, or leaves the organisation. There is no way back. */
	| "DESTRUCTIVE"
	/** Could interrupt something running, or loosen a control around it. */
	| "DISRUPTIVE"
	/** Observes only. Nothing changes anywhere. */
	| "READ_ONLY"
	/** Changes state, but nothing that cannot be put back. */
	| "REVERSIBLE"
	/** The description does not say enough to tell. The honest answer, and a prompt to write more. */
	| "UNCLEAR";

export type BooleanOperationFilterInput = {
	eq?: boolean | null | undefined;
	neq?: boolean | null | undefined;
};

/**
 * What kind of principal a request authenticated as. Distinct from AssigneeType: an
 * App is a caller but never a task assignee.
 */
export type CallerKind =
	/** A workspace-scoped agent (platform-minted agent token, ADR-038). */
	| "AGENT"
	/** An organization-level app such as Port (platform-minted token, ADR-061). */
	| "APP"
	/** A human user (Cognito token). */
	| "USER";

/** What happened when a linked repository's catalog file was read. */
export type CatalogFileStatus =
	/** A catalog file was found and parsed. */
	| "FOUND"
	/** A catalog file exists but is not valid YAML, or is too large to read. */
	| "INVALID"
	/** The repository has no catalog file at any of the conventional paths. */
	| "MISSING"
	/** The git provider could not be reached or refused the request; retrying may work. */
	| "UNAVAILABLE";

/** How one catalog entity relates to another, following Backstage's own relation names. */
export type CatalogRelationType =
	/** The source component calls the target API (spec.consumesApis). */
	| "CONSUMES_API"
	/** The source needs the target to run (spec.dependsOn, or the target's spec.dependencyOf). */
	| "DEPENDS_ON"
	/**
	 * The source belongs to the target: a system, domain, parent component or parent domain
	 *             (spec.system, spec.domain, spec.subcomponentOf, spec.subdomainOf).
	 */
	| "PART_OF"
	/** The source component exposes the target API (spec.providesApis). */
	| "PROVIDES_API";

/** Input for claiming or joining an organization. */
export type ClaimOrganizationInput = {
	name: string;
	slug: string;
};

/** Outcome of simulating a requested action set against the ceiling role's IAM policies. */
export type CoverageStatus =
	/** Every requested action is permitted by the ceiling role. */
	| "COVERED"
	/** At least one requested action is not permitted by the ceiling role. */
	| "UNCOVERED"
	/**
	 * Coverage could not be determined (e.g. the customer stack predates the
	 *             iam:SimulatePrincipalPolicy grant, or AWS was transiently unavailable).
	 *             Does not reject the request: the escalation is still filed and still reaches a human.
	 *             It does block auto-approval, because a grant the platform could not verify is
	 *             precisely the one a person should look at. See the ADR superseding ADR-042's fail-open.
	 */
	| "UNKNOWN";

/** Input for creating a new agent. */
export type CreateAgentInput = {
	/**
	 * Optional expiry for a External agent's
	 *             client secret (ADR-050). null means the secret never expires. Ignored for Managed agents,
	 *             whose credential rotates on redeploy.
	 */
	clientSecretExpiresAt?: unknown;
	/**
	 * Provider-specific deploy settings chosen at deploy time (e.g.
	 *             AWS Region). Generic key-value pairs, mirroring environment metadata.
	 */
	deploymentSettings?: Array<MetadataKvInput> | null | undefined;
	/**
	 * The environment the agent runs in. The association is optional: null means the agent is
	 * not running on any tracked environment — for External agents that is the
	 * normal case (a local machine, a remote A2A host, or anywhere else outside platform-managed
	 * infrastructure, ARCH-030). For Managed agents it is the deployment
	 * target and currently required (hosted deployment is disabled).
	 */
	environmentId?: string | null | undefined;
	/** Optional caller-supplied key. Repeated calls with the same key return the cached result. */
	idempotencyKey?: string | null | undefined;
	/**
	 * How the platform relates to the agent's runtime (ADR-050). null means
	 *             Managed (deploy and push, today's default). External
	 *             registers an identity only: no runtime is provisioned and the credential is returned once.
	 */
	kind?: AgentKind | null | undefined;
	/** The agent name. */
	name: string;
	/** The workspace this agent belongs to. */
	workspaceId: string;
};

/** Input for creating a new deployment. */
export type CreateDeploymentInput = {
	/** AWS-specific configuration. Required when the target environment is an AWS environment. */
	awsConfig?: AwsDeploymentConfigInput | null | undefined;
	/** Optional description. */
	description?: string | null | undefined;
	/** The environment this deployment targets. */
	environmentId: string;
	/**
	 * True when this deployment's graph will come from supplied Terraform files rather than from
	 * provider discovery. Recorded up front so the deployment is identifiable as Terraform-backed
	 * before any analysis has succeeded — otherwise a failed or not-yet-run analysis is
	 * indistinguishable from a CloudFormation deployment, and callers are offered a "Discover"
	 * action that cannot work for it.
	 */
	isTerraform: boolean;
	/** Human-readable name for the deployment. */
	name: string;
	/** The project this deployment belongs to. */
	projectId: string;
};

/** Input for registering a new environment. */
export type CreateEnvironmentInput = {
	/** Whether agents may access (operate on) this environment. */
	allowAgentsToAccess: boolean;
	/** Whether agents may be deployed into this environment. */
	allowAgentsToBeDeployed: boolean;
	/** Highest risk level auto-approved here. Defaults to Medium (Supervised). */
	autoApproveUpTo?: AutoApprovalThreshold | null | undefined;
	/** The environment classification. */
	classification?: EnvironmentClassification | null | undefined;
	/** Optional caller-supplied key. Repeated calls with the same key return the cached result. */
	idempotencyKey?: string | null | undefined;
	/** Optional provider-specific metadata key-value pairs (e.g. AccountId, DefaultRegion). */
	metadata?: Array<MetadataKvInput> | null | undefined;
	/** Human-readable name. Must be unique within the workspace. */
	name: string;
	/** The cloud provider. */
	provider: EnvironmentProvider;
	/** The workspace this environment belongs to. */
	workspaceId: string;
};

/** Input for creating a new job. */
export type CreateJobInput = {
	agentId: string;
	deploymentIds?: Array<string> | null | undefined;
	description: string;
	environmentIds?: Array<string> | null | undefined;
	instructions?: string | null | undefined;
	name: string;
	projectId?: string | null | undefined;
	schedule: string;
	timezone?: string | null | undefined;
	workspaceId: string;
};

/** Input for creating a new project. */
export type CreateProjectInput = {
	/** The project description. */
	description: string;
	/** Optional caller-supplied key. Repeated calls with the same key return the cached result. */
	idempotencyKey?: string | null | undefined;
	/** The project name. Must be unique within the workspace. */
	name: string;
	/** The type of the project */
	type?: ProjectType | null | undefined;
	/** The workspace this project belongs to. */
	workspaceId: string;
};

/** Input for creating a new report. */
export type CreateReportInput = {
	/** Optional initial body content. */
	body?: string | null | undefined;
	/** Optional key for at-most-once creation. */
	idempotencyKey?: string | null | undefined;
	/** Owner user ID; defaults to the calling user when null. */
	ownerId?: string | null | undefined;
	/** The human-readable title. */
	title: string;
	/** The report type; defaults to Generic. */
	type: ReportType;
	/** The workspace that owns the report. */
	workspaceId: string;
};

/** Input for creating, or upserting, a signal. */
export type CreateSignalInput = {
	/** Source-specific settings; see the source's configFields. */
	config?: Array<MetadataKvInput> | null | undefined;
	/** The deployment to scope the signal to, or null for a project-level signal. */
	deploymentId?: string | null | undefined;
	/** An optional description. */
	description?: string | null | undefined;
	/**
	 * The environment to read through. Taken from the deployment when one is given; required for a
	 * project-level signal whose source reads through an environment.
	 */
	environmentId?: string | null | undefined;
	/**
	 * The source's id for what to watch. For CloudWatch alarms, an alarm ARN or a bare alarm name (taken
	 * to be in the environment's default region). May be omitted for sources identified by their
	 * config (a cost filter, a metric).
	 */
	externalId?: string | null | undefined;
	/** The display name, or null to use the source's own name. */
	name?: string | null | undefined;
	/** The project the signal belongs to. */
	projectId: string;
	/** Where the signal's status is read from. */
	source: SignalSource;
	/** For a metric source, the levels to judge it against; null for none. */
	thresholds?: SignalThresholdsInput | null | undefined;
};

/** Input for creating a new task. */
export type CreateTaskInput = {
	/** The agent ID. */
	assigneeId?: string | null | undefined;
	/** Whether the assignee is an agent or a human user. Null when no assignee is set. */
	assigneeType?: AssigneeType | null | undefined;
	/**
	 * IDs of the project deployments this task is meant to work on. Optional but recommended —
	 * the assigned agent resolves each deployment's environment via its own pre-start hook chain.
	 */
	deploymentIds?: Array<string> | null | undefined;
	/** The task description. */
	description: string;
	/**
	 * IDs of the environments this task targets directly. Optional — lets the assigned agent operate
	 * on an environment even when the task's project has no deployments tying it to that environment.
	 */
	environmentIds?: Array<string> | null | undefined;
	/** Optional caller-supplied key. Repeated calls with the same key return the cached result. */
	idempotencyKey?: string | null | undefined;
	/** The task instructions. */
	instructions?: string | null | undefined;
	/** The job run ID that created this task. Null for tasks not created by a job. */
	jobRunId?: string | null | undefined;
	/** The task name. */
	name: string;
	/** The project ID. Null for a task that targets environments directly. */
	projectId?: string | null | undefined;
	/** Optional description of what the agent will check to confirm the task succeeded. */
	verificationCriteria?: string | null | undefined;
	/** Optional notes from the verification run. */
	verificationNotes?: string | null | undefined;
	/** Initial verification status. Null when no verification is defined. */
	verificationStatus?: VerificationStatus | null | undefined;
	/** The workspace ID. */
	workspaceId: string;
};

/** Input for creating a new task template. */
export type CreateTaskTemplateInput = {
	/** The default assignee. */
	assigneeId?: string | null | undefined;
	/** The kind of the default assignee. */
	assigneeType?: AssigneeType | null | undefined;
	/** The targeted deployment IDs. */
	deploymentIds?: Array<string> | null | undefined;
	/** The description of the work. */
	description: string;
	/** The directly targeted environment IDs. */
	environmentIds?: Array<string> | null | undefined;
	/** Instructions passed to the agent. */
	instructions?: string | null | undefined;
	/** The template name, also used as the default name of tasks created from it. */
	name: string;
	/** The values asked for when a task is created from the template, referenced as {{key}} tokens. */
	placeholders?: Array<TaskTemplatePlaceholderInput> | null | undefined;
	/** The targeted project, or null when targeting environments directly. */
	projectId?: string | null | undefined;
	/** The criteria describing when the work is done. */
	verificationCriteria?: string | null | undefined;
	/** The workspace this template belongs to. */
	workspaceId: string;
};

/**
 * Input for creating a webhook. One webhook serves many callers: everything but the workspace and
 * the action is a default a caller may replace per call (ids in the query string, task fields in
 * the body's task object).
 */
export type CreateWebhookInput = {
	/** What an invocation does. Immutable. */
	action: WebhookAction;
	/** Default agent for CreateTask; a caller that names none needs it set. Not allowed otherwise. */
	agentId?: string | null | undefined;
	/** Default deployment, used when the caller names neither a project nor a deployment. */
	deploymentId?: string | null | undefined;
	/** Default task description for CreateTask; a caller that sends none needs it set. */
	description: string;
	/** Default agent instructions for CreateTask, ahead of the caller's payload. */
	instructions?: string | null | undefined;
	/** Name; also the default name of the tasks the webhook creates. */
	name: string;
	/** Default project, used when the caller names neither a project nor a deployment. */
	projectId?: string | null | undefined;
	/** The workspace the webhook belongs to. */
	workspaceId: string;
};

/** Input for creating a new workspace. */
export type CreateWorkspaceInput = {
	/** The workspace name. Must be unique within the organization. */
	name: string;
};

/**
 * One requested IAM grant: actions scoped to the resources they apply to. Keeping actions
 *             and resources together per statement lets an agent pin a broad action to specific ARNs while
 *             requesting account-level reads on *, instead of one cross-product over all resources.
 */
export type CredentialStatementInput = {
	/** The requested IAM actions (e.g. s3:PutObject). */
	actions: Array<string>;
	/** The resource ARNs these actions apply to; empty means *. */
	resources: Array<string>;
};

export type DateTimeOperationFilterInput = {
	eq?: unknown;
	gt?: unknown;
	gte?: unknown;
	in?: Array<unknown> | null | undefined;
	lt?: unknown;
	lte?: unknown;
	neq?: unknown;
	ngt?: unknown;
	ngte?: unknown;
	nin?: Array<unknown> | null | undefined;
	nlt?: unknown;
	nlte?: unknown;
};

/**
 * Defines the classification of an environment. Its only role is to inform risk classification:
 * the risk evaluator escalates one level for a production target. It is deliberately NOT a term
 * in the auto-approval decision — that would count production twice (see
 * ApprovalService.ShouldAutoApprove).
 */
export type EnvironmentClassification =
	/** Any non-production environment (development, staging, testing). */
	| "NON_PRODUCTION"
	/** Production environment. */
	| "PRODUCTION";

export type EnvironmentClassificationOperationFilterInput = {
	eq?: EnvironmentClassification | null | undefined;
	in?: Array<EnvironmentClassification> | null | undefined;
	neq?: EnvironmentClassification | null | undefined;
	nin?: Array<EnvironmentClassification> | null | undefined;
};

/** Identifies the provider for an Environment. */
export type EnvironmentProvider =
	/** Amazon Web Services. */
	| "AWS"
	/** Microsoft Azure. */
	| "AZURE"
	/** Cloudflare. */
	| "CLOUDFLARE"
	/** Google Cloud Platform. */
	| "GOOGLE_CLOUD"
	/** Other. */
	| "OTHER"
	/** Vercel. */
	| "VERCEL";

export type EnvironmentProviderOperationFilterInput = {
	eq?: EnvironmentProvider | null | undefined;
	in?: Array<EnvironmentProvider> | null | undefined;
	neq?: EnvironmentProvider | null | undefined;
	nin?: Array<EnvironmentProvider> | null | undefined;
};

/**
 * Wizard selections that tailor the onboarding CloudFormation template. Everything is
 *             baked into the returned template at serve time, so the delivered file has no stack parameters:
 *             unselected capabilities are omitted, the external id and role names are literal, and the operator
 *             action sets are injected.
 */
export type EnvironmentSetupTemplateInput = {
	/** Name for the always-present connection role. */
	connectionRoleName: string;
	/** Name for the deployment role (required when deployment enabled). */
	deploymentRoleName?: string | null | undefined;
	/** Include the operations + operations-ceiling roles. */
	enableAgentAccess: boolean;
	/** Include the runtime + deployment roles. */
	enableAgentDeployment: boolean;
	/** Confused-deputy guard baked into every StratoNext role's trust policy. */
	externalId: string;
	/** IAM actions the operations role may execute (required when access enabled). */
	operationsActions?: Array<string> | null | undefined;
	/** Ceiling IAM actions an approval can escalate to (required when access enabled). */
	operationsMaxActions?: Array<string> | null | undefined;
	/** Name for the ceiling role (required when access enabled). */
	operationsMaxRoleName?: string | null | undefined;
	/** Name for the operations role (required when access enabled). */
	operationsRoleName?: string | null | undefined;
	/** Name for the runtime role (required when deployment enabled). */
	runtimeRoleName?: string | null | undefined;
};

/** Represents the lifecycle status of an ArtemisEnvironment. */
export type EnvironmentStatus =
	/** Environment is active and accepts task execution. */
	| "ACTIVE"
	/** Environment is disabled; no new task executions are accepted. */
	| "DISABLED"
	/** Environment failed; no new task executions are accepted. */
	| "FAILED";

export type EnvironmentStatusOperationFilterInput = {
	eq?: EnvironmentStatus | null | undefined;
	in?: Array<EnvironmentStatus> | null | undefined;
	neq?: EnvironmentStatus | null | undefined;
	nin?: Array<EnvironmentStatus> | null | undefined;
};

export type FloatOperationFilterInput = {
	eq?: number | null | undefined;
	gt?: number | null | undefined;
	gte?: number | null | undefined;
	in?: Array<number | null | undefined> | null | undefined;
	lt?: number | null | undefined;
	lte?: number | null | undefined;
	neq?: number | null | undefined;
	ngt?: number | null | undefined;
	ngte?: number | null | undefined;
	nin?: Array<number | null | undefined> | null | undefined;
	nlt?: number | null | undefined;
	nlte?: number | null | undefined;
};

/** Where an installation the caller can access on GitHub stands in StratoNext. */
export type GitProviderInstallationStatus =
	/** Not connected to any organization yet. */
	| "AVAILABLE"
	/** The caller's organization's current connection. */
	| "CONNECTED_IN_ORGANIZATION"
	/** Connected to a different organization; cannot be connected here (ADR-062: one organization per installation). */
	| "CONNECTED_TO_ANOTHER_ORGANIZATION";

/**
 * Identifies the external git provider an ArtemisGitProviderConnection talks to.
 * GitHub is the only implementation today (REQ-090); adding GitLab/Bitbucket later means a new
 * enum member plus a new IGitProviderConnector implementation, never a change to GitHub's.
 */
export type GitProviderType =
	/** GitHub, via a StratoNext-owned GitHub App installation. */
	"GIT_HUB";

/** Represents a node in the semantic graph. */
export type GraphNodeFilterInput = {
	and?: Array<GraphNodeFilterInput> | null | undefined;
	/** Gets or sets the unique identifier of the node. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the label of the node. */
	label?: StringOperationFilterInput | null | undefined;
	or?: Array<GraphNodeFilterInput> | null | undefined;
	/** Gets or sets the properties of the node. */
	properties?:
		| ListFilterInputTypeOfKeyValuePairOfStringAndStringFilterInput
		| null
		| undefined;
	/** Gets or sets the name of the provider associated with the node. */
	providerName?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the unique identifier of the resource associated with the node. */
	resourceId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the name of the service associated with the node. */
	serviceName?: StringOperationFilterInput | null | undefined;
};

/** Represents a relationship between two nodes in the semantic graph. */
export type GraphRelationshipFilterInput = {
	and?: Array<GraphRelationshipFilterInput> | null | undefined;
	/** Gets or sets the unique identifier of the relationship. */
	id?: StringOperationFilterInput | null | undefined;
	or?: Array<GraphRelationshipFilterInput> | null | undefined;
	/** Gets or sets the properties of the relationship. */
	properties?:
		| ListFilterInputTypeOfKeyValuePairOfStringAndStringFilterInput
		| null
		| undefined;
	/** Gets or sets the unique identifier of the source node. */
	sourceNodeId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the unique identifier of the target node. */
	targetNodeId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the type of the relationship. */
	type?: StringOperationFilterInput | null | undefined;
};

/** Represents the type of graph. */
export type GraphType =
	/** A cloud application graph. */
	"CLOUD_APPLICATION";

export type GraphTypeOperationFilterInput = {
	eq?: GraphType | null | undefined;
	in?: Array<GraphType> | null | undefined;
	neq?: GraphType | null | undefined;
	nin?: Array<GraphType> | null | undefined;
};

export type IntOperationFilterInput = {
	eq?: number | null | undefined;
	gt?: number | null | undefined;
	gte?: number | null | undefined;
	in?: Array<number | null | undefined> | null | undefined;
	lt?: number | null | undefined;
	lte?: number | null | undefined;
	neq?: number | null | undefined;
	ngt?: number | null | undefined;
	ngte?: number | null | undefined;
	nin?: Array<number | null | undefined> | null | undefined;
	nlt?: number | null | undefined;
	nlte?: number | null | undefined;
};

/** How a job run was triggered. */
export type JobRunTriggerType =
	/** Run was manually triggered by a user. */
	| "MANUAL"
	/** Run was triggered by the external scheduler on schedule. */
	| "SCHEDULED";

export type JobRunTriggerTypeOperationFilterInput = {
	eq?: JobRunTriggerType | null | undefined;
	in?: Array<JobRunTriggerType> | null | undefined;
	neq?: JobRunTriggerType | null | undefined;
	nin?: Array<JobRunTriggerType> | null | undefined;
};

/** Lifecycle status of a job definition. */
export type JobStatus =
	/** Job is active and will fire on schedule. */
	| "ACTIVE"
	/** Job is paused; scheduled firings are suppressed. */
	| "PAUSED";

export type JobStatusOperationFilterInput = {
	eq?: JobStatus | null | undefined;
	in?: Array<JobStatus> | null | undefined;
	neq?: JobStatus | null | undefined;
	nin?: Array<JobStatus> | null | undefined;
};

export type KeyValuePairOfStringAndStringFilterInput = {
	and?: Array<KeyValuePairOfStringAndStringFilterInput> | null | undefined;
	key?: StringOperationFilterInput | null | undefined;
	or?: Array<KeyValuePairOfStringAndStringFilterInput> | null | undefined;
	value?: StringOperationFilterInput | null | undefined;
};

/** Represents the type of a ResourceLink. */
export type LinkType =
	/** Link points to the project's documentation. */
	| "DOCUMENTATION"
	/** Generic link type; no specific meaning. */
	| "GENERIC"
	/** Link points to the project's source code repository. */
	| "SOURCE_CODE"
	/** Link points to a tool or utility related to the project. */
	| "TOOL";

export type LinkTypeOperationFilterInput = {
	eq?: LinkType | null | undefined;
	in?: Array<LinkType> | null | undefined;
	neq?: LinkType | null | undefined;
	nin?: Array<LinkType> | null | undefined;
};

export type ListFilterInputTypeOfAgentSkillFilterInput = {
	all?: AgentSkillFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: AgentSkillFilterInput | null | undefined;
	some?: AgentSkillFilterInput | null | undefined;
};

export type ListFilterInputTypeOfArtemisAssetFilterInput = {
	all?: ArtemisAssetFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: ArtemisAssetFilterInput | null | undefined;
	some?: ArtemisAssetFilterInput | null | undefined;
};

export type ListFilterInputTypeOfArtemisDeploymentFilterInput = {
	all?: ArtemisDeploymentFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: ArtemisDeploymentFilterInput | null | undefined;
	some?: ArtemisDeploymentFilterInput | null | undefined;
};

export type ListFilterInputTypeOfArtemisProjectRepositoryFilterInput = {
	all?: ArtemisProjectRepositoryFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: ArtemisProjectRepositoryFilterInput | null | undefined;
	some?: ArtemisProjectRepositoryFilterInput | null | undefined;
};

export type ListFilterInputTypeOfGraphNodeFilterInput = {
	all?: GraphNodeFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: GraphNodeFilterInput | null | undefined;
	some?: GraphNodeFilterInput | null | undefined;
};

export type ListFilterInputTypeOfGraphRelationshipFilterInput = {
	all?: GraphRelationshipFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: GraphRelationshipFilterInput | null | undefined;
	some?: GraphRelationshipFilterInput | null | undefined;
};

export type ListFilterInputTypeOfKeyValuePairOfStringAndStringFilterInput = {
	all?: KeyValuePairOfStringAndStringFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: KeyValuePairOfStringAndStringFilterInput | null | undefined;
	some?: KeyValuePairOfStringAndStringFilterInput | null | undefined;
};

export type ListFilterInputTypeOfProjectLinkFilterInput = {
	all?: ProjectLinkFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: ProjectLinkFilterInput | null | undefined;
	some?: ProjectLinkFilterInput | null | undefined;
};

export type ListFilterInputTypeOfTaskLinkFilterInput = {
	all?: TaskLinkFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: TaskLinkFilterInput | null | undefined;
	some?: TaskLinkFilterInput | null | undefined;
};

export type ListFilterInputTypeOfTaskTemplatePlaceholderFilterInput = {
	all?: TaskTemplatePlaceholderFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: TaskTemplatePlaceholderFilterInput | null | undefined;
	some?: TaskTemplatePlaceholderFilterInput | null | undefined;
};

export type ListFilterInputTypeOfWorklogEntryFilterInput = {
	all?: WorklogEntryFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: WorklogEntryFilterInput | null | undefined;
	some?: WorklogEntryFilterInput | null | undefined;
};

export type ListStringOperationFilterInput = {
	all?: StringOperationFilterInput | null | undefined;
	any?: boolean | null | undefined;
	none?: StringOperationFilterInput | null | undefined;
	some?: StringOperationFilterInput | null | undefined;
};

export type LongOperationFilterInput = {
	eq?: unknown;
	gt?: unknown;
	gte?: unknown;
	in?: Array<unknown> | null | undefined;
	lt?: unknown;
	lte?: unknown;
	neq?: unknown;
	ngt?: unknown;
	ngte?: unknown;
	nin?: Array<unknown> | null | undefined;
	nlt?: unknown;
	nlte?: unknown;
};

/** A key-value pair used to supply provider-specific metadata when creating or updating an environment. */
export type MetadataKvInput = {
	/** The metadata key (e.g. AccountId, DefaultRegion). */
	key: string;
	/** The metadata value. */
	value: string;
};

export type NullableOfAgentHealthOperationFilterInput = {
	eq?: AgentHealth | null | undefined;
	in?: Array<AgentHealth | null | undefined> | null | undefined;
	neq?: AgentHealth | null | undefined;
	nin?: Array<AgentHealth | null | undefined> | null | undefined;
};

export type NullableOfAssigneeTypeOperationFilterInput = {
	eq?: AssigneeType | null | undefined;
	in?: Array<AssigneeType | null | undefined> | null | undefined;
	neq?: AssigneeType | null | undefined;
	nin?: Array<AssigneeType | null | undefined> | null | undefined;
};

export type NullableOfVerificationStatusOperationFilterInput = {
	eq?: VerificationStatus | null | undefined;
	in?: Array<VerificationStatus | null | undefined> | null | undefined;
	neq?: VerificationStatus | null | undefined;
	nin?: Array<VerificationStatus | null | undefined> | null | undefined;
};

/** Represents the type of a ArtemisEntity. */
export type ParentType =
	/** The resource is associated with a project. */
	| "PROJECT"
	/** The resource is associated with a report. */
	| "REPORT"
	/** The resource is associated with a task. */
	| "TASK";

export type ParentTypeOperationFilterInput = {
	eq?: ParentType | null | undefined;
	in?: Array<ParentType> | null | undefined;
	neq?: ParentType | null | undefined;
	nin?: Array<ParentType> | null | undefined;
};

/** Represents a link related to a project. */
export type ProjectLinkFilterInput = {
	and?: Array<ProjectLinkFilterInput> | null | undefined;
	/** Gets or sets the description of the link. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the name of the link. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<ProjectLinkFilterInput> | null | undefined;
	/** Gets or sets the ID of the project. */
	projectId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the type of the link. */
	type?: LinkTypeOperationFilterInput | null | undefined;
	/** Gets or sets the URL of the link. */
	url?: StringOperationFilterInput | null | undefined;
};

/** Represents the lifecycle status of a ArtemisProject. */
export type ProjectStatus =
	/** Project is active; tasks, workflows, and jobs may be created within it. */
	| "ACTIVE"
	/** Project is archived; read-only. No new tasks, workflows, or jobs may be created. */
	| "ARCHIVED";

export type ProjectStatusOperationFilterInput = {
	eq?: ProjectStatus | null | undefined;
	in?: Array<ProjectStatus> | null | undefined;
	neq?: ProjectStatus | null | undefined;
	nin?: Array<ProjectStatus> | null | undefined;
};

/** Represents the type of the ArtemisProject. */
export type ProjectType =
	/** Project is not related to a software application */
	| "OTHER"
	/** Project represents a software */
	| "SOFTWARE_APPLICATION";

export type ProjectTypeOperationFilterInput = {
	eq?: ProjectType | null | undefined;
	in?: Array<ProjectType> | null | undefined;
	neq?: ProjectType | null | undefined;
	nin?: Array<ProjectType> | null | undefined;
};

/** Input for registering a new asset. */
export type RegisterAssetInput = {
	/** The asset category. */
	category: AssetCategory;
	/** The asset description. */
	description: string;
	/** The original filename, used to construct the storage key. */
	filename: string;
	/** Optional caller-supplied key. Repeated calls with the same key return the cached result. */
	idempotencyKey?: string | null | undefined;
	/** The caller-supplied MIME type. */
	mediaType: string;
	/** The asset name. */
	name: string;
	/** The parent resource ID. */
	parentId: string;
	/** The parent resource type. */
	parentType: ParentType;
	/** The owning workspace. */
	workspaceId: string;
};

/** Represents the lifecycle state of a report. */
export type ReportStatus =
	/** Report is writable by the owner and agents. */
	| "OPEN"
	/** Report is immutable; only a workspace administrator may reopen it. */
	| "SEALED";

export type ReportStatusOperationFilterInput = {
	eq?: ReportStatus | null | undefined;
	in?: Array<ReportStatus> | null | undefined;
	neq?: ReportStatus | null | undefined;
	nin?: Array<ReportStatus> | null | undefined;
};

/** Classifies the intended purpose of a report. */
export type ReportType =
	/** General-purpose workspace knowledge artifact. */
	| "GENERIC"
	/** Records the response to an operational incident. */
	| "INCIDENT"
	/** Documents an active or completed investigation. */
	| "INVESTIGATION";

export type ReportTypeOperationFilterInput = {
	eq?: ReportType | null | undefined;
	in?: Array<ReportType> | null | undefined;
	neq?: ReportType | null | undefined;
	nin?: Array<ReportType> | null | undefined;
};

/** Input for requesting an approval-gated AWS permission escalation. */
export type RequestCredentialEscalationInput = {
	/** The requesting agent. */
	agentId: string;
	/** The target environment. */
	environmentId: string;
	/** A short human-readable name for the request. */
	name: string;
	/** Why the escalation is needed; shown to approvers. */
	reason: string;
	/** The requested IAM statements (actions scoped to resources). */
	statements: Array<CredentialStatementInput>;
	/** The task the agent is executing. */
	taskId: string;
	/** The workspace the approval belongs to. */
	workspaceId: string;
};

/** Input for resolving a pending approval. */
export type ResolveApprovalInput = {
	/** The approver's decision. */
	decision: ApprovalDecision;
	/** Optional notes attached by the approver. */
	notes?: string | null | undefined;
};

/** Input for a resource link. ID is generated server-side. */
export type ResourceLinkInput = {
	/** Optional description. */
	description?: string | null | undefined;
	/** The display name. */
	name?: string | null | undefined;
	/** The link type. */
	type: LinkType;
	/** The URL. */
	url?: string | null | undefined;
};

/** Represents a semantic graph consisting of nodes and relationships. */
export type SemanticGraphFilterInput = {
	and?: Array<SemanticGraphFilterInput> | null | undefined;
	/** Gets or sets the error message if the graph discovery fails. */
	discoveryError?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the unique identifier of the graph. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets a value indicating whether the graph is currently being discovered. */
	isDiscovering?: BooleanOperationFilterInput | null | undefined;
	/**
	 * Gets or sets the date and time discovery last completed successfully. Null until the first
	 * successful discovery; unlike UpdatedAt it is not touched on start or on failure.
	 */
	lastDiscoveryAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the list of nodes in the graph. */
	nodes?: ListFilterInputTypeOfGraphNodeFilterInput | null | undefined;
	or?: Array<SemanticGraphFilterInput> | null | undefined;
	/** Gets or sets the list of relationships in the graph. */
	relationships?:
		| ListFilterInputTypeOfGraphRelationshipFilterInput
		| null
		| undefined;
	/** Gets or sets the type of the graph. */
	type?: GraphTypeOperationFilterInput | null | undefined;
	/** Gets or sets the date and time the graph was last updated. */
	updatedAt?: DateTimeOperationFilterInput | null | undefined;
	/**
	 * Gets or sets a monotonically increasing counter, incremented on every discovery refresh.
	 * Lets clients tell one discovery run apart from the next.
	 */
	version?: IntOperationFilterInput | null | undefined;
};

/** Where to list signal candidates from. */
export type SignalCandidatesInput = {
	/** The environment to read through. */
	environmentId: string;
	/** The region to list, or null for the environment's default region. */
	region?: string | null | undefined;
	/** A name prefix to narrow the list. */
	search?: string | null | undefined;
	/** The source to list. */
	source: SignalSource;
};

/**
 * What a signal means to the people looking at it. The UI groups and renders signals by kind,
 * independent of where their status comes from (SignalSource).
 */
export type SignalKind =
	/** A monitored condition that is either firing or not (a CloudWatch alarm). */
	| "ALARM"
	/**
	 * A number read from the source — users in a Cognito pool, month-to-date cost, a CloudWatch
	 * metric. Healthy or not only when the signal has SignalThresholds; otherwise
	 * informational.
	 */
	| "METRIC";

/** Who created a signal. Discovery only ever reconciles the signals it created itself. */
export type SignalOrigin =
	/** An agent added it through the API or CLI. */
	| "AGENT"
	/** Deployment discovery found it in the deployment's infrastructure. */
	| "DISCOVERY"
	/** A person added it from the UI or the CLI. */
	| "MANUAL";

/**
 * Where a signal's live status is read from. Each source has exactly one resolver on the backend;
 * adding a source means adding a member here and a resolver, nothing else.
 */
export type SignalSource =
	/** An AWS account's month-to-date cost from Cost Explorer, optionally filtered by a cost allocation tag. */
	| "AWS_COST"
	/** An Amazon CloudWatch metric or composite alarm, read through an AWS environment's connection role. */
	| "CLOUD_WATCH"
	/** The latest value of any Amazon CloudWatch metric (namespace, name, dimensions, statistic). */
	| "CLOUD_WATCH_METRIC"
	/** The estimated number of users in an Amazon Cognito user pool. */
	| "COGNITO_USERS";

/** A signal's normalized, source-agnostic state at the moment it was read. */
export type SignalState =
	/** The source reports the signal as firing (an alarm in ALARM), or a metric crossed its critical threshold. */
	| "FIRING"
	/** The source has too little data to decide (CloudWatch INSUFFICIENT_DATA). */
	| "NO_DATA"
	/** The source reports the signal as healthy. */
	| "OK"
	/** The status could not be read; Error says why. */
	| "UNKNOWN"
	/** A metric crossed its warning threshold but not its critical one. */
	| "WARNING";

/** A metric signal's warning and critical levels. Both null removes them. */
export type SignalThresholdsInput = {
	/** The level that makes the signal FIRING. */
	critical?: number | null | undefined;
	/** Which side of the levels is bad. */
	direction: ThresholdDirection;
	/** The level that makes the signal WARNING. */
	warning?: number | null | undefined;
};

export type SortEnumType = "ASC" | "DESC";

export type StringOperationFilterInput = {
	and?: Array<StringOperationFilterInput> | null | undefined;
	contains?: string | null | undefined;
	endsWith?: string | null | undefined;
	eq?: string | null | undefined;
	in?: Array<string | null | undefined> | null | undefined;
	ncontains?: string | null | undefined;
	nendsWith?: string | null | undefined;
	neq?: string | null | undefined;
	nin?: Array<string | null | undefined> | null | undefined;
	nstartsWith?: string | null | undefined;
	or?: Array<StringOperationFilterInput> | null | undefined;
	startsWith?: string | null | undefined;
};

/** What the user has typed so far, plus the environment they picked, if any. */
export type TaskBlastRadiusInput = {
	/** The task description. */
	description: string;
	/**
	 * The selected environment, or null when the task targets a project. Only the id is accepted: the
	 * classification is resolved server-side, because it feeds a judgment and a client-supplied value
	 * would be a caller-controlled input to it.
	 */
	environmentId?: string | null | undefined;
	/** The task name. */
	name: string;
};

/** Represents a link related to a task. */
export type TaskLinkFilterInput = {
	and?: Array<TaskLinkFilterInput> | null | undefined;
	/** Gets or sets the description of the link. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the ID. */
	id?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the name of the link. */
	name?: StringOperationFilterInput | null | undefined;
	or?: Array<TaskLinkFilterInput> | null | undefined;
	/** Gets or sets the ID of the task. */
	taskId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the type of the link. */
	type?: LinkTypeOperationFilterInput | null | undefined;
	/** Gets or sets the URL of the link. */
	url?: StringOperationFilterInput | null | undefined;
};

/** Cumulative execution metrics for a task, accumulated across all runs and restarts. */
export type TaskMetricsFilterInput = {
	and?: Array<TaskMetricsFilterInput> | null | undefined;
	/** Gets or sets the total number of tokens consumed across all runs. */
	inputTokens?: IntOperationFilterInput | null | undefined;
	or?: Array<TaskMetricsFilterInput> | null | undefined;
	/** Gets or sets the total number of tokens produced across all runs. */
	outputTokens?: IntOperationFilterInput | null | undefined;
	/** Gets or sets the total active execution time in seconds across all runs. */
	timeSpentInSeconds?: FloatOperationFilterInput | null | undefined;
};

/** Cumulative execution metrics for a task, accumulated across all runs and restarts. */
export type TaskMetricsSortInput = {
	/** Gets or sets the total number of tokens consumed across all runs. */
	inputTokens?: SortEnumType | null | undefined;
	/** Gets or sets the total number of tokens produced across all runs. */
	outputTokens?: SortEnumType | null | undefined;
	/** Gets or sets the total active execution time in seconds across all runs. */
	timeSpentInSeconds?: SortEnumType | null | undefined;
};

/** Represents the lifecycle status of an ArtemisTask. */
export type TaskStatus =
	/** Task is paused awaiting approval of a credential request. */
	| "APPROVAL_REQUIRED"
	/** Task was cancelled by a user. Terminal. */
	| "CANCELLED"
	/** Task has finished execution successfully. Terminal. */
	| "COMPLETED"
	/** Task has been created but not yet submitted for execution. */
	| "CREATED"
	/** Task failed during execution. Terminal. */
	| "FAILED"
	/** Task is paused awaiting a response from a user. */
	| "INPUT_REQUIRED"
	/** Task has been submitted and is awaiting agent pickup. */
	| "SUBMITTED"
	/** The platform has lost contact with the assigned agent. */
	| "UNKNOWN"
	/** Task has unresolved dependencies and is waiting for upstream tasks to complete. */
	| "WAITING"
	/** An agent is actively executing the task. */
	| "WORKING";

export type TaskStatusOperationFilterInput = {
	eq?: TaskStatus | null | undefined;
	in?: Array<TaskStatus> | null | undefined;
	neq?: TaskStatus | null | undefined;
	nin?: Array<TaskStatus> | null | undefined;
};

/**
 * A value a ArtemisTaskTemplate declares, mirroring a gallery template's
 * placeholders.variables[] entry: it is filled in when a task is created from the template.
 */
export type TaskTemplatePlaceholderFilterInput = {
	and?: Array<TaskTemplatePlaceholderFilterInput> | null | undefined;
	/** Gets or sets the value the input starts with, as text (true/false for a boolean). */
	defaultValue?: StringOperationFilterInput | null | undefined;
	/** Gets or sets optional help text shown under the input. */
	description?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the token key, referenced in text as {{key}}. Letters, digits and underscores only. */
	key?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the label shown on the input that asks for the value. */
	label?: StringOperationFilterInput | null | undefined;
	or?: Array<TaskTemplatePlaceholderFilterInput> | null | undefined;
	/** Gets or sets a value indicating whether a value must be given before the task can be created. */
	required?: BooleanOperationFilterInput | null | undefined;
	/** Gets or sets the kind of value asked for. */
	type?: TaskTemplatePlaceholderTypeOperationFilterInput | null | undefined;
};

/**
 * A value a ArtemisTaskTemplate declares, mirroring a gallery template's
 * placeholders.variables[] entry: it is filled in when a task is created from the template.
 */
export type TaskTemplatePlaceholderInput = {
	/** Gets or sets the value the input starts with, as text (true/false for a boolean). */
	defaultValue?: string | null | undefined;
	/** Gets or sets optional help text shown under the input. */
	description?: string | null | undefined;
	/** Gets or sets the token key, referenced in text as {{key}}. Letters, digits and underscores only. */
	key: string;
	/** Gets or sets the label shown on the input that asks for the value. */
	label: string;
	/** Gets or sets a value indicating whether a value must be given before the task can be created. */
	required: boolean;
	/** Gets or sets the kind of value asked for. */
	type: TaskTemplatePlaceholderType;
};

/** The kind of value a TaskTemplatePlaceholder asks for, which picks its input control. */
export type TaskTemplatePlaceholderType =
	/** A 12-digit AWS account ID. */
	| "AWS_ACCOUNT_ID"
	/** An AWS region code, picked from the known regions (e.g. us-east-1). */
	| "AWS_REGION"
	/** A yes/no toggle, substituted as true or false. */
	| "BOOLEAN"
	/** A number. */
	| "NUMBER"
	/** Free text. */
	| "STRING";

export type TaskTemplatePlaceholderTypeOperationFilterInput = {
	eq?: TaskTemplatePlaceholderType | null | undefined;
	in?: Array<TaskTemplatePlaceholderType> | null | undefined;
	neq?: TaskTemplatePlaceholderType | null | undefined;
	nin?: Array<TaskTemplatePlaceholderType> | null | undefined;
};

/**
 * Records that a deployment's graph was built from supplied Terraform files, and which ones.
 * Stored as a JSONB column on ArtemisDeployment.
 */
export type TerraformDeploymentConfigFilterInput = {
	/**
	 * Gets or sets when those files were last analysed, or null if no analysis has succeeded yet.
	 * A Terraform deployment exists from creation, so this stays null until the first successful
	 * run rather than claiming the deployment was analysed the moment it was created.
	 */
	analyzedAt?: DateTimeOperationFilterInput | null | undefined;
	and?: Array<TerraformDeploymentConfigFilterInput> | null | undefined;
	or?: Array<TerraformDeploymentConfigFilterInput> | null | undefined;
	/** Gets or sets the names of the files the current graph was built from, in the order supplied. */
	sourceNames?: ListStringOperationFilterInput | null | undefined;
};

/** Which side of a threshold is bad. */
export type ThresholdDirection =
	/** Values at or above the threshold are bad (cost, error count). */
	| "ABOVE"
	/** Values at or below the threshold are bad (free storage, healthy hosts). */
	| "BELOW";

/** Input for updating an existing agent. Null fields are left unchanged. */
export type UpdateAgentInput = {
	/** The agent name. Null leaves it unchanged. */
	name?: string | null | undefined;
	/** The user to make the agent's owner. Null leaves it unchanged. */
	ownerId?: string | null | undefined;
	/** The user-controlled lifecycle state (Enabled/Disabled). Null leaves it unchanged. */
	status?: AgentStatus | null | undefined;
};

/** Input for updating an environment. */
export type UpdateEnvironmentInput = {
	/** Whether agents may access this environment. Null leaves it unchanged. */
	allowAgentsToAccess?: boolean | null | undefined;
	/** Whether agents may be deployed into this environment. Null leaves it unchanged. */
	allowAgentsToBeDeployed?: boolean | null | undefined;
	/** Highest risk level auto-approved here. Null leaves it unchanged. */
	autoApproveUpTo?: AutoApprovalThreshold | null | undefined;
	/** The environment classification. */
	classification?: EnvironmentClassification | null | undefined;
	/** Optional provider-specific metadata key-value pairs. When provided, replaces the existing metadata in full. */
	metadata?: Array<MetadataKvInput> | null | undefined;
	/** Human-readable name. Must be unique within the workspace. */
	name?: string | null | undefined;
	/** The user to make the environment's owner; null means no change. */
	ownerId?: string | null | undefined;
	/** The environment status. */
	status?: EnvironmentStatus | null | undefined;
};

/** Input for updating an existing job. */
export type UpdateJobInput = {
	agentId?: string | null | undefined;
	description?: string | null | undefined;
	instructions?: string | null | undefined;
	name?: string | null | undefined;
	schedule?: string | null | undefined;
	timezone?: string | null | undefined;
};

/** Input for changing a workspace member's role. */
export type UpdateMemberRoleInput = {
	/** The new role to assign. */
	role: WorkspaceRole;
	/** The Artemis user ID whose role is being changed. */
	userId: string;
	/** The workspace containing the member. */
	workspaceId: string;
};

/** Input for updating an organization-level app row. null = leave unchanged. */
export type UpdateOrganizationAppInput = {
	/** The new org-level status. */
	status?: AppStatus | null | undefined;
};

/** Input for updating an organization. */
export type UpdateOrganizationInput = {
	/** The new organization name. */
	name?: string | null | undefined;
};

/** Input for updating an existing project. */
export type UpdateProjectInput = {
	/** The new project description. */
	description?: string | null | undefined;
	/** The new project instructions. */
	instructions?: string | null | undefined;
	/** The new project links. */
	links?: Array<ResourceLinkInput> | null | undefined;
	/** The new project name. */
	name?: string | null | undefined;
	/** The new project notes. */
	notes?: Array<string> | null | undefined;
	/** The user to make the project's owner; null means no change. */
	ownerId?: string | null | undefined;
	/** The new project status. */
	status?: ProjectStatus | null | undefined;
	/** The new project type. */
	type?: ProjectType | null | undefined;
};

/** Input for updating an existing report. */
export type UpdateReportInput = {
	/** New body; null means no change. */
	body?: string | null | undefined;
	/** New owner; null means no change. */
	ownerId?: string | null | undefined;
	/** Replacement set of linked project IDs; null means no change. */
	projectIds?: Array<string> | null | undefined;
	/** New lifecycle status. Sealed requires owner or workspace-admin; Open (reopen) requires workspace-admin. */
	status?: ReportStatus | null | undefined;
	/** New title; null means no change. */
	title?: string | null | undefined;
};

/** Input for updating a signal. Null fields are left unchanged. */
export type UpdateSignalInput = {
	/** The new description. */
	description?: string | null | undefined;
	/** Whether a metric is shown among the project's key numbers. */
	keyMetric?: boolean | null | undefined;
	/** The new display name. */
	name?: string | null | undefined;
	/** For a metric, its new levels; pass both levels null to remove them. */
	thresholds?: SignalThresholdsInput | null | undefined;
};

/** Input for updating an existing task. */
export type UpdateTaskInput = {
	/** The new assigneeId. */
	assigneeId?: string | null | undefined;
	/** Whether the assignee is an agent or a human user. Null leaves the current value unchanged. */
	assigneeType?: AssigneeType | null | undefined;
	/** The new set of deployment IDs this task is meant to work on. */
	deploymentIds?: Array<string> | null | undefined;
	/** The new task description. */
	description?: string | null | undefined;
	/** The new set of environment IDs this task targets directly. Null leaves unchanged. */
	environmentIds?: Array<string> | null | undefined;
	/** The new task instructions. */
	instructions?: string | null | undefined;
	/** The new task links. */
	links?: Array<ResourceLinkInput> | null | undefined;
	/** The new task name. */
	name?: string | null | undefined;
	/**
	 * The task's result text. Only takes effect when paired with status — same
	 * rule and identity check as status.
	 */
	result?: string | null | undefined;
	/**
	 * Reports the task's own status, for an assignee with no A2A endpoint to push status through
	 * (e.g. an External-mode agent). Only the task's assigned agent may set this — the platform rejects
	 * the call otherwise (ADR-024). Has no effect if the task is already in a terminal status.
	 */
	status?: TaskStatus | null | undefined;
	/** A summary of the task's progress and outcome. Null leaves unchanged. */
	summary?: string | null | undefined;
	/** Description of what the agent will check to confirm success. Null leaves unchanged. */
	verificationCriteria?: string | null | undefined;
	/** Agent's observations from the verification run. Null leaves unchanged. */
	verificationNotes?: string | null | undefined;
	/** Outcome of the agent's verification step. Null leaves unchanged. */
	verificationStatus?: VerificationStatus | null | undefined;
};

/**
 * Input for updating an existing task template. Every field is optional and null means "leave
 * unchanged". There is deliberately no workspace field: a template never moves between workspaces.
 */
export type UpdateTaskTemplateInput = {
	/** The new default assignee. */
	assigneeId?: string | null | undefined;
	/** The new default assignee kind. */
	assigneeType?: AssigneeType | null | undefined;
	/** The new targeted deployment IDs. */
	deploymentIds?: Array<string> | null | undefined;
	/** The new description. */
	description?: string | null | undefined;
	/** The new directly targeted environment IDs. */
	environmentIds?: Array<string> | null | undefined;
	/** The new instructions. */
	instructions?: string | null | undefined;
	/** The new template name. */
	name?: string | null | undefined;
	/** The new placeholder declarations, replacing the old ones wholesale. Pass an empty list to remove them all. */
	placeholders?: Array<TaskTemplatePlaceholderInput> | null | undefined;
	/** The new targeted project. Pass an empty string to clear it and target environments instead. */
	projectId?: string | null | undefined;
	/** The new verification criteria. */
	verificationCriteria?: string | null | undefined;
};

/** Input for updating a webhook. Null leaves a field unchanged; an empty string clears an optional one. */
export type UpdateWebhookInput = {
	/** New default agent (CreateTask only); empty clears it. */
	agentId?: string | null | undefined;
	/** New default deployment; empty clears it. */
	deploymentId?: string | null | undefined;
	/** New description. */
	description?: string | null | undefined;
	/** New default instructions (CreateTask only); empty clears them. */
	instructions?: string | null | undefined;
	/** New name. */
	name?: string | null | undefined;
	/** New default project; empty clears it. */
	projectId?: string | null | undefined;
	/** Enable or disable the webhook. */
	status?: WebhookStatus | null | undefined;
};

/** Input for updating a workspace-level app row. null = leave unchanged. */
export type UpdateWorkspaceAppInput = {
	/** The new workspace-level status. */
	status?: AppStatus | null | undefined;
};

/** Input for updating an existing workspace. */
export type UpdateWorkspaceInput = {
	/** The new workspace name. */
	name?: string | null | undefined;
};

/**
 * Represents the outcome of an agent's self-verification step on an ArtemisTask.
 * Null when no verification criteria have been defined for the task.
 */
export type VerificationStatus =
	/** The agent ran verification and one or more criteria failed. */
	| "FAILED"
	/** The agent ran verification and all criteria passed. */
	| "PASSED"
	/** Verification criteria are defined but the check has not been run yet. */
	| "PENDING";

/** The one thing a webhook does when invoked. Fixed at creation (ARCH-035). */
export type WebhookAction =
	/** Create a task from the webhook's template plus the caller's payload, and dispatch it. */
	| "CREATE_TASK"
	/** Run infrastructure discovery on a deployment. */
	| "DISCOVER_DEPLOYMENT";

export type WebhookActionOperationFilterInput = {
	eq?: WebhookAction | null | undefined;
	in?: Array<WebhookAction> | null | undefined;
	neq?: WebhookAction | null | undefined;
	nin?: Array<WebhookAction> | null | undefined;
};

/** Outcome of one accepted webhook invocation. */
export type WebhookDeliveryStatus =
	/** Discovery was already running for the deployment, which already satisfies the call. */
	| "COALESCED"
	/** The caller authenticated but the action could not run; see Error. */
	| "FAILED"
	/** The action ran: the task was created (and dispatch attempted), or discovery started. */
	| "SUCCEEDED";

export type WebhookDeliveryStatusOperationFilterInput = {
	eq?: WebhookDeliveryStatus | null | undefined;
	in?: Array<WebhookDeliveryStatus> | null | undefined;
	neq?: WebhookDeliveryStatus | null | undefined;
	nin?: Array<WebhookDeliveryStatus> | null | undefined;
};

/** User-controlled lifecycle of a webhook. */
export type WebhookStatus =
	/** The webhook rejects every invocation as unauthorized. */
	| "DISABLED"
	/** The webhook accepts invocations. */
	| "ENABLED";

export type WebhookStatusOperationFilterInput = {
	eq?: WebhookStatus | null | undefined;
	in?: Array<WebhookStatus> | null | undefined;
	neq?: WebhookStatus | null | undefined;
	nin?: Array<WebhookStatus> | null | undefined;
};

/** Represents the author of a worklog entry. */
export type WorklogEntryAuthor =
	/** The entry was authored by the agent. */
	| "AGENT"
	/** The entry was authored by the system. */
	| "SYSTEM"
	/** The entry was authored by the user. */
	| "USER";

export type WorklogEntryAuthorOperationFilterInput = {
	eq?: WorklogEntryAuthor | null | undefined;
	in?: Array<WorklogEntryAuthor> | null | undefined;
	neq?: WorklogEntryAuthor | null | undefined;
	nin?: Array<WorklogEntryAuthor> | null | undefined;
};

/** Represents a single append-only entry in a task's worklog. */
export type WorklogEntryFilterInput = {
	and?: Array<WorklogEntryFilterInput> | null | undefined;
	/** Gets or sets the author of the entry. */
	authorId?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the author of the entry. */
	authorType?: WorklogEntryAuthorOperationFilterInput | null | undefined;
	/** Gets or sets the UTC timestamp of this entry. */
	createdAt?: DateTimeOperationFilterInput | null | undefined;
	/** Gets or sets the unique ID of this entry. */
	id?: StringOperationFilterInput | null | undefined;
	or?: Array<WorklogEntryFilterInput> | null | undefined;
	/** Gets or sets the log message. */
	text?: StringOperationFilterInput | null | undefined;
	/** Gets or sets the type of the entry. */
	type?: WorklogEntryTypeOperationFilterInput | null | undefined;
};

/** Represents the type of a worklog entry. */
export type WorklogEntryType =
	/** The entry is an agent output. */
	| "AGENT_OUTPUT"
	/** The entry is a system message. */
	| "SYSTEM"
	/** The entry is a user input. */
	| "USER_INPUT";

export type WorklogEntryTypeOperationFilterInput = {
	eq?: WorklogEntryType | null | undefined;
	in?: Array<WorklogEntryType> | null | undefined;
	neq?: WorklogEntryType | null | undefined;
	nin?: Array<WorklogEntryType> | null | undefined;
};

/** Represents the role a user holds within a workspace. */
export type WorkspaceRole =
	/** Default role; can create and manage tasks and projects. */
	| "PARTICIPANT"
	/** Can manage workspace membership, roles, catalog, and approvals. */
	| "WORKSPACE_ADMIN";

export type WorkspaceRoleOperationFilterInput = {
	eq?: WorkspaceRole | null | undefined;
	in?: Array<WorkspaceRole> | null | undefined;
	neq?: WorkspaceRole | null | undefined;
	nin?: Array<WorkspaceRole> | null | undefined;
};

export type ClaimOrganizationMutationVariables = Exact<{
	input: ClaimOrganizationInput;
}>;

export type ClaimOrganizationMutation = {
	claimOrganization: {
		organization: { id: string; name: string; slug: string };
	};
};

export type UpdateOrganizationMutationVariables = Exact<{
	input: UpdateOrganizationInput;
}>;

export type UpdateOrganizationMutation = {
	updateOrganization: {
		organization: {
			id: string;
			name: string;
			organizationId: string;
			createdAt: unknown;
		};
	};
};

export type DeactivateAccountMutationVariables = Exact<{
	[key: string]: never;
}>;

export type DeactivateAccountMutation = {
	deactivateAccount: { success: boolean };
};

export type AddOrganizationAdminMutationVariables = Exact<{
	userId: string;
}>;

export type AddOrganizationAdminMutation = {
	addOrganizationAdmin: { organizationAdmin: { id: string; userId: string } };
};

export type RemoveOrganizationAdminMutationVariables = Exact<{
	userId: string;
}>;

export type RemoveOrganizationAdminMutation = {
	removeOrganizationAdmin: { success: boolean };
};

export type AddOrganizationDomainMutationVariables = Exact<{
	domain: string;
}>;

export type AddOrganizationDomainMutation = {
	addOrganizationDomain: {
		organizationDomain: {
			id: string;
			domain: string;
			organizationId: string;
			createdAt: unknown;
		};
	};
};

export type RemoveOrganizationDomainMutationVariables = Exact<{
	domainId: string;
}>;

export type RemoveOrganizationDomainMutation = {
	removeOrganizationDomain: { success: boolean };
};

export type RequestOrganizationDomainVerificationMutationVariables = Exact<{
	domainId: string;
}>;

export type RequestOrganizationDomainVerificationMutation = {
	requestOrganizationDomainVerification: {
		organizationDomain: {
			id: string;
			domain: string;
			verificationToken: string | null;
			verifiedAt: unknown;
		};
	};
};

export type VerifyOrganizationDomainMutationVariables = Exact<{
	domainId: string;
}>;

export type VerifyOrganizationDomainMutation = {
	verifyOrganizationDomain: {
		organizationDomain: { id: string; domain: string; verifiedAt: unknown };
	};
};

export type LinkAwsMarketplaceSubscriptionMutationVariables = Exact<{
	claimToken: string;
}>;

export type LinkAwsMarketplaceSubscriptionMutation = {
	linkAWSMarketplaceSubscription: { success: boolean };
};

export type CreateStripeCheckoutSessionMutationVariables = Exact<{
	[key: string]: never;
}>;

export type CreateStripeCheckoutSessionMutation = {
	createStripeCheckoutSession: { url: string };
};

export type CreateStripeBillingPortalSessionMutationVariables = Exact<{
	[key: string]: never;
}>;

export type CreateStripeBillingPortalSessionMutation = {
	createStripeBillingPortalSession: { url: string };
};

export type CreateTaskMutationVariables = Exact<{
	input: CreateTaskInput;
}>;

export type CreateTaskMutation = { createTask: { task: { id: string } } };

export type UpdateTaskMutationVariables = Exact<{
	taskId: string;
	input: UpdateTaskInput;
}>;

export type UpdateTaskMutation = {
	updateTask: {
		task: { id: string; status: TaskStatus; result: string | null };
	};
};

export type DeleteTaskMutationVariables = Exact<{
	taskId: string;
}>;

export type DeleteTaskMutation = { deleteTask: { success: boolean } };

export type CreateTaskTemplateMutationVariables = Exact<{
	input: CreateTaskTemplateInput;
}>;

export type CreateTaskTemplateMutation = {
	createTaskTemplate: { taskTemplate: { id: string; name: string } };
};

export type UpdateTaskTemplateMutationVariables = Exact<{
	taskTemplateId: string;
	input: UpdateTaskTemplateInput;
}>;

export type UpdateTaskTemplateMutation = {
	updateTaskTemplate: {
		taskTemplate: { id: string; name: string; updatedAt: unknown };
	};
};

export type DeleteTaskTemplateMutationVariables = Exact<{
	taskTemplateId: string;
}>;

export type DeleteTaskTemplateMutation = {
	deleteTaskTemplate: { success: boolean };
};

export type ReportTaskUsageMutationVariables = Exact<{
	taskId: string;
	inputTokens: number;
	outputTokens: number;
}>;

export type ReportTaskUsageMutation = { reportTaskUsage: { success: boolean } };

export type SetUserDisabledMutationVariables = Exact<{
	userId: string;
	disabled: boolean;
}>;

export type SetUserDisabledMutation = {
	setUserDisabled: { user: { id: string; disabledAt: unknown } };
};

export type CancelTaskMutationVariables = Exact<{
	taskId: string;
}>;

export type CancelTaskMutation = {
	cancelTask: { task: { id: string; status: TaskStatus } };
};

export type SubmitTaskInputMutationVariables = Exact<{
	taskId: string;
	text: string;
}>;

export type SubmitTaskInputMutation = {
	submitTaskInput: { task: { id: string; status: TaskStatus } };
};

export type AppendTaskProgressMutationVariables = Exact<{
	taskId: string;
	text: string;
}>;

export type AppendTaskProgressMutation = {
	appendTaskProgress: { task: { id: string } };
};

export type DispatchTaskMutationVariables = Exact<{
	taskId: string;
}>;

export type DispatchTaskMutation = {
	dispatchTask: {
		task: { id: string; status: TaskStatus; statusReason: string };
	};
};

export type ReopenTaskMutationVariables = Exact<{
	taskId: string;
}>;

export type ReopenTaskMutation = {
	reopenTask: {
		task: { id: string; status: TaskStatus; result: string | null };
	};
};

export type PostTaskMessageMutationVariables = Exact<{
	taskId: string;
	text: string;
}>;

export type PostTaskMessageMutation = {
	postTaskMessage: { task: { id: string; status: TaskStatus } };
};

export type CreateEnvironmentMutationVariables = Exact<{
	input: CreateEnvironmentInput;
}>;

export type CreateEnvironmentMutation = {
	createEnvironment: { environment: { id: string } };
};

export type UpdateEnvironmentMutationVariables = Exact<{
	environmentId: string;
	input: UpdateEnvironmentInput;
}>;

export type UpdateEnvironmentMutation = {
	updateEnvironment: { environment: { id: string } };
};

export type DeleteEnvironmentMutationVariables = Exact<{
	environmentId: string;
}>;

export type DeleteEnvironmentMutation = {
	deleteEnvironment: { success: boolean };
};

export type CreateWorkspaceMutationVariables = Exact<{
	input: CreateWorkspaceInput;
}>;

export type CreateWorkspaceMutation = {
	createWorkspace: { workspace: { id: string; name: string } };
};

export type UpdateWorkspaceMutationVariables = Exact<{
	workspaceId: string;
	input: UpdateWorkspaceInput;
}>;

export type UpdateWorkspaceMutation = {
	updateWorkspace: { workspace: { id: string; name: string } };
};

export type DeleteWorkspaceMutationVariables = Exact<{
	workspaceId: string;
}>;

export type DeleteWorkspaceMutation = { deleteWorkspace: { success: boolean } };

export type CreateProjectMutationVariables = Exact<{
	input: CreateProjectInput;
}>;

export type CreateProjectMutation = {
	createProject: { project: { id: string } };
};

export type UpdateProjectMutationVariables = Exact<{
	projectId: string;
	input: UpdateProjectInput;
}>;

export type UpdateProjectMutation = {
	updateProject: { project: { id: string } };
};

export type DeleteProjectMutationVariables = Exact<{
	projectId: string;
}>;

export type DeleteProjectMutation = { deleteProject: { success: boolean } };

export type ArchiveProjectMutationVariables = Exact<{
	projectId: string;
}>;

export type ArchiveProjectMutation = {
	archiveProject: { project: { id: string; status: ProjectStatus } };
};

export type CreateAgentMutationVariables = Exact<{
	input: CreateAgentInput;
}>;

export type CreateAgentMutation = {
	createAgent: {
		agent: { id: string; kind: AgentKind; clientId: string };
		credentials: {
			clientId: string;
			clientSecret: string;
			expiresAt: unknown;
		} | null;
	};
};

export type RotateAgentCredentialMutationVariables = Exact<{
	agentId: string;
	workspaceId: string;
	clientSecretExpiresAt?: unknown;
}>;

export type RotateAgentCredentialMutation = {
	rotateAgentCredential: {
		agent: { id: string; clientId: string; clientSecretExpiresAt: unknown };
		credentials: { clientId: string; clientSecret: string; expiresAt: unknown };
	};
};

export type UpdateAgentMutationVariables = Exact<{
	agentId: string;
	workspaceId: string;
	input: UpdateAgentInput;
}>;

export type UpdateAgentMutation = {
	updateAgent: { agent: { id: string; status: AgentStatus } };
};

export type DeleteAgentMutationVariables = Exact<{
	agentId: string;
	workspaceId: string;
	force?: boolean;
}>;

export type DeleteAgentMutation = { deleteAgent: { success: boolean } };

export type SetAgentRuntimeVersionMutationVariables = Exact<{
	agentId: string;
	workspaceId: string;
	version: string;
}>;

export type SetAgentRuntimeVersionMutation = {
	setAgentRuntimeVersion: {
		agent: {
			id: string;
			runtimeVersion: string | null;
			health: AgentHealth | null;
		};
	};
};

export type PingAgentMutationVariables = Exact<{
	agentId: string;
	workspaceId: string;
}>;

export type PingAgentMutation = {
	pingAgent: {
		health: {
			isHealthy: boolean;
			checkedAt: unknown;
			errorMessage: string | null;
		};
	};
};

export type RegisterAssetMutationVariables = Exact<{
	input: RegisterAssetInput;
}>;

export type RegisterAssetMutation = {
	registerAsset: {
		asset: {
			id: string;
			name: string;
			mediaType: string;
			category: AssetCategory;
			state: AssetState;
			stagingKey: string;
			storageKey: string;
			parentId: string;
			parentType: ParentType;
			createdAt: unknown;
		};
		upload: { url: string; fields: Array<{ name: string; value: string }> };
	};
};

export type ConfirmAssetMutationVariables = Exact<{
	assetId: string;
}>;

export type ConfirmAssetMutation = {
	confirmAsset: {
		asset: { id: string; state: AssetState; size: unknown; storageKey: string };
	};
};

export type DeleteAssetMutationVariables = Exact<{
	assetId: string;
}>;

export type DeleteAssetMutation = { deleteAsset: { success: boolean } };

export type CreateDeploymentMutationVariables = Exact<{
	input: CreateDeploymentInput;
}>;

export type CreateDeploymentMutation = {
	createDeployment: { deployment: { id: string } };
};

export type RefreshDeploymentMutationVariables = Exact<{
	deploymentId: string;
}>;

export type RefreshDeploymentMutation = {
	refreshDeployment: { deployment: { id: string } };
};

export type RequestTerraformUploadMutationVariables = Exact<{
	workspaceId: string;
	filenames: Array<string> | string;
}>;

export type RequestTerraformUploadMutation = {
	requestTerraformUpload: {
		uploads: Array<{
			key: string;
			upload: { url: string; fields: Array<{ name: string; value: string }> };
		}>;
	};
};

export type AnalyzeTerraformDeploymentMutationVariables = Exact<{
	deploymentId: string;
	input: AnalyzeTerraformInput;
}>;

export type AnalyzeTerraformDeploymentMutation = {
	analyzeTerraformDeployment: { deployment: { id: string } };
};

export type DeleteDeploymentMutationVariables = Exact<{
	deploymentId: string;
}>;

export type DeleteDeploymentMutation = {
	deleteDeployment: { success: boolean };
};

export type ResolveApprovalMutationVariables = Exact<{
	approvalId: string;
	input: ResolveApprovalInput;
}>;

export type ResolveApprovalMutation = {
	resolveApproval: {
		approval: {
			id: string;
			status: ApprovalStatus;
			resolvedBy: string | null;
			resolvedAt: unknown;
			notes: string | null;
		};
	};
};

export type RequestCredentialEscalationMutationVariables = Exact<{
	input: RequestCredentialEscalationInput;
}>;

export type RequestCredentialEscalationMutation = {
	requestCredentialEscalation: {
		approval: {
			id: string;
			name: string;
			status: ApprovalStatus;
			taskId: string;
			environmentId: string;
			riskLevel: ApprovalRiskLevel;
			requestType: ApprovalRequestType;
			actionDescription: string;
			approvalWindowExpiresAt: unknown;
			createdAt: unknown;
		};
	};
};

export type IssueCredentialsMutationVariables = Exact<{
	environmentId: string;
	approvalId?: string | null | undefined;
}>;

export type IssueCredentialsMutation = {
	issueCredentials: {
		credentials: {
			accessKeyId: string;
			secretAccessKey: string;
			sessionToken: string;
			expiration: unknown;
			region: string;
		};
	};
};

export type AddWorkspaceMemberMutationVariables = Exact<{
	input: AddWorkspaceMemberInput;
}>;

export type AddWorkspaceMemberMutation = {
	addWorkspaceMember: {
		workspaceMember: {
			id: string;
			workspaceId: string;
			userId: string;
			role: WorkspaceRole;
			createdAt: unknown;
		};
	};
};

export type RemoveWorkspaceMemberMutationVariables = Exact<{
	workspaceId: string;
	userId: string;
}>;

export type RemoveWorkspaceMemberMutation = {
	removeWorkspaceMember: { success: boolean };
};

export type UpdateWorkspaceMemberRoleMutationVariables = Exact<{
	input: UpdateMemberRoleInput;
}>;

export type UpdateWorkspaceMemberRoleMutation = {
	updateWorkspaceMemberRole: {
		workspaceMember: { id: string; userId: string; role: WorkspaceRole };
	};
};

export type CreateJobMutationVariables = Exact<{
	input: CreateJobInput;
}>;

export type CreateJobMutation = {
	createJob: {
		job: {
			id: string;
			name: string;
			description: string;
			projectId: string | null;
			workspaceId: string;
			schedule: string;
			timezone: string;
			status: JobStatus;
			lastFiredAt: unknown;
			nextFireAt: unknown;
			createdAt: unknown;
		};
	};
};

export type UpdateJobMutationVariables = Exact<{
	jobId: string;
	input: UpdateJobInput;
}>;

export type UpdateJobMutation = {
	updateJob: {
		job: {
			id: string;
			name: string;
			description: string;
			projectId: string | null;
			workspaceId: string;
			schedule: string;
			timezone: string;
			status: JobStatus;
			lastFiredAt: unknown;
			nextFireAt: unknown;
			createdAt: unknown;
		};
	};
};

export type PauseJobMutationVariables = Exact<{
	jobId: string;
}>;

export type PauseJobMutation = {
	pauseJob: { job: { id: string; status: JobStatus } };
};

export type ResumeJobMutationVariables = Exact<{
	jobId: string;
}>;

export type ResumeJobMutation = {
	resumeJob: { job: { id: string; status: JobStatus } };
};

export type DeleteJobMutationVariables = Exact<{
	jobId: string;
}>;

export type DeleteJobMutation = { deleteJob: { success: boolean } };

export type TriggerJobRunMutationVariables = Exact<{
	jobId: string;
}>;

export type TriggerJobRunMutation = {
	triggerJobRun: {
		jobRun: {
			id: string;
			jobId: string;
			taskId: string | null;
			projectId: string | null;
			triggerType: JobRunTriggerType;
			firedAt: unknown;
			completedAt: unknown;
			task: { id: string; status: TaskStatus } | null;
		};
	};
};

export type CreateReportMutationVariables = Exact<{
	input: CreateReportInput;
}>;

export type CreateReportMutation = {
	createReport: {
		report: {
			id: string;
			title: string;
			type: ReportType;
			status: ReportStatus;
			ownerId: string;
			createdBy: string;
			projectIds: Array<string>;
			workspaceId: string;
			createdAt: unknown;
			updatedAt: unknown;
		};
	};
};

export type UpdateReportMutationVariables = Exact<{
	reportId: string;
	input: UpdateReportInput;
}>;

export type UpdateReportMutation = {
	updateReport: {
		report: {
			id: string;
			title: string;
			type: ReportType;
			status: ReportStatus;
			ownerId: string;
			body: string;
			projectIds: Array<string>;
			workspaceId: string;
			createdAt: unknown;
			updatedAt: unknown;
		};
	};
};

export type DeleteReportMutationVariables = Exact<{
	reportId: string;
}>;

export type DeleteReportMutation = { deleteReport: { success: boolean } };

export type CreateGitProviderConnectionAuthUrlMutationVariables = Exact<{
	[key: string]: never;
}>;

export type CreateGitProviderConnectionAuthUrlMutation = {
	createGitProviderConnectionAuthUrl: { url: string; installUrl: string };
};

export type ListGitProviderInstallationsMutationVariables = Exact<{
	state: string;
	code: string;
}>;

export type ListGitProviderInstallationsMutation = {
	listGitProviderInstallations: Array<{
		installationId: string;
		accountLogin: string;
		accountType: string;
		status: GitProviderInstallationStatus;
		grant: string | null;
	}>;
};

export type CompleteGitProviderConnectionMutationVariables = Exact<{
	installationId: string;
	grant: string;
}>;

export type CompleteGitProviderConnectionMutation = {
	completeGitProviderConnection: {
		id: string;
		provider: GitProviderType;
		installationId: string;
		accountLogin: string;
		accountType: string;
		createdByUserId: string;
	};
};

export type DeleteGitProviderConnectionMutationVariables = Exact<{
	connectionId: string;
}>;

export type DeleteGitProviderConnectionMutation = {
	deleteGitProviderConnection: { success: boolean };
};

export type LinkProjectRepositoryMutationVariables = Exact<{
	projectId: string;
	gitProviderConnectionId: string;
	repositoryExternalId: string;
	repositoryFullName: string;
}>;

export type LinkProjectRepositoryMutation = {
	linkProjectRepository: {
		repository: {
			id: string;
			projectId: string;
			gitProviderConnectionId: string;
			provider: string;
			repositoryFullName: string;
			repositoryExternalId: string;
			defaultBranch: string | null;
		};
	};
};

export type UnlinkProjectRepositoryMutationVariables = Exact<{
	projectRepositoryId: string;
}>;

export type UnlinkProjectRepositoryMutation = {
	unlinkProjectRepository: { success: boolean };
};

export type UpdateOrganizationAppMutationVariables = Exact<{
	kind: AppKind;
	input: UpdateOrganizationAppInput;
}>;

export type UpdateOrganizationAppMutation = {
	updateOrganizationApp: {
		organizationApp: {
			id: string;
			kind: AppKind;
			status: AppStatus;
			clientId: string | null;
			clientSecretExpiresAt: unknown;
			lastSeenAt: unknown;
		};
		credentials: {
			clientId: string;
			clientSecret: string;
			expiresAt: unknown;
		} | null;
	};
};

export type RotateOrganizationAppCredentialMutationVariables = Exact<{
	kind: AppKind;
	clientSecretExpiresAt?: unknown;
}>;

export type RotateOrganizationAppCredentialMutation = {
	rotateOrganizationAppCredential: {
		organizationApp: {
			id: string;
			kind: AppKind;
			clientId: string | null;
			clientSecretExpiresAt: unknown;
		};
		credentials: { clientId: string; clientSecret: string; expiresAt: unknown };
	};
};

export type UpdateWorkspaceAppMutationVariables = Exact<{
	workspaceId: string;
	kind: AppKind;
	input: UpdateWorkspaceAppInput;
}>;

export type UpdateWorkspaceAppMutation = {
	updateWorkspaceApp: {
		workspaceApp: {
			id: string;
			kind: AppKind;
			status: AppStatus;
			workspaceId: string;
		};
	};
};

export type CreateWebhookMutationVariables = Exact<{
	input: CreateWebhookInput;
}>;

export type CreateWebhookMutation = {
	createWebhook: {
		secret: string;
		webhook: {
			id: string;
			name: string;
			action: WebhookAction;
			status: WebhookStatus;
			workspaceId: string;
		};
	};
};

export type UpdateWebhookMutationVariables = Exact<{
	webhookId: string;
	input: UpdateWebhookInput;
}>;

export type UpdateWebhookMutation = {
	updateWebhook: {
		webhook: {
			id: string;
			name: string;
			description: string;
			status: WebhookStatus;
			agentId: string | null;
			instructions: string | null;
			projectId: string | null;
			deploymentId: string | null;
			updatedAt: unknown;
		};
	};
};

export type RotateWebhookSecretMutationVariables = Exact<{
	webhookId: string;
}>;

export type RotateWebhookSecretMutation = {
	rotateWebhookSecret: {
		secret: string;
		webhook: { id: string; secretRotatedAt: unknown };
	};
};

export type DeleteWebhookMutationVariables = Exact<{
	webhookId: string;
}>;

export type DeleteWebhookMutation = { deleteWebhook: { success: boolean } };

export type CreateSignalMutationVariables = Exact<{
	input: CreateSignalInput;
}>;

export type CreateSignalMutation = {
	createSignal: {
		signal: {
			id: string;
			name: string;
			externalId: string;
			deploymentId: string | null;
			environmentId: string | null;
			origin: SignalOrigin;
		};
	};
};

export type UpsertSignalMutationVariables = Exact<{
	input: CreateSignalInput;
}>;

export type UpsertSignalMutation = {
	upsertSignal: {
		created: boolean;
		signal: {
			id: string;
			name: string;
			externalId: string;
			deploymentId: string | null;
			environmentId: string | null;
			origin: SignalOrigin;
		};
	};
};

export type SyncSignalStatusMutationVariables = Exact<{
	signalId: string;
}>;

export type SyncSignalStatusMutation = {
	syncSignalStatus: {
		state: SignalState;
		rawState: string | null;
		reason: string | null;
		url: string | null;
		error: string | null;
		observedAt: unknown;
		value: number | null;
		unit: string | null;
		previousValue: number | null;
		previousLabel: string | null;
		series: Array<number> | null;
		asOf: unknown;
	};
};

export type UpdateSignalMutationVariables = Exact<{
	signalId: string;
	input: UpdateSignalInput;
}>;

export type UpdateSignalMutation = {
	updateSignal: {
		signal: {
			id: string;
			name: string;
			description: string | null;
			thresholds: {
				warning: number | null;
				critical: number | null;
				direction: ThresholdDirection;
			} | null;
		};
	};
};

export type DeleteSignalMutationVariables = Exact<{
	signalId: string;
}>;

export type DeleteSignalMutation = { deleteSignal: { success: boolean } };

export type OrganizationLookupByDomainQueryVariables = Exact<{
	domain: string;
}>;

export type OrganizationLookupByDomainQuery = {
	organizationLookupByDomain: {
		orgId: string;
		orgName: string;
		orgSlug: string;
		hasSso: boolean;
	} | null;
};

export type OrganizationLookupBySlugQueryVariables = Exact<{
	slug: string;
}>;

export type OrganizationLookupBySlugQuery = {
	organizationLookupBySlug: {
		orgId: string;
		orgName: string;
		orgSlug: string;
		hasSso: boolean;
	} | null;
};

export type GetOrganizationQueryVariables = Exact<{ [key: string]: never }>;

export type GetOrganizationQuery = {
	organization: Array<{
		id: string;
		name: string;
		organizationId: string;
		slug: string;
		createdAt: unknown;
	}>;
};

export type GetOrganizationAdminsQueryVariables = Exact<{
	[key: string]: never;
}>;

export type GetOrganizationAdminsQuery = {
	organizationAdmins: Array<{ userId: string }>;
};

export type GetOrganizationDomainsQueryVariables = Exact<{
	[key: string]: never;
}>;

export type GetOrganizationDomainsQuery = {
	organizationDomains: Array<{
		id: string;
		domain: string;
		verifiedAt: unknown;
		verificationToken: string | null;
	}>;
};

export type GetPlatformFeaturesQueryVariables = Exact<{ [key: string]: never }>;

export type GetPlatformFeaturesQuery = {
	platformFeatures: {
		gitHub: boolean;
		awsMarketplace: boolean;
		stripeBilling: boolean;
	};
};

export type GetOrganizationSubscriptionQueryVariables = Exact<{
	[key: string]: never;
}>;

export type GetOrganizationSubscriptionQuery = {
	organizationSubscription: {
		status: string;
		environments: number;
		updatedAt: unknown;
	} | null;
};

export type GetOrganizationAwsMarketplaceSubscriptionQueryVariables = Exact<{
	[key: string]: never;
}>;

export type GetOrganizationAwsMarketplaceSubscriptionQuery = {
	organizationAWSMarketplaceSubscription: {
		environments: number;
		customerAwsAccountId: string;
	} | null;
};

export type GetWorkspacesQueryVariables = Exact<{
	first?: number | null | undefined;
	after?: string | null | undefined;
}>;

export type GetWorkspacesQuery = {
	workspaces: {
		nodes: Array<{
			id: string;
			name: string;
			organizationId: string;
			createdAt: unknown;
		}> | null;
		pageInfo: { hasNextPage: boolean; endCursor: string | null };
	} | null;
};

export type GetWorkspaceQueryVariables = Exact<{
	workspaceId: string;
}>;

export type GetWorkspaceQuery = {
	workspace: {
		id: string;
		name: string;
		organizationId: string;
		createdAt: unknown;
		agents: Array<{ id: string; name: string; createdAt: unknown }>;
		environments: Array<{
			id: string;
			name: string;
			status: EnvironmentStatus;
			provider: EnvironmentProvider;
			classification: EnvironmentClassification;
			createdAt: unknown;
		}>;
		projects: Array<{
			id: string;
			name: string;
			status: ProjectStatus;
			createdAt: unknown;
		}>;
	} | null;
};

export type GetWorkspaceInfoQueryVariables = Exact<{
	workspaceId: string;
}>;

export type GetWorkspaceInfoQuery = {
	workspace: {
		id: string;
		name: string;
		organizationId: string;
		createdAt: unknown;
	} | null;
};

export type GetProjectsQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisProjectSortInput>
		| ArtemisProjectSortInput
		| null
		| undefined;
	where?: ArtemisProjectFilterInput | null | undefined;
}>;

export type GetProjectsQuery = {
	projects: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			description: string;
			status: ProjectStatus;
			type: ProjectType;
			summary: string | null;
			createdAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetProjectQueryVariables = Exact<{
	projectId: string;
}>;

export type GetProjectQuery = {
	project: {
		id: string;
		name: string;
		description: string;
		status: ProjectStatus;
		ownerId: string | null;
		workspaceId: string;
		type: ProjectType;
		notes: Array<string>;
		summary: string | null;
		instructions: string;
		createdAt: unknown;
		links: Array<{
			url: string | null;
			name: string | null;
			description: string | null;
			type: LinkType;
		}>;
		deployments: Array<{
			id: string;
			name: string;
			description: string | null;
			environmentId: string;
			workspaceId: string;
			createdAt: unknown;
			config: { region: string | null; stackNames: Array<string> } | null;
			terraformConfig: {
				sourceNames: Array<string>;
				analyzedAt: unknown;
			} | null;
			graph: {
				id: string;
				type: GraphType;
				isDiscovering: boolean;
				discoveryError: string | null;
				lastDiscoveryAt: unknown;
				nodes: Array<{
					id: string;
					resourceId: string | null;
					label: string;
					serviceName: string;
					providerName: string;
				}>;
				relationships: Array<{
					id: string;
					sourceNodeId: string;
					targetNodeId: string;
					type: string;
				}>;
			} | null;
		}>;
		assets: Array<{
			id: string;
			name: string;
			description: string;
			mediaType: string;
			category: AssetCategory;
			size: unknown;
			summary: string | null;
			createdAt: unknown;
		}>;
		reports: Array<{
			id: string;
			title: string;
			type: ReportType;
			status: ReportStatus;
			ownerId: string;
			createdBy: string;
			projectIds: Array<string>;
			workspaceId: string;
			summary: string | null;
			createdAt: unknown;
			updatedAt: unknown;
		}>;
	} | null;
};

export type GetTasksQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?: Array<ArtemisTaskSortInput> | ArtemisTaskSortInput | null | undefined;
	where?: ArtemisTaskFilterInput | null | undefined;
}>;

export type GetTasksQuery = {
	tasks: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			summary: string | null;
			status: TaskStatus;
			projectId: string | null;
			workspaceId: string;
			assigneeId: string | null;
			assigneeType: AssigneeType | null;
			verificationCriteria: string | null;
			verificationStatus: VerificationStatus | null;
			verificationNotes: string | null;
			createdAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetTaskQueryVariables = Exact<{
	taskId: string;
}>;

export type GetTaskQuery = {
	task: {
		id: string;
		name: string;
		description: string;
		instructions: string;
		status: TaskStatus;
		statusReason: string;
		projectId: string | null;
		workspaceId: string;
		assigneeId: string | null;
		assigneeType: AssigneeType | null;
		result: string | null;
		verificationCriteria: string | null;
		verificationStatus: VerificationStatus | null;
		verificationNotes: string | null;
		summary: string | null;
		deploymentIds: Array<string>;
		environmentIds: Array<string>;
		createdAt: unknown;
		assets: Array<{
			id: string;
			name: string;
			description: string;
			mediaType: string;
			category: AssetCategory;
			size: unknown;
			summary: string | null;
			createdAt: unknown;
		}>;
		links: Array<{
			url: string | null;
			description: string | null;
			name: string | null;
			type: LinkType;
		}>;
		worklog: Array<{
			id: string;
			authorId: string | null;
			authorType: WorklogEntryAuthor;
			type: WorklogEntryType;
			text: string;
			createdAt: unknown;
		}>;
		metrics: {
			inputTokens: number;
			outputTokens: number;
			timeSpentInSeconds: number;
		} | null;
	} | null;
};

export type GetTaskTemplatesQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisTaskTemplateSortInput>
		| ArtemisTaskTemplateSortInput
		| null
		| undefined;
	where?: ArtemisTaskTemplateFilterInput | null | undefined;
}>;

export type GetTaskTemplatesQuery = {
	taskTemplates: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			description: string;
			instructions: string | null;
			projectId: string | null;
			workspaceId: string;
			deploymentIds: Array<string>;
			environmentIds: Array<string>;
			assigneeId: string | null;
			assigneeType: AssigneeType | null;
			verificationCriteria: string | null;
			createdAt: unknown;
			updatedAt: unknown;
			placeholders: Array<{
				key: string;
				label: string;
				description: string | null;
				type: TaskTemplatePlaceholderType;
				required: boolean;
				defaultValue: string | null;
			}>;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetTaskTemplateQueryVariables = Exact<{
	taskTemplateId: string;
}>;

export type GetTaskTemplateQuery = {
	taskTemplate: {
		id: string;
		name: string;
		description: string;
		instructions: string | null;
		projectId: string | null;
		workspaceId: string;
		deploymentIds: Array<string>;
		environmentIds: Array<string>;
		assigneeId: string | null;
		assigneeType: AssigneeType | null;
		verificationCriteria: string | null;
		createdAt: unknown;
		updatedAt: unknown;
		placeholders: Array<{
			key: string;
			label: string;
			description: string | null;
			type: TaskTemplatePlaceholderType;
			required: boolean;
			defaultValue: string | null;
		}>;
	} | null;
};

export type GetAgentsQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisAgentSortInput>
		| ArtemisAgentSortInput
		| null
		| undefined;
	where?: ArtemisAgentFilterInput | null | undefined;
}>;

export type GetAgentsQuery = {
	agents: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			role: AgentRole;
			kind: AgentKind;
			workspaceId: string;
			endpointUrl: string;
			status: AgentStatus;
			health: AgentHealth | null;
			lastSeenAt: unknown;
			createdAt: unknown;
			environmentId: string | null;
			environment: {
				id: string;
				name: string;
				provider: EnvironmentProvider;
				status: EnvironmentStatus;
			} | null;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetAgentQueryVariables = Exact<{
	agentId: string;
	workspaceId: string;
}>;

export type GetAgentQuery = {
	agent: {
		id: string;
		name: string;
		role: AgentRole;
		kind: AgentKind;
		workspaceId: string;
		endpointUrl: string;
		clientId: string;
		clientSecretExpiresAt: unknown;
		status: AgentStatus;
		health: AgentHealth | null;
		lastSeenAt: unknown;
		createdAt: unknown;
		environmentId: string | null;
		runtimeVersion: string | null;
		supportsStreaming: boolean;
		supportsPushNotifications: boolean;
		ownerId: string | null;
		environment: {
			id: string;
			name: string;
			provider: EnvironmentProvider;
			status: EnvironmentStatus;
		} | null;
		deploymentSettings: Array<{ key: string; value: string }>;
		skills: Array<{
			id: string;
			name: string;
			description: string | null;
			tags: Array<string>;
		}>;
	} | null;
};

export type GetAgentDeploymentOptionsQueryVariables = Exact<{
	[key: string]: never;
}>;

export type GetAgentDeploymentOptionsQuery = {
	agentDeploymentOptions: {
		awsRegions: Array<string>;
		runtimeVersions: Array<string>;
	};
};

export type GetAssetDownloadUrlQueryVariables = Exact<{
	assetId: string;
}>;

export type GetAssetDownloadUrlQuery = { assetDownloadUrl: unknown };

export type GetEnvironmentsQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisEnvironmentSortInput>
		| ArtemisEnvironmentSortInput
		| null
		| undefined;
	where?: ArtemisEnvironmentFilterInput | null | undefined;
}>;

export type GetEnvironmentsQuery = {
	environments: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			status: EnvironmentStatus;
			provider: EnvironmentProvider;
			classification: EnvironmentClassification;
			autoApproveUpTo: AutoApprovalThreshold;
			allowAgentsToBeDeployed: boolean;
			allowAgentsToAccess: boolean;
			createdAt: unknown;
			metadata: Array<{ key: string; value: string }>;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetEnvironmentQueryVariables = Exact<{
	environmentId: string;
}>;

export type GetEnvironmentQuery = {
	environment: {
		id: string;
		name: string;
		status: EnvironmentStatus;
		provider: EnvironmentProvider;
		classification: EnvironmentClassification;
		autoApproveUpTo: AutoApprovalThreshold;
		allowAgentsToBeDeployed: boolean;
		allowAgentsToAccess: boolean;
		ownerId: string | null;
		workspaceId: string;
		createdAt: unknown;
		metadata: Array<{ key: string; value: string }>;
	} | null;
};

export type GetEnvironmentDeleteImpactQueryVariables = Exact<{
	environmentId: string;
}>;

export type GetEnvironmentDeleteImpactQuery = {
	environmentDeleteImpact: {
		isBlocked: boolean;
		deployments: Array<{
			id: string;
			name: string;
			projectId: string | null;
			projectName: string | null;
		}>;
		managedAgents: Array<{ id: string; name: string }>;
		jobs: Array<{ id: string; name: string }>;
		taskTemplates: Array<{ id: string; name: string }>;
		externalAgents: Array<{ id: string; name: string }>;
	} | null;
};

export type EnvironmentAwsStacksQueryVariables = Exact<{
	environmentId: string;
	region?: string | null | undefined;
}>;

export type EnvironmentAwsStacksQuery = { environmentAwsStacks: Array<string> };

export type EnvironmentConnectivityQueryVariables = Exact<{
	environmentId: string;
}>;

export type EnvironmentConnectivityQuery = {
	environmentConnectivity: {
		ok: boolean;
		checkedAt: unknown;
		checks: Array<{
			role: string;
			roleArn: string;
			ok: boolean;
			message: string | null;
		}>;
	};
};

export type GetEnvironmentSetupTemplateQueryVariables = Exact<{
	provider: EnvironmentProvider;
	input: EnvironmentSetupTemplateInput;
}>;

export type GetEnvironmentSetupTemplateQuery = {
	environmentSetupTemplate: { fileName: string; content: string };
};

export type GetMeQueryVariables = Exact<{ [key: string]: never }>;

export type GetMeQuery = {
	me: {
		id: string;
		email: string;
		displayName: string;
		organizationId: string;
		createdAt: unknown;
	} | null;
};

export type GetCallerIdentityQueryVariables = Exact<{ [key: string]: never }>;

export type GetCallerIdentityQuery = {
	callerIdentity: {
		kind: CallerKind;
		id: string;
		organizationId: string;
		workspaceId: string | null;
	} | null;
};

export type GetUserQueryVariables = Exact<{
	userId: string;
}>;

export type GetUserQuery = {
	user: {
		id: string;
		email: string;
		displayName: string;
		provider: string;
		organizationId: string;
		createdAt: unknown;
		updatedAt: unknown;
	} | null;
};

export type GetUsersQueryVariables = Exact<{
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	workspaceId?: string | null | undefined;
	order?: Array<ArtemisUserSortInput> | ArtemisUserSortInput | null | undefined;
	where?: ArtemisUserFilterInput | null | undefined;
}>;

export type GetUsersQuery = {
	users: {
		totalCount: number;
		nodes: Array<{
			id: string;
			email: string;
			displayName: string;
			provider: string;
			organizationId: string;
			createdAt: unknown;
			updatedAt: unknown;
			lastActivityAt: unknown;
			disabledAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetApprovalQueryVariables = Exact<{
	approvalId: string;
}>;

export type GetApprovalQuery = {
	approval: {
		id: string;
		name: string;
		taskId: string;
		agentId: string;
		environmentId: string;
		workspaceId: string;
		requestType: ApprovalRequestType;
		riskLevel: ApprovalRiskLevel;
		status: ApprovalStatus;
		actionDescription: string;
		payloadRef: string;
		approvalWindowExpiresAt: unknown;
		resolvedBy: string | null;
		resolvedAt: unknown;
		notes: string | null;
		createdAt: unknown;
		updatedAt: unknown;
		credentialRequest: {
			reason: string;
			coverage: CoverageStatus;
			statements: Array<{ actions: Array<string>; resources: Array<string> }>;
		} | null;
	} | null;
};

export type GetApprovalsQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisApprovalSortInput>
		| ArtemisApprovalSortInput
		| null
		| undefined;
	where?: ArtemisApprovalFilterInput | null | undefined;
}>;

export type GetApprovalsQuery = {
	approvals: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			taskId: string;
			agentId: string;
			environmentId: string;
			requestType: ApprovalRequestType;
			riskLevel: ApprovalRiskLevel;
			status: ApprovalStatus;
			actionDescription: string;
			approvalWindowExpiresAt: unknown;
			resolvedBy: string | null;
			resolvedAt: unknown;
			workspaceId: string;
			notes: string | null;
			createdAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetAuditLogsQueryVariables = Exact<{
	workspaceId?: string | null | undefined;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisAuditLogSortInput>
		| ArtemisAuditLogSortInput
		| null
		| undefined;
	where?: ArtemisAuditLogFilterInput | null | undefined;
}>;

export type GetAuditLogsQuery = {
	auditLogs: {
		totalCount: number;
		nodes: Array<{
			id: string;
			occurredAt: unknown;
			action: AuditAction;
			entityType: string;
			entityId: string;
			organizationId: string;
			workspaceId: string | null;
			actorUserId: string | null;
			actorClientId: string | null;
			actorSubject: string;
			before: string | null;
			after: string | null;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetWorkspaceMetricsQueryVariables = Exact<{
	workspaceId: string;
}>;

export type GetWorkspaceMetricsQuery = {
	workspaceMetrics: {
		totalTasks: number;
		activeTasks: number;
		completedTasks: number;
		failedTasks: number;
		pendingApprovals: number;
		memberCount: number;
		totalInputTokens: unknown;
		totalOutputTokens: unknown;
		totalTimeSpentSeconds: number;
	};
};

export type GetOrganizationMetricsQueryVariables = Exact<{
	[key: string]: never;
}>;

export type GetOrganizationMetricsQuery = {
	organizationMetrics: {
		userCount: number;
		workspaceCount: number;
		taskCount: number;
		agentCount: number;
		totalUsers: number;
		totalAgents: number;
		maxActiveUsers: number | null;
		maxWorkspaces: number | null;
		maxAgents: number | null;
		environmentCount: number;
		maxEnvironments: number | null;
		usage: {
			organizationId: string;
			periodStart: unknown;
			periodEnd: unknown;
			totalObservedInputTokens: unknown;
			totalObservedOutputTokens: unknown;
		};
	};
};

export type GetWorkspaceMembersQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisWorkspaceMemberSortInput>
		| ArtemisWorkspaceMemberSortInput
		| null
		| undefined;
	where?: ArtemisWorkspaceMemberFilterInput | null | undefined;
}>;

export type GetWorkspaceMembersQuery = {
	workspaceMembers: {
		totalCount: number;
		nodes: Array<{
			id: string;
			workspaceId: string;
			userId: string;
			role: WorkspaceRole;
			createdAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetJobQueryVariables = Exact<{
	jobId: string;
}>;

export type GetJobQuery = {
	job: {
		id: string;
		name: string;
		description: string;
		projectId: string | null;
		workspaceId: string;
		schedule: string;
		timezone: string;
		deploymentIds: Array<string>;
		environmentIds: Array<string>;
		instructions: string | null;
		agentId: string;
		status: JobStatus;
		lastFiredAt: unknown;
		nextFireAt: unknown;
		createdAt: unknown;
	} | null;
};

export type GetJobsQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?: Array<ArtemisJobSortInput> | ArtemisJobSortInput | null | undefined;
	where?: ArtemisJobFilterInput | null | undefined;
}>;

export type GetJobsQuery = {
	jobs: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			description: string;
			projectId: string | null;
			workspaceId: string;
			schedule: string;
			timezone: string;
			status: JobStatus;
			agentId: string;
			lastFiredAt: unknown;
			nextFireAt: unknown;
			createdAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetJobRunQueryVariables = Exact<{
	jobRunId: string;
}>;

export type GetJobRunQuery = {
	jobRun: {
		id: string;
		jobId: string;
		taskId: string | null;
		projectId: string | null;
		triggerType: JobRunTriggerType;
		firedAt: unknown;
		completedAt: unknown;
		workspaceId: string;
		task: { id: string; status: TaskStatus } | null;
	} | null;
};

export type GetJobRunsQueryVariables = Exact<{
	jobId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisJobRunSortInput>
		| ArtemisJobRunSortInput
		| null
		| undefined;
	where?: ArtemisJobRunFilterInput | null | undefined;
}>;

export type GetJobRunsQuery = {
	jobRuns: {
		totalCount: number;
		nodes: Array<{
			id: string;
			jobId: string;
			taskId: string | null;
			projectId: string | null;
			triggerType: JobRunTriggerType;
			firedAt: unknown;
			completedAt: unknown;
			workspaceId: string;
			task: { id: string; status: TaskStatus } | null;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetReportsQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisReportSortInput>
		| ArtemisReportSortInput
		| null
		| undefined;
	where?: ArtemisReportFilterInput | null | undefined;
}>;

export type GetReportsQuery = {
	reports: {
		totalCount: number;
		nodes: Array<{
			id: string;
			title: string;
			type: ReportType;
			status: ReportStatus;
			ownerId: string;
			createdBy: string;
			projectIds: Array<string>;
			workspaceId: string;
			summary: string | null;
			createdAt: unknown;
			updatedAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetReportQueryVariables = Exact<{
	reportId: string;
}>;

export type GetReportQuery = {
	report: {
		id: string;
		title: string;
		type: ReportType;
		status: ReportStatus;
		ownerId: string;
		createdBy: string;
		body: string;
		projectIds: Array<string>;
		workspaceId: string;
		summary: string | null;
		createdAt: unknown;
		updatedAt: unknown;
		assets: Array<{
			id: string;
			name: string;
			description: string;
			mediaType: string;
			category: AssetCategory;
			size: unknown;
			summary: string | null;
			createdAt: unknown;
		}>;
	} | null;
};

export type GetTaskBlastRadiusQueryVariables = Exact<{
	input: TaskBlastRadiusInput;
}>;

export type GetTaskBlastRadiusQuery = {
	taskBlastRadius: { band: BlastRadius; flags: Array<string> } | null;
};

export type GetGitProviderConnectionQueryVariables = Exact<{
	[key: string]: never;
}>;

export type GetGitProviderConnectionQuery = {
	gitProviderConnection: {
		id: string;
		provider: GitProviderType;
		installationId: string;
		accountLogin: string;
		accountType: string;
		createdByUserId: string;
	} | null;
};

export type GetGitProviderConnectionRepositoriesQueryVariables = Exact<{
	workspaceId: string;
	cursor?: string | null | undefined;
}>;

export type GetGitProviderConnectionRepositoriesQuery = {
	gitProviderConnectionRepositories: {
		nextCursor: string | null;
		repositories: Array<{
			id: string;
			name: string;
			fullName: string;
			private: boolean;
			ownerLogin: string;
			ownerType: string;
		}>;
	};
};

export type GetGitProviderRepositoryCommitsQueryVariables = Exact<{
	projectRepositoryId: string;
	cursor?: string | null | undefined;
}>;

export type GetGitProviderRepositoryCommitsQuery = {
	gitProviderRepositoryCommits: {
		nextCursor: string | null;
		commits: Array<{
			sha: string;
			message: string;
			authorName: string | null;
			authoredAt: unknown;
			htmlUrl: string;
		}>;
	};
};

export type GetGitProviderRepositoryBranchesQueryVariables = Exact<{
	projectRepositoryId: string;
	cursor?: string | null | undefined;
}>;

export type GetGitProviderRepositoryBranchesQuery = {
	gitProviderRepositoryBranches: {
		nextCursor: string | null;
		branches: Array<{ name: string; isDefault: boolean }>;
	};
};

export type GetProjectRepositoriesQueryVariables = Exact<{
	projectId: string;
}>;

export type GetProjectRepositoriesQuery = {
	projectRepositories: Array<{
		id: string;
		projectId: string;
		gitProviderConnectionId: string;
		provider: string;
		repositoryFullName: string;
		repositoryExternalId: string;
		defaultBranch: string | null;
	}>;
};

export type GetProjectCatalogGraphQueryVariables = Exact<{
	projectId: string;
	refresh?: boolean;
}>;

export type GetProjectCatalogGraphQuery = {
	projectCatalogGraph: {
		entities: Array<{
			ref: string;
			kind: string;
			namespace: string;
			name: string;
			title: string | null;
			description: string | null;
			type: string | null;
			lifecycle: string | null;
			owner: string | null;
			tags: Array<string>;
			projectRepositoryId: string | null;
			repositoryFullName: string | null;
			isExternal: boolean;
		}>;
		relations: Array<{
			sourceRef: string;
			targetRef: string;
			type: CatalogRelationType;
		}>;
		repositories: Array<{
			projectRepositoryId: string;
			repositoryFullName: string;
			status: CatalogFileStatus;
			path: string | null;
			entityCount: number;
			message: string | null;
		}>;
	};
};

export type GetOrganizationAppsQueryVariables = Exact<{ [key: string]: never }>;

export type GetOrganizationAppsQuery = {
	organizationApps: Array<{
		id: string;
		kind: AppKind;
		status: AppStatus;
		clientId: string | null;
		clientSecretExpiresAt: unknown;
		lastSeenAt: unknown;
		createdAt: unknown;
		workspaces: Array<{ id: string; name: string }>;
	}>;
};

export type GetEnabledAppKindsQueryVariables = Exact<{ [key: string]: never }>;

export type GetEnabledAppKindsQuery = { enabledAppKinds: Array<AppKind> };

export type GetWorkspaceAppsQueryVariables = Exact<{
	workspaceId: string;
}>;

export type GetWorkspaceAppsQuery = {
	workspaceApps: Array<{
		id: string;
		kind: AppKind;
		status: AppStatus;
		workspaceId: string;
		createdAt: unknown;
	}>;
};

export type GetWebhookQueryVariables = Exact<{
	webhookId: string;
}>;

export type GetWebhookQuery = {
	webhook: {
		id: string;
		name: string;
		description: string;
		action: WebhookAction;
		status: WebhookStatus;
		workspaceId: string;
		agentId: string | null;
		instructions: string | null;
		projectId: string | null;
		deploymentId: string | null;
		secretRotatedAt: unknown;
		lastInvokedAt: unknown;
		createdBy: string | null;
		createdAt: unknown;
		updatedAt: unknown;
	} | null;
};

export type GetWebhooksQueryVariables = Exact<{
	workspaceId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisWebhookSortInput>
		| ArtemisWebhookSortInput
		| null
		| undefined;
	where?: ArtemisWebhookFilterInput | null | undefined;
}>;

export type GetWebhooksQuery = {
	webhooks: {
		totalCount: number;
		nodes: Array<{
			id: string;
			name: string;
			description: string;
			action: WebhookAction;
			status: WebhookStatus;
			workspaceId: string;
			agentId: string | null;
			projectId: string | null;
			deploymentId: string | null;
			lastInvokedAt: unknown;
			createdAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetWebhookDeliveriesQueryVariables = Exact<{
	webhookId: string;
	first?: number | null | undefined;
	after?: string | null | undefined;
	last?: number | null | undefined;
	before?: string | null | undefined;
	order?:
		| Array<ArtemisWebhookDeliverySortInput>
		| ArtemisWebhookDeliverySortInput
		| null
		| undefined;
	where?: ArtemisWebhookDeliveryFilterInput | null | undefined;
}>;

export type GetWebhookDeliveriesQuery = {
	webhookDeliveries: {
		totalCount: number;
		nodes: Array<{
			id: string;
			status: WebhookDeliveryStatus;
			projectId: string | null;
			deploymentId: string | null;
			taskId: string | null;
			error: string | null;
			payload: string | null;
			sourceIp: string | null;
			userAgent: string | null;
			receivedAt: unknown;
		}> | null;
		pageInfo: {
			hasNextPage: boolean;
			hasPreviousPage: boolean;
			startCursor: string | null;
			endCursor: string | null;
		};
	} | null;
};

export type GetSignalsQueryVariables = Exact<{
	projectId: string;
	deploymentId?: string | null | undefined;
}>;

export type GetSignalsQuery = {
	signals: Array<{
		id: string;
		projectId: string;
		deploymentId: string | null;
		environmentId: string | null;
		kind: SignalKind;
		source: SignalSource;
		externalId: string;
		name: string;
		description: string | null;
		origin: SignalOrigin;
		createdAt: unknown;
		keyMetric: boolean;
		config: Array<{ key: string; value: string }>;
		thresholds: {
			warning: number | null;
			critical: number | null;
			direction: ThresholdDirection;
		} | null;
	}>;
};

export type GetSignalStatusesQueryVariables = Exact<{
	projectId: string;
	deploymentId?: string | null | undefined;
}>;

export type GetSignalStatusesQuery = {
	signals: Array<{
		id: string;
		status: {
			state: SignalState;
			rawState: string | null;
			reason: string | null;
			url: string | null;
			error: string | null;
			observedAt: unknown;
			value: number | null;
			unit: string | null;
			previousValue: number | null;
			previousLabel: string | null;
			series: Array<number> | null;
			asOf: unknown;
		};
	}>;
};

export type GetSignalQueryVariables = Exact<{
	signalId: string;
}>;

export type GetSignalQuery = {
	signal: {
		id: string;
		projectId: string;
		deploymentId: string | null;
		environmentId: string | null;
		kind: SignalKind;
		source: SignalSource;
		externalId: string;
		name: string;
		description: string | null;
		origin: SignalOrigin;
		createdAt: unknown;
		keyMetric: boolean;
		config: Array<{ key: string; value: string }>;
		thresholds: {
			warning: number | null;
			critical: number | null;
			direction: ThresholdDirection;
		} | null;
		status: {
			state: SignalState;
			rawState: string | null;
			reason: string | null;
			url: string | null;
			error: string | null;
			observedAt: unknown;
			value: number | null;
			unit: string | null;
			previousValue: number | null;
			previousLabel: string | null;
			series: Array<number> | null;
			asOf: unknown;
		};
	} | null;
};

export type GetSignalSourcesQueryVariables = Exact<{
	workspaceId: string;
}>;

export type GetSignalSourcesQuery = {
	signalSources: Array<{
		source: SignalSource;
		kind: SignalKind;
		displayName: string;
		environmentProvider: EnvironmentProvider | null;
		externalIdLabel: string | null;
		available: boolean;
		unavailableReason: string | null;
		configFields: Array<{
			key: string;
			label: string;
			required: boolean;
			placeholder: string | null;
			options: Array<string> | null;
			defaultValue: string | null;
		}>;
	}>;
};

export type GetSignalCandidatesQueryVariables = Exact<{
	input: SignalCandidatesInput;
}>;

export type GetSignalCandidatesQuery = {
	signalCandidates: Array<{
		externalId: string;
		name: string;
		description: string | null;
		status: { state: SignalState; rawState: string | null } | null;
		config: Array<{ key: string; value: string }> | null;
	}>;
};

export type OnChangeSubscriptionVariables = Exact<{ [key: string]: never }>;

export type OnChangeSubscription = {
	onChange: {
		entityType: string;
		action: string;
		entityId: string;
		organizationId: string;
		workspaceId: string | null;
		status: string | null;
		statusChanged: boolean;
		actorUserId: string | null;
	};
};

export class TypedDocumentString<TResult, TVariables>
	extends String
	implements DocumentTypeDecoration<TResult, TVariables>
{
	__apiType?: NonNullable<
		DocumentTypeDecoration<TResult, TVariables>["__apiType"]
	>;
	private value: string;
	public __meta__?: Record<string, any> | undefined;

	constructor(value: string, __meta__?: Record<string, any> | undefined) {
		super(value);
		this.value = value;
		this.__meta__ = __meta__;
	}

	override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
		return this.value;
	}
}

export const ClaimOrganizationDocument = new TypedDocumentString(`
    mutation ClaimOrganization($input: ClaimOrganizationInput!) {
  claimOrganization(input: $input) {
    organization {
      id
      name
      slug
    }
  }
}
    `) as unknown as TypedDocumentString<
	ClaimOrganizationMutation,
	ClaimOrganizationMutationVariables
>;
export const UpdateOrganizationDocument = new TypedDocumentString(`
    mutation UpdateOrganization($input: UpdateOrganizationInput!) {
  updateOrganization(input: $input) {
    organization {
      id
      name
      organizationId
      createdAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateOrganizationMutation,
	UpdateOrganizationMutationVariables
>;
export const DeactivateAccountDocument = new TypedDocumentString(`
    mutation DeactivateAccount {
  deactivateAccount {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeactivateAccountMutation,
	DeactivateAccountMutationVariables
>;
export const AddOrganizationAdminDocument = new TypedDocumentString(`
    mutation AddOrganizationAdmin($userId: String!) {
  addOrganizationAdmin(userId: $userId) {
    organizationAdmin {
      id
      userId
    }
  }
}
    `) as unknown as TypedDocumentString<
	AddOrganizationAdminMutation,
	AddOrganizationAdminMutationVariables
>;
export const RemoveOrganizationAdminDocument = new TypedDocumentString(`
    mutation RemoveOrganizationAdmin($userId: String!) {
  removeOrganizationAdmin(userId: $userId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	RemoveOrganizationAdminMutation,
	RemoveOrganizationAdminMutationVariables
>;
export const AddOrganizationDomainDocument = new TypedDocumentString(`
    mutation AddOrganizationDomain($domain: String!) {
  addOrganizationDomain(domain: $domain) {
    organizationDomain {
      id
      domain
      organizationId
      createdAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	AddOrganizationDomainMutation,
	AddOrganizationDomainMutationVariables
>;
export const RemoveOrganizationDomainDocument = new TypedDocumentString(`
    mutation RemoveOrganizationDomain($domainId: String!) {
  removeOrganizationDomain(domainId: $domainId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	RemoveOrganizationDomainMutation,
	RemoveOrganizationDomainMutationVariables
>;
export const RequestOrganizationDomainVerificationDocument =
	new TypedDocumentString(`
    mutation RequestOrganizationDomainVerification($domainId: String!) {
  requestOrganizationDomainVerification(domainId: $domainId) {
    organizationDomain {
      id
      domain
      verificationToken
      verifiedAt
    }
  }
}
    `) as unknown as TypedDocumentString<
		RequestOrganizationDomainVerificationMutation,
		RequestOrganizationDomainVerificationMutationVariables
	>;
export const VerifyOrganizationDomainDocument = new TypedDocumentString(`
    mutation VerifyOrganizationDomain($domainId: String!) {
  verifyOrganizationDomain(domainId: $domainId) {
    organizationDomain {
      id
      domain
      verifiedAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	VerifyOrganizationDomainMutation,
	VerifyOrganizationDomainMutationVariables
>;
export const LinkAwsMarketplaceSubscriptionDocument = new TypedDocumentString(`
    mutation LinkAWSMarketplaceSubscription($claimToken: String!) {
  linkAWSMarketplaceSubscription(claimToken: $claimToken) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	LinkAwsMarketplaceSubscriptionMutation,
	LinkAwsMarketplaceSubscriptionMutationVariables
>;
export const CreateStripeCheckoutSessionDocument = new TypedDocumentString(`
    mutation CreateStripeCheckoutSession {
  createStripeCheckoutSession {
    url
  }
}
    `) as unknown as TypedDocumentString<
	CreateStripeCheckoutSessionMutation,
	CreateStripeCheckoutSessionMutationVariables
>;
export const CreateStripeBillingPortalSessionDocument =
	new TypedDocumentString(`
    mutation CreateStripeBillingPortalSession {
  createStripeBillingPortalSession {
    url
  }
}
    `) as unknown as TypedDocumentString<
		CreateStripeBillingPortalSessionMutation,
		CreateStripeBillingPortalSessionMutationVariables
	>;
export const CreateTaskDocument = new TypedDocumentString(`
    mutation CreateTask($input: CreateTaskInput!) {
  createTask(input: $input) {
    task {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateTaskMutation,
	CreateTaskMutationVariables
>;
export const UpdateTaskDocument = new TypedDocumentString(`
    mutation UpdateTask($taskId: String!, $input: UpdateTaskInput!) {
  updateTask(taskId: $taskId, input: $input) {
    task {
      id
      status
      result
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateTaskMutation,
	UpdateTaskMutationVariables
>;
export const DeleteTaskDocument = new TypedDocumentString(`
    mutation DeleteTask($taskId: String!) {
  deleteTask(taskId: $taskId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteTaskMutation,
	DeleteTaskMutationVariables
>;
export const CreateTaskTemplateDocument = new TypedDocumentString(`
    mutation CreateTaskTemplate($input: CreateTaskTemplateInput!) {
  createTaskTemplate(input: $input) {
    taskTemplate {
      id
      name
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateTaskTemplateMutation,
	CreateTaskTemplateMutationVariables
>;
export const UpdateTaskTemplateDocument = new TypedDocumentString(`
    mutation UpdateTaskTemplate($taskTemplateId: String!, $input: UpdateTaskTemplateInput!) {
  updateTaskTemplate(taskTemplateId: $taskTemplateId, input: $input) {
    taskTemplate {
      id
      name
      updatedAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateTaskTemplateMutation,
	UpdateTaskTemplateMutationVariables
>;
export const DeleteTaskTemplateDocument = new TypedDocumentString(`
    mutation DeleteTaskTemplate($taskTemplateId: String!) {
  deleteTaskTemplate(taskTemplateId: $taskTemplateId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteTaskTemplateMutation,
	DeleteTaskTemplateMutationVariables
>;
export const ReportTaskUsageDocument = new TypedDocumentString(`
    mutation ReportTaskUsage($taskId: String!, $inputTokens: Int!, $outputTokens: Int!) {
  reportTaskUsage(
    taskId: $taskId
    inputTokens: $inputTokens
    outputTokens: $outputTokens
  ) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	ReportTaskUsageMutation,
	ReportTaskUsageMutationVariables
>;
export const SetUserDisabledDocument = new TypedDocumentString(`
    mutation SetUserDisabled($userId: String!, $disabled: Boolean!) {
  setUserDisabled(userId: $userId, disabled: $disabled) {
    user {
      id
      disabledAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	SetUserDisabledMutation,
	SetUserDisabledMutationVariables
>;
export const CancelTaskDocument = new TypedDocumentString(`
    mutation CancelTask($taskId: String!) {
  cancelTask(taskId: $taskId) {
    task {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	CancelTaskMutation,
	CancelTaskMutationVariables
>;
export const SubmitTaskInputDocument = new TypedDocumentString(`
    mutation SubmitTaskInput($taskId: String!, $text: String!) {
  submitTaskInput(taskId: $taskId, text: $text) {
    task {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	SubmitTaskInputMutation,
	SubmitTaskInputMutationVariables
>;
export const AppendTaskProgressDocument = new TypedDocumentString(`
    mutation AppendTaskProgress($taskId: String!, $text: String!) {
  appendTaskProgress(taskId: $taskId, text: $text) {
    task {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	AppendTaskProgressMutation,
	AppendTaskProgressMutationVariables
>;
export const DispatchTaskDocument = new TypedDocumentString(`
    mutation DispatchTask($taskId: String!) {
  dispatchTask(taskId: $taskId) {
    task {
      id
      status
      statusReason
    }
  }
}
    `) as unknown as TypedDocumentString<
	DispatchTaskMutation,
	DispatchTaskMutationVariables
>;
export const ReopenTaskDocument = new TypedDocumentString(`
    mutation ReopenTask($taskId: String!) {
  reopenTask(taskId: $taskId) {
    task {
      id
      status
      result
    }
  }
}
    `) as unknown as TypedDocumentString<
	ReopenTaskMutation,
	ReopenTaskMutationVariables
>;
export const PostTaskMessageDocument = new TypedDocumentString(`
    mutation PostTaskMessage($taskId: String!, $text: String!) {
  postTaskMessage(taskId: $taskId, text: $text) {
    task {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	PostTaskMessageMutation,
	PostTaskMessageMutationVariables
>;
export const CreateEnvironmentDocument = new TypedDocumentString(`
    mutation CreateEnvironment($input: CreateEnvironmentInput!) {
  createEnvironment(input: $input) {
    environment {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateEnvironmentMutation,
	CreateEnvironmentMutationVariables
>;
export const UpdateEnvironmentDocument = new TypedDocumentString(`
    mutation UpdateEnvironment($environmentId: String!, $input: UpdateEnvironmentInput!) {
  updateEnvironment(environmentId: $environmentId, input: $input) {
    environment {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateEnvironmentMutation,
	UpdateEnvironmentMutationVariables
>;
export const DeleteEnvironmentDocument = new TypedDocumentString(`
    mutation DeleteEnvironment($environmentId: String!) {
  deleteEnvironment(environmentId: $environmentId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteEnvironmentMutation,
	DeleteEnvironmentMutationVariables
>;
export const CreateWorkspaceDocument = new TypedDocumentString(`
    mutation CreateWorkspace($input: CreateWorkspaceInput!) {
  createWorkspace(input: $input) {
    workspace {
      id
      name
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateWorkspaceMutation,
	CreateWorkspaceMutationVariables
>;
export const UpdateWorkspaceDocument = new TypedDocumentString(`
    mutation UpdateWorkspace($workspaceId: String!, $input: UpdateWorkspaceInput!) {
  updateWorkspace(workspaceId: $workspaceId, input: $input) {
    workspace {
      id
      name
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateWorkspaceMutation,
	UpdateWorkspaceMutationVariables
>;
export const DeleteWorkspaceDocument = new TypedDocumentString(`
    mutation DeleteWorkspace($workspaceId: String!) {
  deleteWorkspace(workspaceId: $workspaceId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteWorkspaceMutation,
	DeleteWorkspaceMutationVariables
>;
export const CreateProjectDocument = new TypedDocumentString(`
    mutation CreateProject($input: CreateProjectInput!) {
  createProject(input: $input) {
    project {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateProjectMutation,
	CreateProjectMutationVariables
>;
export const UpdateProjectDocument = new TypedDocumentString(`
    mutation UpdateProject($projectId: String!, $input: UpdateProjectInput!) {
  updateProject(projectId: $projectId, input: $input) {
    project {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateProjectMutation,
	UpdateProjectMutationVariables
>;
export const DeleteProjectDocument = new TypedDocumentString(`
    mutation DeleteProject($projectId: String!) {
  deleteProject(projectId: $projectId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteProjectMutation,
	DeleteProjectMutationVariables
>;
export const ArchiveProjectDocument = new TypedDocumentString(`
    mutation ArchiveProject($projectId: String!) {
  archiveProject(projectId: $projectId) {
    project {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	ArchiveProjectMutation,
	ArchiveProjectMutationVariables
>;
export const CreateAgentDocument = new TypedDocumentString(`
    mutation CreateAgent($input: CreateAgentInput!) {
  createAgent(input: $input) {
    agent {
      id
      kind
      clientId
    }
    credentials {
      clientId
      clientSecret
      expiresAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateAgentMutation,
	CreateAgentMutationVariables
>;
export const RotateAgentCredentialDocument = new TypedDocumentString(`
    mutation RotateAgentCredential($agentId: String!, $workspaceId: String!, $clientSecretExpiresAt: DateTime) {
  rotateAgentCredential(
    agentId: $agentId
    workspaceId: $workspaceId
    clientSecretExpiresAt: $clientSecretExpiresAt
  ) {
    agent {
      id
      clientId
      clientSecretExpiresAt
    }
    credentials {
      clientId
      clientSecret
      expiresAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	RotateAgentCredentialMutation,
	RotateAgentCredentialMutationVariables
>;
export const UpdateAgentDocument = new TypedDocumentString(`
    mutation UpdateAgent($agentId: String!, $workspaceId: String!, $input: UpdateAgentInput!) {
  updateAgent(agentId: $agentId, workspaceId: $workspaceId, input: $input) {
    agent {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateAgentMutation,
	UpdateAgentMutationVariables
>;
export const DeleteAgentDocument = new TypedDocumentString(`
    mutation DeleteAgent($agentId: String!, $workspaceId: String!, $force: Boolean! = false) {
  deleteAgent(agentId: $agentId, workspaceId: $workspaceId, force: $force) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteAgentMutation,
	DeleteAgentMutationVariables
>;
export const SetAgentRuntimeVersionDocument = new TypedDocumentString(`
    mutation SetAgentRuntimeVersion($agentId: String!, $workspaceId: String!, $version: String!) {
  setAgentRuntimeVersion(
    agentId: $agentId
    workspaceId: $workspaceId
    version: $version
  ) {
    agent {
      id
      runtimeVersion
      health
    }
  }
}
    `) as unknown as TypedDocumentString<
	SetAgentRuntimeVersionMutation,
	SetAgentRuntimeVersionMutationVariables
>;
export const PingAgentDocument = new TypedDocumentString(`
    mutation PingAgent($agentId: String!, $workspaceId: String!) {
  pingAgent(agentId: $agentId, workspaceId: $workspaceId) {
    health {
      isHealthy
      checkedAt
      errorMessage
    }
  }
}
    `) as unknown as TypedDocumentString<
	PingAgentMutation,
	PingAgentMutationVariables
>;
export const RegisterAssetDocument = new TypedDocumentString(`
    mutation RegisterAsset($input: RegisterAssetInput!) {
  registerAsset(input: $input) {
    asset {
      id
      name
      mediaType
      category
      state
      stagingKey
      storageKey
      parentId
      parentType
      createdAt
    }
    upload {
      url
      fields {
        name
        value
      }
    }
  }
}
    `) as unknown as TypedDocumentString<
	RegisterAssetMutation,
	RegisterAssetMutationVariables
>;
export const ConfirmAssetDocument = new TypedDocumentString(`
    mutation ConfirmAsset($assetId: String!) {
  confirmAsset(assetId: $assetId) {
    asset {
      id
      state
      size
      storageKey
    }
  }
}
    `) as unknown as TypedDocumentString<
	ConfirmAssetMutation,
	ConfirmAssetMutationVariables
>;
export const DeleteAssetDocument = new TypedDocumentString(`
    mutation DeleteAsset($assetId: String!) {
  deleteAsset(assetId: $assetId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteAssetMutation,
	DeleteAssetMutationVariables
>;
export const CreateDeploymentDocument = new TypedDocumentString(`
    mutation CreateDeployment($input: CreateDeploymentInput!) {
  createDeployment(input: $input) {
    deployment {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateDeploymentMutation,
	CreateDeploymentMutationVariables
>;
export const RefreshDeploymentDocument = new TypedDocumentString(`
    mutation RefreshDeployment($deploymentId: String!) {
  refreshDeployment(deploymentId: $deploymentId) {
    deployment {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	RefreshDeploymentMutation,
	RefreshDeploymentMutationVariables
>;
export const RequestTerraformUploadDocument = new TypedDocumentString(`
    mutation RequestTerraformUpload($workspaceId: String!, $filenames: [String!]!) {
  requestTerraformUpload(workspaceId: $workspaceId, filenames: $filenames) {
    uploads {
      key
      upload {
        url
        fields {
          name
          value
        }
      }
    }
  }
}
    `) as unknown as TypedDocumentString<
	RequestTerraformUploadMutation,
	RequestTerraformUploadMutationVariables
>;
export const AnalyzeTerraformDeploymentDocument = new TypedDocumentString(`
    mutation AnalyzeTerraformDeployment($deploymentId: String!, $input: AnalyzeTerraformInput!) {
  analyzeTerraformDeployment(deploymentId: $deploymentId, input: $input) {
    deployment {
      id
    }
  }
}
    `) as unknown as TypedDocumentString<
	AnalyzeTerraformDeploymentMutation,
	AnalyzeTerraformDeploymentMutationVariables
>;
export const DeleteDeploymentDocument = new TypedDocumentString(`
    mutation DeleteDeployment($deploymentId: String!) {
  deleteDeployment(deploymentId: $deploymentId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteDeploymentMutation,
	DeleteDeploymentMutationVariables
>;
export const ResolveApprovalDocument = new TypedDocumentString(`
    mutation ResolveApproval($approvalId: String!, $input: ResolveApprovalInput!) {
  resolveApproval(approvalId: $approvalId, input: $input) {
    approval {
      id
      status
      resolvedBy
      resolvedAt
      notes
    }
  }
}
    `) as unknown as TypedDocumentString<
	ResolveApprovalMutation,
	ResolveApprovalMutationVariables
>;
export const RequestCredentialEscalationDocument = new TypedDocumentString(`
    mutation RequestCredentialEscalation($input: RequestCredentialEscalationInput!) {
  requestCredentialEscalation(input: $input) {
    approval {
      id
      name
      status
      taskId
      environmentId
      riskLevel
      requestType
      actionDescription
      approvalWindowExpiresAt
      createdAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	RequestCredentialEscalationMutation,
	RequestCredentialEscalationMutationVariables
>;
export const IssueCredentialsDocument = new TypedDocumentString(`
    mutation IssueCredentials($environmentId: String!, $approvalId: String) {
  issueCredentials(environmentId: $environmentId, approvalId: $approvalId) {
    credentials {
      accessKeyId
      secretAccessKey
      sessionToken
      expiration
      region
    }
  }
}
    `) as unknown as TypedDocumentString<
	IssueCredentialsMutation,
	IssueCredentialsMutationVariables
>;
export const AddWorkspaceMemberDocument = new TypedDocumentString(`
    mutation AddWorkspaceMember($input: AddWorkspaceMemberInput!) {
  addWorkspaceMember(input: $input) {
    workspaceMember {
      id
      workspaceId
      userId
      role
      createdAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	AddWorkspaceMemberMutation,
	AddWorkspaceMemberMutationVariables
>;
export const RemoveWorkspaceMemberDocument = new TypedDocumentString(`
    mutation RemoveWorkspaceMember($workspaceId: String!, $userId: String!) {
  removeWorkspaceMember(workspaceId: $workspaceId, userId: $userId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	RemoveWorkspaceMemberMutation,
	RemoveWorkspaceMemberMutationVariables
>;
export const UpdateWorkspaceMemberRoleDocument = new TypedDocumentString(`
    mutation UpdateWorkspaceMemberRole($input: UpdateMemberRoleInput!) {
  updateWorkspaceMemberRole(input: $input) {
    workspaceMember {
      id
      userId
      role
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateWorkspaceMemberRoleMutation,
	UpdateWorkspaceMemberRoleMutationVariables
>;
export const CreateJobDocument = new TypedDocumentString(`
    mutation CreateJob($input: CreateJobInput!) {
  createJob(input: $input) {
    job {
      id
      name
      description
      projectId
      workspaceId
      schedule
      timezone
      status
      lastFiredAt
      nextFireAt
      createdAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateJobMutation,
	CreateJobMutationVariables
>;
export const UpdateJobDocument = new TypedDocumentString(`
    mutation UpdateJob($jobId: String!, $input: UpdateJobInput!) {
  updateJob(jobId: $jobId, input: $input) {
    job {
      id
      name
      description
      projectId
      workspaceId
      schedule
      timezone
      status
      lastFiredAt
      nextFireAt
      createdAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateJobMutation,
	UpdateJobMutationVariables
>;
export const PauseJobDocument = new TypedDocumentString(`
    mutation PauseJob($jobId: String!) {
  pauseJob(jobId: $jobId) {
    job {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	PauseJobMutation,
	PauseJobMutationVariables
>;
export const ResumeJobDocument = new TypedDocumentString(`
    mutation ResumeJob($jobId: String!) {
  resumeJob(jobId: $jobId) {
    job {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	ResumeJobMutation,
	ResumeJobMutationVariables
>;
export const DeleteJobDocument = new TypedDocumentString(`
    mutation DeleteJob($jobId: String!) {
  deleteJob(jobId: $jobId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteJobMutation,
	DeleteJobMutationVariables
>;
export const TriggerJobRunDocument = new TypedDocumentString(`
    mutation TriggerJobRun($jobId: String!) {
  triggerJobRun(jobId: $jobId) {
    jobRun {
      id
      jobId
      taskId
      projectId
      triggerType
      firedAt
      completedAt
      task {
        id
        status
      }
    }
  }
}
    `) as unknown as TypedDocumentString<
	TriggerJobRunMutation,
	TriggerJobRunMutationVariables
>;
export const CreateReportDocument = new TypedDocumentString(`
    mutation CreateReport($input: CreateReportInput!) {
  createReport(input: $input) {
    report {
      id
      title
      type
      status
      ownerId
      createdBy
      projectIds
      workspaceId
      createdAt
      updatedAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateReportMutation,
	CreateReportMutationVariables
>;
export const UpdateReportDocument = new TypedDocumentString(`
    mutation UpdateReport($reportId: String!, $input: UpdateReportInput!) {
  updateReport(reportId: $reportId, input: $input) {
    report {
      id
      title
      type
      status
      ownerId
      body
      projectIds
      workspaceId
      createdAt
      updatedAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateReportMutation,
	UpdateReportMutationVariables
>;
export const DeleteReportDocument = new TypedDocumentString(`
    mutation DeleteReport($reportId: String!) {
  deleteReport(reportId: $reportId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteReportMutation,
	DeleteReportMutationVariables
>;
export const CreateGitProviderConnectionAuthUrlDocument =
	new TypedDocumentString(`
    mutation CreateGitProviderConnectionAuthUrl {
  createGitProviderConnectionAuthUrl {
    url
    installUrl
  }
}
    `) as unknown as TypedDocumentString<
		CreateGitProviderConnectionAuthUrlMutation,
		CreateGitProviderConnectionAuthUrlMutationVariables
	>;
export const ListGitProviderInstallationsDocument = new TypedDocumentString(`
    mutation ListGitProviderInstallations($state: String!, $code: String!) {
  listGitProviderInstallations(state: $state, code: $code) {
    installationId
    accountLogin
    accountType
    status
    grant
  }
}
    `) as unknown as TypedDocumentString<
	ListGitProviderInstallationsMutation,
	ListGitProviderInstallationsMutationVariables
>;
export const CompleteGitProviderConnectionDocument = new TypedDocumentString(`
    mutation CompleteGitProviderConnection($installationId: String!, $grant: String!) {
  completeGitProviderConnection(installationId: $installationId, grant: $grant) {
    id
    provider
    installationId
    accountLogin
    accountType
    createdByUserId
  }
}
    `) as unknown as TypedDocumentString<
	CompleteGitProviderConnectionMutation,
	CompleteGitProviderConnectionMutationVariables
>;
export const DeleteGitProviderConnectionDocument = new TypedDocumentString(`
    mutation DeleteGitProviderConnection($connectionId: String!) {
  deleteGitProviderConnection(connectionId: $connectionId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteGitProviderConnectionMutation,
	DeleteGitProviderConnectionMutationVariables
>;
export const LinkProjectRepositoryDocument = new TypedDocumentString(`
    mutation LinkProjectRepository($projectId: String!, $gitProviderConnectionId: String!, $repositoryExternalId: String!, $repositoryFullName: String!) {
  linkProjectRepository(
    projectId: $projectId
    gitProviderConnectionId: $gitProviderConnectionId
    repositoryExternalId: $repositoryExternalId
    repositoryFullName: $repositoryFullName
  ) {
    repository {
      id
      projectId
      gitProviderConnectionId
      provider
      repositoryFullName
      repositoryExternalId
      defaultBranch
    }
  }
}
    `) as unknown as TypedDocumentString<
	LinkProjectRepositoryMutation,
	LinkProjectRepositoryMutationVariables
>;
export const UnlinkProjectRepositoryDocument = new TypedDocumentString(`
    mutation UnlinkProjectRepository($projectRepositoryId: String!) {
  unlinkProjectRepository(projectRepositoryId: $projectRepositoryId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	UnlinkProjectRepositoryMutation,
	UnlinkProjectRepositoryMutationVariables
>;
export const UpdateOrganizationAppDocument = new TypedDocumentString(`
    mutation UpdateOrganizationApp($kind: AppKind!, $input: UpdateOrganizationAppInput!) {
  updateOrganizationApp(kind: $kind, input: $input) {
    organizationApp {
      id
      kind
      status
      clientId
      clientSecretExpiresAt
      lastSeenAt
    }
    credentials {
      clientId
      clientSecret
      expiresAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateOrganizationAppMutation,
	UpdateOrganizationAppMutationVariables
>;
export const RotateOrganizationAppCredentialDocument = new TypedDocumentString(`
    mutation RotateOrganizationAppCredential($kind: AppKind!, $clientSecretExpiresAt: DateTime) {
  rotateOrganizationAppCredential(
    kind: $kind
    clientSecretExpiresAt: $clientSecretExpiresAt
  ) {
    organizationApp {
      id
      kind
      clientId
      clientSecretExpiresAt
    }
    credentials {
      clientId
      clientSecret
      expiresAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	RotateOrganizationAppCredentialMutation,
	RotateOrganizationAppCredentialMutationVariables
>;
export const UpdateWorkspaceAppDocument = new TypedDocumentString(`
    mutation UpdateWorkspaceApp($workspaceId: String!, $kind: AppKind!, $input: UpdateWorkspaceAppInput!) {
  updateWorkspaceApp(workspaceId: $workspaceId, kind: $kind, input: $input) {
    workspaceApp {
      id
      kind
      status
      workspaceId
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateWorkspaceAppMutation,
	UpdateWorkspaceAppMutationVariables
>;
export const CreateWebhookDocument = new TypedDocumentString(`
    mutation CreateWebhook($input: CreateWebhookInput!) {
  createWebhook(input: $input) {
    secret
    webhook {
      id
      name
      action
      status
      workspaceId
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateWebhookMutation,
	CreateWebhookMutationVariables
>;
export const UpdateWebhookDocument = new TypedDocumentString(`
    mutation UpdateWebhook($webhookId: String!, $input: UpdateWebhookInput!) {
  updateWebhook(webhookId: $webhookId, input: $input) {
    webhook {
      id
      name
      description
      status
      agentId
      instructions
      projectId
      deploymentId
      updatedAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateWebhookMutation,
	UpdateWebhookMutationVariables
>;
export const RotateWebhookSecretDocument = new TypedDocumentString(`
    mutation RotateWebhookSecret($webhookId: String!) {
  rotateWebhookSecret(webhookId: $webhookId) {
    secret
    webhook {
      id
      secretRotatedAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	RotateWebhookSecretMutation,
	RotateWebhookSecretMutationVariables
>;
export const DeleteWebhookDocument = new TypedDocumentString(`
    mutation DeleteWebhook($webhookId: String!) {
  deleteWebhook(webhookId: $webhookId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteWebhookMutation,
	DeleteWebhookMutationVariables
>;
export const CreateSignalDocument = new TypedDocumentString(`
    mutation CreateSignal($input: CreateSignalInput!) {
  createSignal(input: $input) {
    signal {
      id
      name
      externalId
      deploymentId
      environmentId
      origin
    }
  }
}
    `) as unknown as TypedDocumentString<
	CreateSignalMutation,
	CreateSignalMutationVariables
>;
export const UpsertSignalDocument = new TypedDocumentString(`
    mutation UpsertSignal($input: CreateSignalInput!) {
  upsertSignal(input: $input) {
    created
    signal {
      id
      name
      externalId
      deploymentId
      environmentId
      origin
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpsertSignalMutation,
	UpsertSignalMutationVariables
>;
export const SyncSignalStatusDocument = new TypedDocumentString(`
    mutation SyncSignalStatus($signalId: String!) {
  syncSignalStatus(signalId: $signalId) {
    state
    rawState
    reason
    url
    error
    observedAt
    value
    unit
    previousValue
    previousLabel
    series
    asOf
  }
}
    `) as unknown as TypedDocumentString<
	SyncSignalStatusMutation,
	SyncSignalStatusMutationVariables
>;
export const UpdateSignalDocument = new TypedDocumentString(`
    mutation UpdateSignal($signalId: String!, $input: UpdateSignalInput!) {
  updateSignal(signalId: $signalId, input: $input) {
    signal {
      id
      name
      description
      thresholds {
        warning
        critical
        direction
      }
    }
  }
}
    `) as unknown as TypedDocumentString<
	UpdateSignalMutation,
	UpdateSignalMutationVariables
>;
export const DeleteSignalDocument = new TypedDocumentString(`
    mutation DeleteSignal($signalId: String!) {
  deleteSignal(signalId: $signalId) {
    success
  }
}
    `) as unknown as TypedDocumentString<
	DeleteSignalMutation,
	DeleteSignalMutationVariables
>;
export const OrganizationLookupByDomainDocument = new TypedDocumentString(`
    query OrganizationLookupByDomain($domain: String!) {
  organizationLookupByDomain(domain: $domain) {
    orgId
    orgName
    orgSlug
    hasSso
  }
}
    `) as unknown as TypedDocumentString<
	OrganizationLookupByDomainQuery,
	OrganizationLookupByDomainQueryVariables
>;
export const OrganizationLookupBySlugDocument = new TypedDocumentString(`
    query OrganizationLookupBySlug($slug: String!) {
  organizationLookupBySlug(slug: $slug) {
    orgId
    orgName
    orgSlug
    hasSso
  }
}
    `) as unknown as TypedDocumentString<
	OrganizationLookupBySlugQuery,
	OrganizationLookupBySlugQueryVariables
>;
export const GetOrganizationDocument = new TypedDocumentString(`
    query GetOrganization {
  organization {
    id
    name
    organizationId
    slug
    createdAt
  }
}
    `) as unknown as TypedDocumentString<
	GetOrganizationQuery,
	GetOrganizationQueryVariables
>;
export const GetOrganizationAdminsDocument = new TypedDocumentString(`
    query GetOrganizationAdmins {
  organizationAdmins {
    userId
  }
}
    `) as unknown as TypedDocumentString<
	GetOrganizationAdminsQuery,
	GetOrganizationAdminsQueryVariables
>;
export const GetOrganizationDomainsDocument = new TypedDocumentString(`
    query GetOrganizationDomains {
  organizationDomains {
    id
    domain
    verifiedAt
    verificationToken
  }
}
    `) as unknown as TypedDocumentString<
	GetOrganizationDomainsQuery,
	GetOrganizationDomainsQueryVariables
>;
export const GetPlatformFeaturesDocument = new TypedDocumentString(`
    query GetPlatformFeatures {
  platformFeatures {
    gitHub
    awsMarketplace
    stripeBilling
  }
}
    `) as unknown as TypedDocumentString<
	GetPlatformFeaturesQuery,
	GetPlatformFeaturesQueryVariables
>;
export const GetOrganizationSubscriptionDocument = new TypedDocumentString(`
    query GetOrganizationSubscription {
  organizationSubscription {
    status
    environments
    updatedAt
  }
}
    `) as unknown as TypedDocumentString<
	GetOrganizationSubscriptionQuery,
	GetOrganizationSubscriptionQueryVariables
>;
export const GetOrganizationAwsMarketplaceSubscriptionDocument =
	new TypedDocumentString(`
    query GetOrganizationAWSMarketplaceSubscription {
  organizationAWSMarketplaceSubscription {
    environments
    customerAwsAccountId
  }
}
    `) as unknown as TypedDocumentString<
		GetOrganizationAwsMarketplaceSubscriptionQuery,
		GetOrganizationAwsMarketplaceSubscriptionQueryVariables
	>;
export const GetWorkspacesDocument = new TypedDocumentString(`
    query GetWorkspaces($first: Int = 50, $after: String) {
  workspaces(first: $first, after: $after) {
    nodes {
      id
      name
      organizationId
      createdAt
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetWorkspacesQuery,
	GetWorkspacesQueryVariables
>;
export const GetWorkspaceDocument = new TypedDocumentString(`
    query GetWorkspace($workspaceId: String!) {
  workspace(workspaceId: $workspaceId) {
    id
    name
    organizationId
    agents {
      id
      name
      createdAt
    }
    environments {
      id
      name
      status
      provider
      classification
      createdAt
    }
    projects {
      id
      name
      status
      createdAt
    }
    createdAt
  }
}
    `) as unknown as TypedDocumentString<
	GetWorkspaceQuery,
	GetWorkspaceQueryVariables
>;
export const GetWorkspaceInfoDocument = new TypedDocumentString(`
    query GetWorkspaceInfo($workspaceId: String!) {
  workspace(workspaceId: $workspaceId) {
    id
    name
    organizationId
    createdAt
  }
}
    `) as unknown as TypedDocumentString<
	GetWorkspaceInfoQuery,
	GetWorkspaceInfoQueryVariables
>;
export const GetProjectsDocument = new TypedDocumentString(`
    query GetProjects($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisProjectSortInput!], $where: ArtemisProjectFilterInput) {
  projects(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      description
      status
      type
      summary
      createdAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetProjectsQuery,
	GetProjectsQueryVariables
>;
export const GetProjectDocument = new TypedDocumentString(`
    query GetProject($projectId: String!) {
  project(projectId: $projectId) {
    id
    name
    description
    status
    ownerId
    workspaceId
    links {
      url
      name
      description
      type
    }
    type
    deployments {
      id
      name
      description
      environmentId
      workspaceId
      createdAt
      config {
        region
        stackNames
      }
      terraformConfig {
        sourceNames
        analyzedAt
      }
      graph {
        id
        type
        nodes {
          id
          resourceId
          label
          serviceName
          providerName
        }
        relationships {
          id
          sourceNodeId
          targetNodeId
          type
        }
        isDiscovering
        discoveryError
        lastDiscoveryAt
      }
    }
    assets {
      id
      name
      description
      mediaType
      category
      size
      summary
      createdAt
    }
    reports {
      id
      title
      type
      status
      ownerId
      createdBy
      projectIds
      workspaceId
      summary
      createdAt
      updatedAt
    }
    notes
    summary
    instructions
    createdAt
  }
}
    `) as unknown as TypedDocumentString<
	GetProjectQuery,
	GetProjectQueryVariables
>;
export const GetTasksDocument = new TypedDocumentString(`
    query GetTasks($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisTaskSortInput!], $where: ArtemisTaskFilterInput) {
  tasks(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      summary
      status
      projectId
      workspaceId
      assigneeId
      assigneeType
      verificationCriteria
      verificationStatus
      verificationNotes
      createdAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<GetTasksQuery, GetTasksQueryVariables>;
export const GetTaskDocument = new TypedDocumentString(`
    query GetTask($taskId: String!) {
  task(taskId: $taskId) {
    id
    name
    description
    instructions
    status
    statusReason
    projectId
    workspaceId
    assigneeId
    assigneeType
    result
    verificationCriteria
    verificationStatus
    verificationNotes
    assets {
      id
      name
      description
      mediaType
      category
      size
      summary
      createdAt
    }
    links {
      url
      description
      name
      type
    }
    worklog {
      id
      authorId
      authorType
      type
      text
      createdAt
    }
    summary
    metrics {
      inputTokens
      outputTokens
      timeSpentInSeconds
    }
    deploymentIds
    environmentIds
    createdAt
  }
}
    `) as unknown as TypedDocumentString<GetTaskQuery, GetTaskQueryVariables>;
export const GetTaskTemplatesDocument = new TypedDocumentString(`
    query GetTaskTemplates($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisTaskTemplateSortInput!], $where: ArtemisTaskTemplateFilterInput) {
  taskTemplates(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      description
      instructions
      projectId
      workspaceId
      deploymentIds
      environmentIds
      assigneeId
      assigneeType
      verificationCriteria
      placeholders {
        key
        label
        description
        type
        required
        defaultValue
      }
      createdAt
      updatedAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetTaskTemplatesQuery,
	GetTaskTemplatesQueryVariables
>;
export const GetTaskTemplateDocument = new TypedDocumentString(`
    query GetTaskTemplate($taskTemplateId: String!) {
  taskTemplate(taskTemplateId: $taskTemplateId) {
    id
    name
    description
    instructions
    projectId
    workspaceId
    deploymentIds
    environmentIds
    assigneeId
    assigneeType
    verificationCriteria
    placeholders {
      key
      label
      description
      type
      required
      defaultValue
    }
    createdAt
    updatedAt
  }
}
    `) as unknown as TypedDocumentString<
	GetTaskTemplateQuery,
	GetTaskTemplateQueryVariables
>;
export const GetAgentsDocument = new TypedDocumentString(`
    query GetAgents($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisAgentSortInput!], $where: ArtemisAgentFilterInput) {
  agents(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      role
      kind
      workspaceId
      endpointUrl
      status
      health
      lastSeenAt
      createdAt
      environmentId
      environment {
        id
        name
        provider
        status
      }
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetAgentsQuery,
	GetAgentsQueryVariables
>;
export const GetAgentDocument = new TypedDocumentString(`
    query GetAgent($agentId: String!, $workspaceId: String!) {
  agent(agentId: $agentId, workspaceId: $workspaceId) {
    id
    name
    role
    kind
    workspaceId
    endpointUrl
    clientId
    clientSecretExpiresAt
    status
    health
    lastSeenAt
    createdAt
    environmentId
    environment {
      id
      name
      provider
      status
    }
    deploymentSettings {
      key
      value
    }
    runtimeVersion
    supportsStreaming
    supportsPushNotifications
    ownerId
    skills {
      id
      name
      description
      tags
    }
  }
}
    `) as unknown as TypedDocumentString<GetAgentQuery, GetAgentQueryVariables>;
export const GetAgentDeploymentOptionsDocument = new TypedDocumentString(`
    query GetAgentDeploymentOptions {
  agentDeploymentOptions {
    awsRegions
    runtimeVersions
  }
}
    `) as unknown as TypedDocumentString<
	GetAgentDeploymentOptionsQuery,
	GetAgentDeploymentOptionsQueryVariables
>;
export const GetAssetDownloadUrlDocument = new TypedDocumentString(`
    query GetAssetDownloadUrl($assetId: String!) {
  assetDownloadUrl(assetId: $assetId)
}
    `) as unknown as TypedDocumentString<
	GetAssetDownloadUrlQuery,
	GetAssetDownloadUrlQueryVariables
>;
export const GetEnvironmentsDocument = new TypedDocumentString(`
    query GetEnvironments($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisEnvironmentSortInput!], $where: ArtemisEnvironmentFilterInput) {
  environments(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      status
      provider
      classification
      autoApproveUpTo
      allowAgentsToBeDeployed
      allowAgentsToAccess
      createdAt
      metadata {
        key
        value
      }
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetEnvironmentsQuery,
	GetEnvironmentsQueryVariables
>;
export const GetEnvironmentDocument = new TypedDocumentString(`
    query GetEnvironment($environmentId: String!) {
  environment(environmentId: $environmentId) {
    id
    name
    status
    provider
    classification
    autoApproveUpTo
    allowAgentsToBeDeployed
    allowAgentsToAccess
    ownerId
    workspaceId
    createdAt
    metadata {
      key
      value
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetEnvironmentQuery,
	GetEnvironmentQueryVariables
>;
export const GetEnvironmentDeleteImpactDocument = new TypedDocumentString(`
    query GetEnvironmentDeleteImpact($environmentId: String!) {
  environmentDeleteImpact(environmentId: $environmentId) {
    isBlocked
    deployments {
      id
      name
      projectId
      projectName
    }
    managedAgents {
      id
      name
    }
    jobs {
      id
      name
    }
    taskTemplates {
      id
      name
    }
    externalAgents {
      id
      name
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetEnvironmentDeleteImpactQuery,
	GetEnvironmentDeleteImpactQueryVariables
>;
export const EnvironmentAwsStacksDocument = new TypedDocumentString(`
    query EnvironmentAwsStacks($environmentId: String!, $region: String) {
  environmentAwsStacks(environmentId: $environmentId, region: $region)
}
    `) as unknown as TypedDocumentString<
	EnvironmentAwsStacksQuery,
	EnvironmentAwsStacksQueryVariables
>;
export const EnvironmentConnectivityDocument = new TypedDocumentString(`
    query EnvironmentConnectivity($environmentId: String!) {
  environmentConnectivity(environmentId: $environmentId) {
    ok
    checkedAt
    checks {
      role
      roleArn
      ok
      message
    }
  }
}
    `) as unknown as TypedDocumentString<
	EnvironmentConnectivityQuery,
	EnvironmentConnectivityQueryVariables
>;
export const GetEnvironmentSetupTemplateDocument = new TypedDocumentString(`
    query GetEnvironmentSetupTemplate($provider: EnvironmentProvider!, $input: EnvironmentSetupTemplateInput!) {
  environmentSetupTemplate(provider: $provider, input: $input) {
    fileName
    content
  }
}
    `) as unknown as TypedDocumentString<
	GetEnvironmentSetupTemplateQuery,
	GetEnvironmentSetupTemplateQueryVariables
>;
export const GetMeDocument = new TypedDocumentString(`
    query GetMe {
  me {
    id
    email
    displayName
    organizationId
    createdAt
  }
}
    `) as unknown as TypedDocumentString<GetMeQuery, GetMeQueryVariables>;
export const GetCallerIdentityDocument = new TypedDocumentString(`
    query GetCallerIdentity {
  callerIdentity {
    kind
    id
    organizationId
    workspaceId
  }
}
    `) as unknown as TypedDocumentString<
	GetCallerIdentityQuery,
	GetCallerIdentityQueryVariables
>;
export const GetUserDocument = new TypedDocumentString(`
    query GetUser($userId: String!) {
  user(userId: $userId) {
    id
    email
    displayName
    provider
    organizationId
    createdAt
    updatedAt
  }
}
    `) as unknown as TypedDocumentString<GetUserQuery, GetUserQueryVariables>;
export const GetUsersDocument = new TypedDocumentString(`
    query GetUsers($first: Int, $after: String, $last: Int, $before: String, $workspaceId: String, $order: [ArtemisUserSortInput!], $where: ArtemisUserFilterInput) {
  users(
    first: $first
    after: $after
    last: $last
    before: $before
    workspaceId: $workspaceId
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      email
      displayName
      provider
      organizationId
      createdAt
      updatedAt
      lastActivityAt
      disabledAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<GetUsersQuery, GetUsersQueryVariables>;
export const GetApprovalDocument = new TypedDocumentString(`
    query GetApproval($approvalId: String!) {
  approval(approvalId: $approvalId) {
    id
    name
    taskId
    agentId
    environmentId
    workspaceId
    requestType
    riskLevel
    status
    actionDescription
    payloadRef
    credentialRequest {
      statements {
        actions
        resources
      }
      reason
      coverage
    }
    approvalWindowExpiresAt
    resolvedBy
    resolvedAt
    notes
    createdAt
    updatedAt
  }
}
    `) as unknown as TypedDocumentString<
	GetApprovalQuery,
	GetApprovalQueryVariables
>;
export const GetApprovalsDocument = new TypedDocumentString(`
    query GetApprovals($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisApprovalSortInput!], $where: ArtemisApprovalFilterInput) {
  approvals(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      taskId
      agentId
      environmentId
      requestType
      riskLevel
      status
      actionDescription
      approvalWindowExpiresAt
      resolvedBy
      resolvedAt
      workspaceId
      notes
      createdAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetApprovalsQuery,
	GetApprovalsQueryVariables
>;
export const GetAuditLogsDocument = new TypedDocumentString(`
    query GetAuditLogs($workspaceId: String, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisAuditLogSortInput!], $where: ArtemisAuditLogFilterInput) {
  auditLogs(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      occurredAt
      action
      entityType
      entityId
      organizationId
      workspaceId
      actorUserId
      actorClientId
      actorSubject
      before
      after
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetAuditLogsQuery,
	GetAuditLogsQueryVariables
>;
export const GetWorkspaceMetricsDocument = new TypedDocumentString(`
    query GetWorkspaceMetrics($workspaceId: String!) {
  workspaceMetrics(workspaceId: $workspaceId) {
    totalTasks
    activeTasks
    completedTasks
    failedTasks
    pendingApprovals
    memberCount
    totalInputTokens
    totalOutputTokens
    totalTimeSpentSeconds
  }
}
    `) as unknown as TypedDocumentString<
	GetWorkspaceMetricsQuery,
	GetWorkspaceMetricsQueryVariables
>;
export const GetOrganizationMetricsDocument = new TypedDocumentString(`
    query GetOrganizationMetrics {
  organizationMetrics {
    userCount
    workspaceCount
    taskCount
    agentCount
    totalUsers
    totalAgents
    maxActiveUsers
    maxWorkspaces
    maxAgents
    environmentCount
    maxEnvironments
    usage {
      organizationId
      periodStart
      periodEnd
      totalObservedInputTokens
      totalObservedOutputTokens
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetOrganizationMetricsQuery,
	GetOrganizationMetricsQueryVariables
>;
export const GetWorkspaceMembersDocument = new TypedDocumentString(`
    query GetWorkspaceMembers($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisWorkspaceMemberSortInput!], $where: ArtemisWorkspaceMemberFilterInput) {
  workspaceMembers(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      workspaceId
      userId
      role
      createdAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetWorkspaceMembersQuery,
	GetWorkspaceMembersQueryVariables
>;
export const GetJobDocument = new TypedDocumentString(`
    query GetJob($jobId: String!) {
  job(jobId: $jobId) {
    id
    name
    description
    projectId
    workspaceId
    schedule
    timezone
    deploymentIds
    environmentIds
    instructions
    agentId
    status
    lastFiredAt
    nextFireAt
    createdAt
  }
}
    `) as unknown as TypedDocumentString<GetJobQuery, GetJobQueryVariables>;
export const GetJobsDocument = new TypedDocumentString(`
    query GetJobs($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisJobSortInput!], $where: ArtemisJobFilterInput) {
  jobs(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      description
      projectId
      workspaceId
      schedule
      timezone
      status
      agentId
      lastFiredAt
      nextFireAt
      createdAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<GetJobsQuery, GetJobsQueryVariables>;
export const GetJobRunDocument = new TypedDocumentString(`
    query GetJobRun($jobRunId: String!) {
  jobRun(jobRunId: $jobRunId) {
    id
    jobId
    taskId
    projectId
    triggerType
    firedAt
    completedAt
    workspaceId
    task {
      id
      status
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetJobRunQuery,
	GetJobRunQueryVariables
>;
export const GetJobRunsDocument = new TypedDocumentString(`
    query GetJobRuns($jobId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisJobRunSortInput!], $where: ArtemisJobRunFilterInput) {
  jobRuns(
    jobId: $jobId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      jobId
      taskId
      projectId
      triggerType
      firedAt
      completedAt
      workspaceId
      task {
        id
        status
      }
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetJobRunsQuery,
	GetJobRunsQueryVariables
>;
export const GetReportsDocument = new TypedDocumentString(`
    query GetReports($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisReportSortInput!], $where: ArtemisReportFilterInput) {
  reports(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      title
      type
      status
      ownerId
      createdBy
      projectIds
      workspaceId
      summary
      createdAt
      updatedAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetReportsQuery,
	GetReportsQueryVariables
>;
export const GetReportDocument = new TypedDocumentString(`
    query GetReport($reportId: String!) {
  report(reportId: $reportId) {
    id
    title
    type
    status
    ownerId
    createdBy
    body
    projectIds
    workspaceId
    summary
    createdAt
    updatedAt
    assets {
      id
      name
      description
      mediaType
      category
      size
      summary
      createdAt
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetReportQuery,
	GetReportQueryVariables
>;
export const GetTaskBlastRadiusDocument = new TypedDocumentString(`
    query GetTaskBlastRadius($input: TaskBlastRadiusInput!) {
  taskBlastRadius(input: $input) {
    band
    flags
  }
}
    `) as unknown as TypedDocumentString<
	GetTaskBlastRadiusQuery,
	GetTaskBlastRadiusQueryVariables
>;
export const GetGitProviderConnectionDocument = new TypedDocumentString(`
    query GetGitProviderConnection {
  gitProviderConnection {
    id
    provider
    installationId
    accountLogin
    accountType
    createdByUserId
  }
}
    `) as unknown as TypedDocumentString<
	GetGitProviderConnectionQuery,
	GetGitProviderConnectionQueryVariables
>;
export const GetGitProviderConnectionRepositoriesDocument =
	new TypedDocumentString(`
    query GetGitProviderConnectionRepositories($workspaceId: String!, $cursor: String) {
  gitProviderConnectionRepositories(workspaceId: $workspaceId, cursor: $cursor) {
    repositories {
      id
      name
      fullName
      private
      ownerLogin
      ownerType
    }
    nextCursor
  }
}
    `) as unknown as TypedDocumentString<
		GetGitProviderConnectionRepositoriesQuery,
		GetGitProviderConnectionRepositoriesQueryVariables
	>;
export const GetGitProviderRepositoryCommitsDocument = new TypedDocumentString(`
    query GetGitProviderRepositoryCommits($projectRepositoryId: String!, $cursor: String) {
  gitProviderRepositoryCommits(
    projectRepositoryId: $projectRepositoryId
    cursor: $cursor
  ) {
    commits {
      sha
      message
      authorName
      authoredAt
      htmlUrl
    }
    nextCursor
  }
}
    `) as unknown as TypedDocumentString<
	GetGitProviderRepositoryCommitsQuery,
	GetGitProviderRepositoryCommitsQueryVariables
>;
export const GetGitProviderRepositoryBranchesDocument =
	new TypedDocumentString(`
    query GetGitProviderRepositoryBranches($projectRepositoryId: String!, $cursor: String) {
  gitProviderRepositoryBranches(
    projectRepositoryId: $projectRepositoryId
    cursor: $cursor
  ) {
    branches {
      name
      isDefault
    }
    nextCursor
  }
}
    `) as unknown as TypedDocumentString<
		GetGitProviderRepositoryBranchesQuery,
		GetGitProviderRepositoryBranchesQueryVariables
	>;
export const GetProjectRepositoriesDocument = new TypedDocumentString(`
    query GetProjectRepositories($projectId: String!) {
  projectRepositories(projectId: $projectId) {
    id
    projectId
    gitProviderConnectionId
    provider
    repositoryFullName
    repositoryExternalId
    defaultBranch
  }
}
    `) as unknown as TypedDocumentString<
	GetProjectRepositoriesQuery,
	GetProjectRepositoriesQueryVariables
>;
export const GetProjectCatalogGraphDocument = new TypedDocumentString(`
    query GetProjectCatalogGraph($projectId: String!, $refresh: Boolean! = false) {
  projectCatalogGraph(projectId: $projectId, refresh: $refresh) {
    entities {
      ref
      kind
      namespace
      name
      title
      description
      type
      lifecycle
      owner
      tags
      projectRepositoryId
      repositoryFullName
      isExternal
    }
    relations {
      sourceRef
      targetRef
      type
    }
    repositories {
      projectRepositoryId
      repositoryFullName
      status
      path
      entityCount
      message
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetProjectCatalogGraphQuery,
	GetProjectCatalogGraphQueryVariables
>;
export const GetOrganizationAppsDocument = new TypedDocumentString(`
    query GetOrganizationApps {
  organizationApps {
    id
    kind
    status
    clientId
    clientSecretExpiresAt
    lastSeenAt
    createdAt
    workspaces {
      id
      name
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetOrganizationAppsQuery,
	GetOrganizationAppsQueryVariables
>;
export const GetEnabledAppKindsDocument = new TypedDocumentString(`
    query GetEnabledAppKinds {
  enabledAppKinds
}
    `) as unknown as TypedDocumentString<
	GetEnabledAppKindsQuery,
	GetEnabledAppKindsQueryVariables
>;
export const GetWorkspaceAppsDocument = new TypedDocumentString(`
    query GetWorkspaceApps($workspaceId: String!) {
  workspaceApps(workspaceId: $workspaceId) {
    id
    kind
    status
    workspaceId
    createdAt
  }
}
    `) as unknown as TypedDocumentString<
	GetWorkspaceAppsQuery,
	GetWorkspaceAppsQueryVariables
>;
export const GetWebhookDocument = new TypedDocumentString(`
    query GetWebhook($webhookId: String!) {
  webhook(webhookId: $webhookId) {
    id
    name
    description
    action
    status
    workspaceId
    agentId
    instructions
    projectId
    deploymentId
    secretRotatedAt
    lastInvokedAt
    createdBy
    createdAt
    updatedAt
  }
}
    `) as unknown as TypedDocumentString<
	GetWebhookQuery,
	GetWebhookQueryVariables
>;
export const GetWebhooksDocument = new TypedDocumentString(`
    query GetWebhooks($workspaceId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisWebhookSortInput!], $where: ArtemisWebhookFilterInput) {
  webhooks(
    workspaceId: $workspaceId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      name
      description
      action
      status
      workspaceId
      agentId
      projectId
      deploymentId
      lastInvokedAt
      createdAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetWebhooksQuery,
	GetWebhooksQueryVariables
>;
export const GetWebhookDeliveriesDocument = new TypedDocumentString(`
    query GetWebhookDeliveries($webhookId: String!, $first: Int, $after: String, $last: Int, $before: String, $order: [ArtemisWebhookDeliverySortInput!], $where: ArtemisWebhookDeliveryFilterInput) {
  webhookDeliveries(
    webhookId: $webhookId
    first: $first
    after: $after
    last: $last
    before: $before
    order: $order
    where: $where
  ) {
    totalCount
    nodes {
      id
      status
      projectId
      deploymentId
      taskId
      error
      payload
      sourceIp
      userAgent
      receivedAt
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetWebhookDeliveriesQuery,
	GetWebhookDeliveriesQueryVariables
>;
export const GetSignalsDocument = new TypedDocumentString(`
    query GetSignals($projectId: String!, $deploymentId: String) {
  signals(projectId: $projectId, deploymentId: $deploymentId) {
    id
    projectId
    deploymentId
    environmentId
    kind
    source
    externalId
    name
    description
    origin
    createdAt
    config {
      key
      value
    }
    thresholds {
      warning
      critical
      direction
    }
    keyMetric
  }
}
    `) as unknown as TypedDocumentString<
	GetSignalsQuery,
	GetSignalsQueryVariables
>;
export const GetSignalStatusesDocument = new TypedDocumentString(`
    query GetSignalStatuses($projectId: String!, $deploymentId: String) {
  signals(projectId: $projectId, deploymentId: $deploymentId) {
    id
    status {
      state
      rawState
      reason
      url
      error
      observedAt
      value
      unit
      previousValue
      previousLabel
      series
      asOf
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetSignalStatusesQuery,
	GetSignalStatusesQueryVariables
>;
export const GetSignalDocument = new TypedDocumentString(`
    query GetSignal($signalId: String!) {
  signal(signalId: $signalId) {
    id
    projectId
    deploymentId
    environmentId
    kind
    source
    externalId
    name
    description
    origin
    createdAt
    config {
      key
      value
    }
    thresholds {
      warning
      critical
      direction
    }
    keyMetric
    status {
      state
      rawState
      reason
      url
      error
      observedAt
      value
      unit
      previousValue
      previousLabel
      series
      asOf
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetSignalQuery,
	GetSignalQueryVariables
>;
export const GetSignalSourcesDocument = new TypedDocumentString(`
    query GetSignalSources($workspaceId: String!) {
  signalSources(workspaceId: $workspaceId) {
    source
    kind
    displayName
    environmentProvider
    externalIdLabel
    configFields {
      key
      label
      required
      placeholder
      options
      defaultValue
    }
    available
    unavailableReason
  }
}
    `) as unknown as TypedDocumentString<
	GetSignalSourcesQuery,
	GetSignalSourcesQueryVariables
>;
export const GetSignalCandidatesDocument = new TypedDocumentString(`
    query GetSignalCandidates($input: SignalCandidatesInput!) {
  signalCandidates(input: $input) {
    externalId
    name
    description
    status {
      state
      rawState
    }
    config {
      key
      value
    }
  }
}
    `) as unknown as TypedDocumentString<
	GetSignalCandidatesQuery,
	GetSignalCandidatesQueryVariables
>;
export const OnChangeDocument = new TypedDocumentString(`
    subscription OnChange {
  onChange {
    entityType
    action
    entityId
    organizationId
    workspaceId
    status
    statusChanged
    actorUserId
  }
}
    `) as unknown as TypedDocumentString<
	OnChangeSubscription,
	OnChangeSubscriptionVariables
>;
